import React, { useEffect, useState, useRef, Component } from 'react';
import { CircleIcon, MinusIcon, DotIcon, HexagonIcon, XIcon, PlusIcon, SettingsIcon, MapIcon, GlobeIcon, RulerIcon, ChevronUpIcon, TypeIcon, TextCursorInputIcon, TrashIcon, MoveIcon, GaugeIcon, DropletIcon, LayersIcon, EyeIcon, EyeOffIcon, ChevronRightIcon, ChevronDownIcon, CrosshairIcon, TagIcon, ZapIcon, PaletteIcon, MountainIcon, WrenchIcon, TrendingUpIcon, CompassIcon, RefreshCwIcon, BookOpenIcon, ArrowUpIcon, ArrowDownIcon, ActivityIcon, InfoIcon, LockIcon, LineChartIcon, AlertTriangleIcon, ShieldCheckIcon, Maximize2Icon, ArrowLeftIcon, ScalingIcon, MoveHorizontalIcon, RotateCcwIcon, WavesIcon, LayoutGridIcon } from 'lucide-react';
type PivotSubLayerKey = 'outline' | 'center' | 'arm' | 'label' | 'towers' | 'contours';
type PivotLayers = Record<PivotSubLayerKey, boolean>;
type PivotDesign = {
  fillColor: string;
  fillOpacity: number; // 0-100
  strokeColor: string;
  strokeWidth: number; // 1-4
  centerColor: string;
  armColor: string;
  labelBg: string;
  labelText: string;
  showLabelBg: boolean;
  spanDefaults: SpanDefaults;
};
type ContourPrecision = '30m' | '10m' | '5m' | '1m';
type TopographySource = 'none' | 'free-30m' | 'free-10m' | 'imported' | 'existing' | 'premium-5m' | 'premium-1m';
type PivotTopography = {
  showSlope: boolean;
  showElevation: boolean;
  showFlowDirection: boolean;
  slopeColorRamp: 'green-red' | 'blue-yellow' | 'mono';
  minElevation: number;
  maxElevation: number;
  avgSlope: number;
  // Topography source & visibility
  source: TopographySource;
  topographyVisible: boolean; // master toggle: show any topography over the pivot
  // Contour-line specific fields
  contoursEnabled: boolean;
  contourPrecision: ContourPrecision; // mirrors source resolution for rendering
  contourEquidistance: number; // meters between contour lines
};
type PivotPanelMode = 'technical' | 'design' | 'topography' | 'contours' | 'profiles' | 'slope-analysis';
type ProfileType = 'radial' | 'tower-path';
type SlopeAnalysisType = 'crop-clearance' | 'transverse' | 'torsional' | 'longitudinal' | 'structural-absorption';
type SpanKind = 'elbow' | 'span' | 'overhang';
type SpanLineStyle = 'solid' | 'dashed' | 'dotted';
type SpanStyle = {
  color?: string;
  lineStyle?: SpanLineStyle;
  fillColor?: string | null; // null = explicitly no fill; undefined = inherit
};
type SpanDefaults = {
  color: string;
  lineStyle: SpanLineStyle;
  fillColor: string | null; // null = no fill
};
type Span = {
  id: number;
  kind: SpanKind;
  diameter: string; // e.g. '8"5/8', '6"5/8'
  length: number; // meters
  style?: SpanStyle;
};
type Pivot = {
  id: number;
  name: string;
  type: 'full-circle' | 'part-circle';
  cxPct: number; // 0-100, percent of canvas width
  cyPct: number; // 0-100, percent of canvas height
  radiusPx: number;
  towers: number;
  flowRate: number; // GPM
  pressure: number; // PSI
  active: boolean;
  visible: boolean;
  layers: PivotLayers;
  design: PivotDesign;
  topography: PivotTopography;
  spans: Span[];
};
type GlobalLayers = {
  crosshair: boolean;
  baseMap: boolean;
  hydraulicNetwork: boolean;
};
// Hydraulic Network — Phase 1
type HydraulicKind = 'water-source' | 'pipe' | 'node' | 'valve' | 'pivot-connection';
type HydraulicWaterSource = {
  id: string;
  kind: 'water-source';
  name: string;
  sourceType: 'well' | 'reservoir' | 'canal' | 'river' | 'existing-pipeline';
  xPct: number;
  yPct: number;
  elevation: number;
  availableFlow: number; // GPM
  availablePressure: number; // PSI
  notes: string;
  visible: boolean;
};
type HydraulicNode = {
  id: string;
  kind: 'node';
  name: string;
  nodeType: 'junction' | 'tee' | 'elbow' | 'reducer' | 'end-node';
  xPct: number;
  yPct: number;
  elevation: number;
  notes: string;
  visible: boolean;
};
type HydraulicValve = {
  id: string;
  kind: 'valve';
  name: string;
  valveType: 'manual' | 'electric' | 'check' | 'air' | 'pressure-reducing';
  diameter: number; // mm
  xPct: number;
  yPct: number;
  status: 'open' | 'closed' | 'unknown';
  notes: string;
  visible: boolean;
};
type HydraulicPivotConnection = {
  id: string;
  kind: 'pivot-connection';
  name: string;
  pivotId: number | null;
  xPct: number;
  yPct: number;
  elevation: number;
  requiredFlow: number; // GPM
  requiredPressure: number; // PSI
  notes: string;
  visible: boolean;
};
type HydraulicPipe = {
  id: string;
  kind: 'pipe';
  name: string;
  pipeType: 'main' | 'secondary' | 'pivot-connection';
  material: string;
  diameter: number; // mm
  pressureRating: number; // bar
  // startObjectId / endObjectId may be:
  //   - null              → free endpoint, position is taken from startPos/endPos
  //   - "hyd-..."         → linked to a HydraulicObject (non-pipe) by id
  //   - "pivot:<number>"  → linked to a Pivot center by its numeric id
  startObjectId: string | null;
  endObjectId: string | null;
  // Free-floating endpoint positions (used when the corresponding objectId is null).
  // Stored in canvas percent coords so endpoints remain visible & draggable even when unlinked.
  startPos?: {
    x: number;
    y: number;
  };
  endPos?: {
    x: number;
    y: number;
  };
  notes: string;
  visible: boolean;
};
type HydraulicObject = HydraulicWaterSource | HydraulicNode | HydraulicValve | HydraulicPivotConnection | HydraulicPipe;
type HydraulicSelection = {
  kind: HydraulicKind;
  id: string;
} | null;
// A potential snap target while dragging a pipe endpoint.
// - 'hydraulic': snap onto a non-pipe hydraulic object (by id)
// - 'pivot'   : snap onto a pivot center
// - 'pipe'    : snap onto another pipe (an inline junction node will be auto-created on drop)
type HydraulicSnapTarget = {
  kind: 'hydraulic';
  objectId: string;
  x: number;
  y: number;
} | {
  kind: 'pivot';
  pivotId: number;
  x: number;
  y: number;
} | {
  kind: 'pipe';
  pipeId: string;
  x: number;
  y: number;
};
type TabType = 'insert' | 'draw' | 'topography' | 'analyse' | 'view' | 'tools';
type BaseMapSource = 'none' | 'google-satellite' | 'bing-maps';
type UnitSystem = 'metric' | 'us-standard';
const BASE_MAP_OPTIONS: {
  id: BaseMapSource;
  label: string;
}[] = [{
  id: 'none',
  label: 'None'
}, {
  id: 'google-satellite',
  label: 'Google Satellite'
}, {
  id: 'bing-maps',
  label: 'Bing Maps'
}];
const TABS: {
  id: TabType;
  label: string;
}[] = [{
  id: 'insert',
  label: 'Insert'
}, {
  id: 'draw',
  label: 'Draw'
}, {
  id: 'topography',
  label: 'Topography'
}, {
  id: 'analyse',
  label: 'Analyse'
}, {
  id: 'view',
  label: 'View'
}, {
  id: 'tools',
  label: 'Tools'
}];
export function ICADPage({
  onNavigate
}: {
  onNavigate?: (page: string) => void;
}) {
  const [activeTab, setActiveTab] = useState<TabType>('insert');
  const [baseMap, setBaseMap] = useState<BaseMapSource>('none');
  const [baseMapMenuOpen, setBaseMapMenuOpen] = useState(false);
  const [unitSystem, setUnitSystem] = useState<UnitSystem>('metric');
  const baseMapRef = useRef<HTMLDivElement>(null);
  const [versions, setVersions] = useState([{
    id: 1,
    name: 'Version 1'
  }, {
    id: 2,
    name: 'Version 2'
  }]);
  const [activeVersion, setActiveVersion] = useState(1);
  const [nextVersionId, setNextVersionId] = useState(3);
  const [pivots, setPivots] = useState<Pivot[]>([]);
  const [selectedPivotId, setSelectedPivotId] = useState<number | null>(null);
  const [nextPivotId, setNextPivotId] = useState(1);
  const [selectedSpanId, setSelectedSpanId] = useState<number | null>(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [spanStyleEditorOpen, setSpanStyleEditorOpen] = useState(false);
  const [panelMode, setPanelMode] = useState<PivotPanelMode>('technical');
  const [radialProfileConfig, setRadialProfileConfig] = useState<{
    angleStep: number;
    selectedIndex: number;
  }>({
    angleStep: 15,
    selectedIndex: 0
  });
  // Profile panel: stays open until user explicitly closes it
  const [profilePanelOpen, setProfilePanelOpen] = useState(false);
  // Cursor position along the selected radial (0..1), driven by chart drag
  const [profileCursorT, setProfileCursorT] = useState<number | null>(null);
  // Which profile mode is active (lifted from the panel so the canvas + horizontal
  // panel can react to it).
  const [profileType, setProfileType] = useState<ProfileType>('radial');
  // Which tower index (1-based) is being analyzed in Tower Path Profile mode.
  const [towerPathTowerIndex, setTowerPathTowerIndex] = useState<number>(1);
  // Sampling distance (in meters) used by BOTH profile types — controls how often
  // elevation is sampled along the profile path. Smaller = more detail; larger = smoother.
  const [profileSamplingDistance, setProfileSamplingDistance] = useState<number>(5);
  // Crop Clearance Analysis configuration — lifted so the chart overlay can read it
  const [cropClearanceConfig, setCropClearanceConfig] = useState<{
    structHeightM: number;
    warnAtM: number;
    cropHeightM: number;
    cropName: string;
  }>({
    structHeightM: 3.6,
    warnAtM: 0.6,
    cropHeightM: 2.4,
    cropName: 'Corn (mature)'
  });
  // Slope Analysis — lifted so the horizontal chart panel can render below the canvas.
  const [activeSlopeAnalysis, setActiveSlopeAnalysis] = useState<SlopeAnalysisType | null>(null);
  const [slopeTowerIndex, setSlopeTowerIndex] = useState<number>(1);
  const [slopeChartPanelOpen, setSlopeChartPanelOpen] = useState(false);
  const [slopeCursorT, setSlopeCursorT] = useState<number | null>(null);
  const [globalLayers, setGlobalLayers] = useState<GlobalLayers>({
    crosshair: true,
    baseMap: true,
    hydraulicNetwork: true
  });
  // Hydraulic Network state — Phase 1
  const [hydraulicObjects, setHydraulicObjects] = useState<HydraulicObject[]>([]);
  const [selectedHydraulic, setSelectedHydraulic] = useState<HydraulicSelection>(null);
  const [nextHydraulicSeq, setNextHydraulicSeq] = useState<number>(1);
  // Canvas ref + drag state for hydraulic interactions
  const canvasRef = useRef<HTMLDivElement>(null);
  // Drag handle for hydraulic point-objects (water-source, node, valve, pivot-connection)
  const [draggingObject, setDraggingObject] = useState<{
    id: string;
    grabDx: number;
    grabDy: number;
  } | null>(null);
  // Drag handle for pipe endpoints (start/end)
  const [draggingPipeEndpoint, setDraggingPipeEndpoint] = useState<{
    pipeId: string;
    endpoint: 'start' | 'end';
    // live transient position while dragging (not yet committed)
    x: number;
    y: number;
    // potential snap target under the cursor
    snap: HydraulicSnapTarget | null;
  } | null>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (baseMapRef.current && !baseMapRef.current.contains(event.target as Node)) {
        setBaseMapMenuOpen(false);
      }
    }
    if (baseMapMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [baseMapMenuOpen]);
  const currentBaseMapLabel = BASE_MAP_OPTIONS.find(o => o.id === baseMap)?.label || 'None';
  const handleToolClick = (toolName: string) => {
    if (toolName === 'Pivot Full Circle') {
      handleAddFullCirclePivot();
      return;
    }
    console.log(`Tool clicked: ${toolName}`);
  };
  const handleAddFullCirclePivot = () => {
    const id = nextPivotId;
    const newPivot: Pivot = {
      id,
      name: `Pivot ${id}`,
      type: 'full-circle',
      cxPct: 50,
      cyPct: 50,
      radiusPx: 140,
      towers: 7,
      flowRate: 850,
      pressure: 35,
      active: true,
      visible: true,
      layers: {
        outline: true,
        center: true,
        arm: true,
        label: true,
        towers: false,
        contours: true
      },
      design: {
        fillColor: '#00D4FF',
        fillOpacity: 8,
        strokeColor: '#1E88E5',
        strokeWidth: 2,
        centerColor: '#1E88E5',
        armColor: '#1E88E5',
        labelBg: '#0b1628',
        labelText: '#FFFFFF',
        showLabelBg: true,
        spanDefaults: {
          color: '#1E88E5',
          lineStyle: 'solid',
          fillColor: null
        }
      },
      topography: {
        showSlope: false,
        showElevation: false,
        showFlowDirection: false,
        slopeColorRamp: 'green-red',
        minElevation: 412,
        maxElevation: 437,
        avgSlope: 2.4,
        source: 'none',
        topographyVisible: true,
        contoursEnabled: true,
        contourPrecision: '30m',
        contourEquidistance: 5
      },
      spans: createDefaultSpans()
    };
    setPivots(prev => [...prev, newPivot]);
    setSelectedPivotId(id);
    setNextPivotId(id + 1);
  };
  // Hydraulic Network — Phase 1 add handler
  const handleAddHydraulicObject = (kind: HydraulicKind) => {
    const seq = nextHydraulicSeq;
    const id = `hyd-${kind}-${seq}-${Date.now()}`;
    // Spawn at canvas center with small jitter so multiple objects don't stack
    const jx = (Math.random() - 0.5) * 6;
    const jy = (Math.random() - 0.5) * 6;
    let obj: HydraulicObject;
    if (kind === 'water-source') {
      obj = {
        id,
        kind: 'water-source',
        name: `Water Source ${seq}`,
        sourceType: 'well',
        xPct: 50 + jx,
        yPct: 50 + jy,
        elevation: 400,
        availableFlow: 1000,
        availablePressure: 60,
        notes: '',
        visible: true
      };
    } else if (kind === 'node') {
      obj = {
        id,
        kind: 'node',
        name: `Node ${seq}`,
        nodeType: 'junction',
        xPct: 50 + jx,
        yPct: 50 + jy,
        elevation: 400,
        notes: '',
        visible: true
      };
    } else if (kind === 'valve') {
      obj = {
        id,
        kind: 'valve',
        name: `Valve ${seq}`,
        valveType: 'manual',
        diameter: 200,
        xPct: 50 + jx,
        yPct: 50 + jy,
        status: 'open',
        notes: '',
        visible: true
      };
    } else if (kind === 'pivot-connection') {
      obj = {
        id,
        kind: 'pivot-connection',
        name: `Pivot Conn ${seq}`,
        pivotId: null,
        xPct: 50 + jx,
        yPct: 50 + jy,
        elevation: 400,
        requiredFlow: 850,
        requiredPressure: 35,
        notes: '',
        visible: true
      };
    } else {
      // pipe — spawn with visible free endpoints near center so the user can immediately drag them
      const cx = 50 + jx;
      const cy = 50 + jy;
      obj = {
        id,
        kind: 'pipe',
        name: `Pipe ${seq}`,
        pipeType: 'main',
        material: 'PVC',
        diameter: 200,
        pressureRating: 10,
        startObjectId: null,
        endObjectId: null,
        startPos: {
          x: cx - 6,
          y: cy
        },
        endPos: {
          x: cx + 6,
          y: cy
        },
        notes: '',
        visible: true
      };
    }
    setHydraulicObjects(prev => [...prev, obj]);
    setNextHydraulicSeq(seq + 1);
    setSelectedHydraulic({
      kind,
      id
    });
    setSelectedPivotId(null);
  };
  const handleUpdateHydraulicObject = (id: string, updates: Partial<HydraulicObject>) => {
    setHydraulicObjects(prev => prev.map(o => o.id === id ? {
      ...o,
      ...updates
    } as HydraulicObject : o));
  };
  const handleDeleteHydraulicObject = (id: string) => {
    setHydraulicObjects(prev => {
      // Also detach any pipe endpoints pointing at this object
      return prev.filter(o => o.id !== id).map(o => {
        if (o.kind === 'pipe') {
          const startObjectId = o.startObjectId === id ? null : o.startObjectId;
          const endObjectId = o.endObjectId === id ? null : o.endObjectId;
          return {
            ...o,
            startObjectId,
            endObjectId
          };
        }
        return o;
      });
    });
    setSelectedHydraulic(null);
  };
  // ─── Hydraulic drag-and-snap helpers ────────────────────────────────────
  // Convert a pointer event into canvas-percent coordinates (0..100), clamped.
  const eventToCanvasPct = (e: {
    clientX: number;
    clientY: number;
  }) => {
    const el = canvasRef.current;
    if (!el) return {
      x: 50,
      y: 50
    };
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width * 100;
    const y = (e.clientY - r.top) / r.height * 100;
    return {
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y))
    };
  };
  // Resolve where a pipe endpoint should render, given its link state.
  // Endpoint IDs may be:
  //   - null               → use pipe.startPos / pipe.endPos
  //   - "pivot:<id>"       → look up pivot center
  //   - "<hydraulic-id>"   → look up the hydraulic object's xPct/yPct
  const resolvePipeEndpoint = (pipe: HydraulicPipe, which: 'start' | 'end'): {
    x: number;
    y: number;
  } => {
    const linkId = which === 'start' ? pipe.startObjectId : pipe.endObjectId;
    const fallback = which === 'start' ? pipe.startPos ?? {
      x: 45,
      y: 50
    } : pipe.endPos ?? {
      x: 55,
      y: 50
    };
    if (!linkId) return fallback;
    if (linkId.startsWith('pivot:')) {
      const pid = Number(linkId.slice('pivot:'.length));
      const p = pivots.find(pp => pp.id === pid);
      if (p) return {
        x: p.cxPct,
        y: p.cyPct
      };
      return fallback;
    }
    const target = hydraulicObjects.find(o => o.id === linkId);
    if (target && 'xPct' in target) return {
      x: target.xPct,
      y: target.yPct
    };
    return fallback;
  };
  // Find the best snap target for a pipe-endpoint drop at canvas coords (x, y).
  // SNAP_RADIUS is expressed in percent units (~2.5% of canvas dimension).
  const SNAP_RADIUS = 2.5;
  const findSnapTarget = (x: number, y: number, excludePipeId: string): HydraulicSnapTarget | null => {
    let best: {
      dist: number;
      target: HydraulicSnapTarget;
    } | null = null;
    // 1) Hydraulic non-pipe objects
    for (const o of hydraulicObjects) {
      if (o.kind === 'pipe') continue;
      if (!('xPct' in o)) continue;
      const dx = o.xPct - x;
      const dy = o.yPct - y;
      const d = Math.hypot(dx, dy);
      if (d <= SNAP_RADIUS && (!best || d < best.dist)) {
        best = {
          dist: d,
          target: {
            kind: 'hydraulic',
            objectId: o.id,
            x: o.xPct,
            y: o.yPct
          }
        };
      }
    }
    // 2) Pivots
    for (const p of pivots) {
      if (!p.visible) continue;
      const dx = p.cxPct - x;
      const dy = p.cyPct - y;
      const d = Math.hypot(dx, dy);
      if (d <= SNAP_RADIUS && (!best || d < best.dist)) {
        best = {
          dist: d,
          target: {
            kind: 'pivot',
            pivotId: p.id,
            x: p.cxPct,
            y: p.cyPct
          }
        };
      }
    }
    // 3) Other pipes — snap to nearest point on the segment
    for (const o of hydraulicObjects) {
      if (o.kind !== 'pipe' || o.id === excludePipeId) continue;
      const a = resolvePipeEndpoint(o, 'start');
      const b = resolvePipeEndpoint(o, 'end');
      const vx = b.x - a.x;
      const vy = b.y - a.y;
      const len2 = vx * vx + vy * vy;
      if (len2 < 1e-6) continue;
      let t = ((x - a.x) * vx + (y - a.y) * vy) / len2;
      t = Math.max(0, Math.min(1, t));
      const px = a.x + t * vx;
      const py = a.y + t * vy;
      const d = Math.hypot(px - x, py - y);
      if (d <= SNAP_RADIUS && (!best || d < best.dist)) {
        best = {
          dist: d,
          target: {
            kind: 'pipe',
            pipeId: o.id,
            x: px,
            y: py
          }
        };
      }
    }
    return best ? best.target : null;
  };
  // Begin dragging a hydraulic point-object (water-source, node, valve, pivot-connection)
  const startObjectDrag = (e: React.PointerEvent, obj: HydraulicObject & {
    xPct: number;
    yPct: number;
  }) => {
    e.preventDefault();
    e.stopPropagation();
    const p = eventToCanvasPct(e);
    setDraggingObject({
      id: obj.id,
      grabDx: p.x - obj.xPct,
      grabDy: p.y - obj.yPct
    });
    setSelectedHydraulic({
      kind: obj.kind,
      id: obj.id
    });
    setSelectedPivotId(null);
  };
  // Begin dragging a pipe endpoint handle
  const startPipeEndpointDrag = (e: React.PointerEvent, pipe: HydraulicPipe, which: 'start' | 'end') => {
    e.preventDefault();
    e.stopPropagation();
    const pos = resolvePipeEndpoint(pipe, which);
    setDraggingPipeEndpoint({
      pipeId: pipe.id,
      endpoint: which,
      x: pos.x,
      y: pos.y,
      snap: null
    });
    setSelectedHydraulic({
      kind: 'pipe',
      id: pipe.id
    });
    setSelectedPivotId(null);
  };
  // Active drag: bind window-level pointer listeners.
  useEffect(() => {
    if (!draggingObject && !draggingPipeEndpoint) return;
    const onMove = (ev: PointerEvent) => {
      const p = eventToCanvasPct(ev);
      if (draggingObject) {
        const nx = Math.max(0, Math.min(100, p.x - draggingObject.grabDx));
        const ny = Math.max(0, Math.min(100, p.y - draggingObject.grabDy));
        setHydraulicObjects(prev => prev.map(o => o.id === draggingObject.id && 'xPct' in o ? {
          ...o,
          xPct: nx,
          yPct: ny
        } as HydraulicObject : o));
      } else if (draggingPipeEndpoint) {
        const snap = findSnapTarget(p.x, p.y, draggingPipeEndpoint.pipeId);
        setDraggingPipeEndpoint({
          ...draggingPipeEndpoint,
          x: snap ? snap.x : p.x,
          y: snap ? snap.y : p.y,
          snap
        });
      }
    };
    const onUp = () => {
      if (draggingPipeEndpoint) {
        const d = draggingPipeEndpoint;
        setHydraulicObjects(prev => {
          // If snapping onto another pipe, materialize a junction node at the snap point first.
          let next = prev;
          let nodeIdForPipeSnap: string | null = null;
          if (d.snap && d.snap.kind === 'pipe') {
            const newNodeId = `hyd-node-auto-${Date.now()}`;
            const newNode: HydraulicNode = {
              id: newNodeId,
              kind: 'node',
              name: `Junction ${nextHydraulicSeq}`,
              nodeType: 'junction',
              xPct: d.snap.x,
              yPct: d.snap.y,
              elevation: 0,
              notes: 'Auto-created from pipe-to-pipe connection',
              visible: true
            };
            next = [...next, newNode];
            nodeIdForPipeSnap = newNodeId;
          }
          return next.map(o => {
            if (o.kind !== 'pipe' || o.id !== d.pipeId) return o;
            const linkValue: string | null = (() => {
              if (!d.snap) return null;
              if (d.snap.kind === 'hydraulic') return d.snap.objectId;
              if (d.snap.kind === 'pivot') return `pivot:${d.snap.pivotId}`;
              return nodeIdForPipeSnap;
            })();
            const freePos = {
              x: d.x,
              y: d.y
            };
            if (d.endpoint === 'start') return {
              ...o,
              startObjectId: linkValue,
              startPos: linkValue ? o.startPos : freePos
            };
            return {
              ...o,
              endObjectId: linkValue,
              endPos: linkValue ? o.endPos : freePos
            };
          });
        });
        if (draggingPipeEndpoint.snap?.kind === 'pipe') {
          setNextHydraulicSeq(s => s + 1);
        }
      }
      setDraggingObject(null);
      setDraggingPipeEndpoint(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggingObject, draggingPipeEndpoint, pivots, hydraulicObjects]);
  // Span helpers
  const handleAddSpan = (pivotId: number, span: Omit<Span, 'id'>) => {
    setPivots(prev => prev.map(p => p.id === pivotId ? {
      ...p,
      spans: [...p.spans, {
        ...span,
        id: Date.now() + Math.random()
      }]
    } : p));
  };
  const handleDeleteSpan = (pivotId: number, spanId: number) => {
    setPivots(prev => prev.map(p => p.id === pivotId ? {
      ...p,
      spans: p.spans.filter(s => s.id !== spanId)
    } : p));
    if (selectedSpanId === spanId) setSelectedSpanId(null);
  };
  const handleReorderSpan = (pivotId: number, spanId: number, direction: 'up' | 'down') => {
    setPivots(prev => prev.map(p => {
      if (p.id !== pivotId) return p;
      const idx = p.spans.findIndex(s => s.id === spanId);
      if (idx === -1) return p;
      const target = direction === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= p.spans.length) return p;
      const next = [...p.spans];
      [next[idx], next[target]] = [next[target], next[idx]];
      return {
        ...p,
        spans: next
      };
    }));
  };
  const handleRecalculateSpans = (pivotId: number) => {
    // Mock: regenerate default spans
    setPivots(prev => prev.map(p => p.id === pivotId ? {
      ...p,
      spans: createDefaultSpans()
    } : p));
    setSelectedSpanId(null);
  };
  const handleUpdateSpansStyle = (pivotId: number, spanIds: number[], style: SpanStyle | null) => {
    const targetIds = new Set(spanIds);
    setPivots(prev => prev.map(p => p.id === pivotId ? {
      ...p,
      spans: p.spans.map(s => targetIds.has(s.id) ? {
        ...s,
        style: style === null ? undefined : {
          ...(s.style || {}),
          ...style
        }
      } : s)
    } : p));
  };
  const handleTogglePivotVisibility = (id: number) => {
    setPivots(prev => prev.map(p => p.id === id ? {
      ...p,
      visible: !p.visible
    } : p));
  };
  const handleTogglePivotLayer = (id: number, key: PivotSubLayerKey) => {
    setPivots(prev => prev.map(p => p.id === id ? {
      ...p,
      layers: {
        ...p.layers,
        [key]: !p.layers[key]
      }
    } : p));
  };
  const handleToggleGlobalLayer = (key: keyof GlobalLayers) => {
    setGlobalLayers(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  const handleUpdatePivot = (id: number, updates: Partial<Pivot>) => {
    setPivots(prev => prev.map(p => p.id === id ? {
      ...p,
      ...updates
    } : p));
  };
  const handleDeletePivot = (id: number) => {
    setPivots(prev => prev.filter(p => p.id !== id));
    if (selectedPivotId === id) setSelectedPivotId(null);
  };
  const selectedPivot = pivots.find(p => p.id === selectedPivotId) || null;
  const handleClose = () => {
    if (onNavigate) onNavigate('home');
  };
  const handleAddVersion = () => {
    const newVersion = {
      id: nextVersionId,
      name: `Version ${nextVersionId}`
    };
    setVersions([...versions, newVersion]);
    setActiveVersion(nextVersionId);
    setNextVersionId(nextVersionId + 1);
  };
  const handleRemoveVersion = (id: number) => {
    if (versions.length <= 1) return;
    const filtered = versions.filter(v => v.id !== id);
    setVersions(filtered);
    if (activeVersion === id) {
      setActiveVersion(filtered[filtered.length - 1].id);
    }
  };
  return <div className="flex flex-col h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 overflow-hidden" data-id="element-9639">
      {/* Project Header + Tab Bar */}
      <div className="bg-[#0b1628] flex-shrink-0" data-id="element-9640">
        <div className="flex items-stretch" data-id="element-9641">
          {/* Logo — spans full height */}
          <div className="flex items-center px-4" data-id="element-9642">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/cZrMyYGysL7x2nzbnxUSuo/White_Drop_for_Whatsapp.png" alt="Aisco" className="h-8 w-auto" data-id="element-9643" />
          </div>

          {/* Right side: project name + tabs stacked */}
          <div className="flex-1 flex flex-col min-w-0" data-id="element-9644">
            {/* Project name row */}
            <div className="flex items-center justify-between px-4 h-[34px]" data-id="element-9645">
              <span className="text-[#F1F5FB] text-sm font-semibold" data-id="element-9646">
                Las Turrazas II
              </span>
              <button onClick={handleClose} className="text-[#8A9BBA] hover:text-[#F87171] hover:bg-[rgba(239,68,68,0.15)] p-1 rounded transition-colors" title="Close iCAD" data-id="element-9647">
                <XIcon size={16} data-id="element-9648" />
              </button>
            </div>

            {/* Tabs row */}
            <div className="flex px-2" data-id="element-9649">
              {TABS.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-1.5 text-xs font-medium transition-colors rounded-t-md ${activeTab === tab.id ? 'text-[#F1F5FB] bg-[#132038]' : 'text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5'}`} data-id="element-9650">
                  {tab.label}
                </button>)}
            </div>
          </div>
        </div>
      </div>

      {/* Ribbon Content — lighter panel matching active tab */}
      <div className="bg-[#132038] border-b border-white/10 px-4 flex-shrink-0" data-id="element-9651">
        {activeTab === 'insert' && <div className="flex items-stretch" data-id="element-9652">
            {/* Pivots Section */}
            <RibbonSection label="Pivots" data-id="element-9653">
              <RibbonTool icon={<PivotFullCircleIcon data-id="element-9655" />} label="Full Circle" onClick={() => handleToolClick('Pivot Full Circle')} data-id="element-9654" />
              <RibbonTool icon={<PivotPartCircleIcon data-id="element-9657" />} label="Part Circle" onClick={() => handleToolClick('Pivot Part Circle')} data-id="element-9656" />
            </RibbonSection>

            {/* Lineals Section */}
            <RibbonSection label="Lineals" data-id="element-9658">
              <RibbonTool icon={<LinealEndFeedIcon data-id="element-9660" />} label="EndFeed" onClick={() => handleToolClick('Lineal EndFeed')} data-id="element-9659" />
              <RibbonTool icon={<LinealCenterFeedIcon data-id="element-9662" />} label="Center Feed" onClick={() => handleToolClick('Lineal Center Feed')} data-id="element-9661" />
              <RibbonTool icon={<LinealSwingAroundIcon data-id="element-9664" />} label="Swing Around" onClick={() => handleToolClick('Lineal Swing Around')} data-id="element-9663" />
            </RibbonSection>

            {/* Boundaries Section */}
            <RibbonSection label="Boundaries" data-id="element-9665">
              <RibbonTool icon={<FarmBoundaryIcon data-id="element-9667" />} label="Farm Boundary" onClick={() => handleToolClick('Farm Boundary')} data-id="element-9666" />
            </RibbonSection>

            {/* Hydraulic Network Section */}
            <RibbonSection label="Hydraulic Network" data-id="element-9668">
              <RibbonTool icon={<DropletIcon size={24} data-id="element-9670" />} label="Water Source" onClick={() => handleAddHydraulicObject('water-source')} data-id="element-9669" />
              <RibbonTool icon={<MinusIcon size={24} data-id="element-9672" />} label="Pipe" onClick={() => handleAddHydraulicObject('pipe')} data-id="element-9671" />
              <RibbonTool icon={<CircleIcon size={24} data-id="element-9674" />} label="Node / Junction" onClick={() => handleAddHydraulicObject('node')} data-id="element-9673" />
              <RibbonTool icon={<HexagonIcon size={24} data-id="element-9676" />} label="Valve" onClick={() => handleAddHydraulicObject('valve')} data-id="element-9675" />
              <RibbonTool icon={<ZapIcon size={24} data-id="element-9678" />} label="Pivot Connection" onClick={() => handleAddHydraulicObject('pivot-connection')} data-id="element-9677" />
            </RibbonSection>
          </div>}

        {activeTab === 'draw' && <div className="flex items-stretch" data-id="element-9679">
            {/* Lines Section */}
            <RibbonSection label="Lines" data-id="element-9680">
              <RibbonTool icon={<LineIcon data-id="element-9682" />} label="Line" onClick={() => handleToolClick('Line')} data-id="element-9681" />
              <RibbonTool icon={<PolylineIcon data-id="element-9684" />} label="Polyline" onClick={() => handleToolClick('Polyline')} data-id="element-9683" />
              <RibbonTool icon={<DrawArcIcon data-id="element-9686" />} label="Arc" onClick={() => handleToolClick('Arc')} data-id="element-9685" />
            </RibbonSection>

            {/* Shapes Section */}
            <RibbonSection label="Shapes" data-id="element-9687">
              <RibbonTool icon={<PointIcon data-id="element-9689" />} label="Point" onClick={() => handleToolClick('Point')} data-id="element-9688" />
              <RibbonTool icon={<DrawCircleIcon data-id="element-9691" />} label="Circle" onClick={() => handleToolClick('Circle')} data-id="element-9690" />
              <RibbonTool icon={<PolygonIcon data-id="element-9693" />} label="Polygon" onClick={() => handleToolClick('Polygon')} data-id="element-9692" />
            </RibbonSection>

            {/* Text Section */}
            <RibbonSection label="Text" data-id="element-9694">
              <RibbonTool icon={<TypeIcon size={24} data-id="element-9696" />} label="Text" onClick={() => handleToolClick('Text')} data-id="element-9695" />
              <RibbonTool icon={<TextCursorInputIcon size={24} data-id="element-9698" />} label="Text Box" onClick={() => handleToolClick('Text Box')} data-id="element-9697" />
            </RibbonSection>
          </div>}

        {activeTab === 'topography' && <div className="flex items-stretch" data-id="element-9699">
            <RibbonSection label="Topography" data-id="element-9700">
              <RibbonTool icon={<ContoursIcon data-id="element-9702" />} label="Contours" onClick={() => handleToolClick('Contours')} data-id="element-9701" />
              <RibbonTool icon={<CotaIcon data-id="element-9704" />} label="Cota" onClick={() => handleToolClick('Cota')} data-id="element-9703" />
              <RibbonTool icon={<ProfileIcon data-id="element-9706" />} label="Profile" onClick={() => handleToolClick('Profile')} data-id="element-9705" />
              <RibbonTool icon={<ElevationMapIcon data-id="element-9708" />} label="Elevation Map" onClick={() => handleToolClick('Elevation Map')} data-id="element-9707" />
            </RibbonSection>
          </div>}

        {activeTab === 'analyse' && <div className="flex items-center justify-center py-4" data-id="element-9709">
            <p className="text-[#8A9BBA] text-sm" data-id="element-9710">Analysis tools coming soon</p>
          </div>}

        {activeTab === 'view' && <div className="flex items-center justify-center py-4" data-id="element-9711">
            <p className="text-[#8A9BBA] text-sm" data-id="element-9712">View tools coming soon</p>
          </div>}

        {activeTab === 'tools' && <div className="flex items-center justify-center py-4" data-id="element-9713">
            <p className="text-[#8A9BBA] text-sm" data-id="element-9714">
              Additional tools coming soon
            </p>
          </div>}
      </div>

      {/* Canvas Area + Right Properties Panel */}
      <div className="flex-1 flex min-h-0" data-id="element-9715">
        {/* Left column: canvas + (optional) horizontal profile panel stacked vertically.
            Keeping the profile panel inside this column ensures it sits between the left edge
            and the right sidebar — it never overlaps the pivot properties panel. */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0" data-id="element-9716">
          {/* Canvas */}
          <div ref={canvasRef} className={`flex-1 relative overflow-hidden min-h-0 ${draggingObject || draggingPipeEndpoint ? 'cursor-grabbing' : 'cursor-crosshair'}`} data-id="element-9717">
            {/* Satellite base map */}
            {baseMap !== 'none' && globalLayers.baseMap && <img src="https://cdn.magicpatterns.com/uploads/bzDbHYffoVA6xsN1KLaZzL/image.png" alt="Satellite base map" className="absolute inset-0 w-full h-full object-cover" draggable={false} data-id="element-9718" />}

            {/* Blank canvas background */}
            {(baseMap === 'none' || !globalLayers.baseMap) && <div className="absolute inset-0 bg-[#e8e8e8]" data-id="element-9719" />}

            {/* Crosshair indicator */}
            {globalLayers.crosshair && <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none" data-id="element-9720">
                <div className={`w-8 h-0.5 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ${baseMap !== 'none' && globalLayers.baseMap ? 'bg-white/40' : 'bg-gray-400/30'}`} data-id="element-9721"></div>
                <div className={`h-8 w-0.5 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ${baseMap !== 'none' && globalLayers.baseMap ? 'bg-white/40' : 'bg-gray-400/30'}`} data-id="element-9722"></div>
              </div>}

            {/* Pivots overlay */}
            {pivots.map(pivot => {
            if (!pivot.visible) return null;
            const isSelected = pivot.id === selectedPivotId;
            const d = pivot.design;
            const sw = d.strokeWidth * 0.3;
            const fillRgba = hexToRgba(d.fillColor, d.fillOpacity / 100);
            return <div key={pivot.id} onClick={() => setSelectedPivotId(pivot.id)} className="absolute cursor-pointer group" style={{
              left: `calc(${pivot.cxPct}% - ${pivot.radiusPx}px)`,
              top: `calc(${pivot.cyPct}% - ${pivot.radiusPx}px)`,
              width: `${pivot.radiusPx * 2}px`,
              height: `${pivot.radiusPx * 2}px`
            }} data-id="element-9723">
                  <svg width="100%" height="100%" viewBox="0 0 100 100" className="overflow-visible" data-id="element-9724">
                    {/* Topography: slope analysis fill (mock gradient) */}
                    {pivot.topography.showSlope && <defs data-id="element-9725">
                        <radialGradient id={`slope-${pivot.id}`} cx="50%" cy="50%" r="50%" data-id="element-9726">
                          {pivot.topography.slopeColorRamp === 'green-red' && <>
                              <stop offset="0%" stopColor="#22c55e" stopOpacity="0.5" data-id="element-9727" />
                              <stop offset="60%" stopColor="#eab308" stopOpacity="0.5" data-id="element-9728" />
                              <stop offset="100%" stopColor="#ef4444" stopOpacity="0.5" data-id="element-9729" />
                            </>}
                          {pivot.topography.slopeColorRamp === 'blue-yellow' && <>
                              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.5" data-id="element-9730" />
                              <stop offset="100%" stopColor="#facc15" stopOpacity="0.5" data-id="element-9731" />
                            </>}
                          {pivot.topography.slopeColorRamp === 'mono' && <>
                              <stop offset="0%" stopColor="#94a3b8" stopOpacity="0.6" data-id="element-9732" />
                              <stop offset="100%" stopColor="#1e293b" stopOpacity="0.6" data-id="element-9733" />
                            </>}
                        </radialGradient>
                      </defs>}
                    {pivot.topography.showSlope && <circle cx="50" cy="50" r="49" fill={`url(#slope-${pivot.id})`} data-id="element-9734" />}
                    {/* Outline */}
                    {pivot.layers.outline && <circle cx="50" cy="50" r="49" fill={pivot.topography.showSlope ? 'none' : fillRgba} stroke={d.strokeColor} strokeWidth={sw} className="transition-all" data-id="element-9735" />}
                    {/* Selection ring */}
                    {isSelected && <circle cx="50" cy="50" r="50.5" fill="none" stroke="#00D4FF" strokeWidth="0.6" strokeDasharray="1.5 1.2" data-id="element-9736" />}
                    {/* Selected span highlight ring */}
                    {isSelected && selectedSpanId !== null && (() => {
                  const totalLen = pivot.spans.reduce((sum, s) => sum + s.length, 0);
                  if (totalLen <= 0) return null;
                  const idx = pivot.spans.findIndex(s => s.id === selectedSpanId);
                  if (idx === -1) return null;
                  const cumLen = pivot.spans.slice(0, idx + 1).reduce((sum, s) => sum + s.length, 0);
                  const prevLen = pivot.spans.slice(0, idx).reduce((sum, s) => sum + s.length, 0);
                  const rOuter = cumLen / totalLen * 49;
                  const rInner = prevLen / totalLen * 49;
                  const rMid = (rOuter + rInner) / 2;
                  const bandWidth = Math.max(0.8, rOuter - rInner);
                  return <g data-id="element-9737">
                            <circle cx="50" cy="50" r={rMid} fill="none" stroke="#FBBF24" strokeWidth={bandWidth} opacity="0.35" data-id="element-9738" />
                            <circle cx="50" cy="50" r={rOuter} fill="none" stroke="#FBBF24" strokeWidth="0.5" strokeDasharray="1.2 0.8" data-id="element-9739" />
                          </g>;
                })()}
                    {/* Topographic contour lines (elevation curves) — marching-squares isolines on a multi-peak Gaussian heightfield */}
                    {pivot.topography.source !== 'none' && pivot.topography.topographyVisible && pivot.topography.contoursEnabled && (() => {
                  const result = buildTopoContours(pivot);
                  if (!result) return null;
                  const {
                    paths,
                    indexPaths,
                    peak,
                    valley,
                    labels
                  } = result;
                  const clipId = `pivot-clip-${pivot.id}`;
                  return <>
                            <defs data-id="element-9740">
                              <clipPath id={clipId} data-id="element-9741">
                                <circle cx="50" cy="50" r="49" data-id="element-9742" />
                              </clipPath>
                            </defs>
                            <g clipPath={`url(#${clipId})`} data-id="element-9743">
                              {/* Regular contour lines */}
                              <path d={paths} fill="none" stroke="#1a1a1a" strokeWidth="0.18" strokeLinejoin="round" strokeLinecap="round" opacity="0.75" data-id="element-9744" />
                              {/* Index contour lines (every 5th) — bolder */}
                              <path d={indexPaths} fill="none" stroke="#0a0a0a" strokeWidth="0.42" strokeLinejoin="round" strokeLinecap="round" opacity="0.95" data-id="element-9745" />
                              {/* Elevation labels on index lines */}
                              {pivot.topography.showElevation && labels.map((lbl, i) => <text key={`lbl-${i}`} x={lbl.x} y={lbl.y} fontSize="1.8" fontFamily="monospace" fill="#1a1a1a" fontWeight="600" textAnchor="middle" transform={`rotate(${lbl.angle}, ${lbl.x}, ${lbl.y})`} data-id="element-9746">
                                    <tspan dy="0.5" style={{
                          paintOrder: 'stroke',
                          stroke: '#FAF7F0',
                          strokeWidth: 0.6,
                          strokeLinejoin: 'round'
                        } as any} data-id="element-9747">
                                      {lbl.text}
                                    </tspan>
                                  </text>)}
                              {/* Highest point marker (peak) */}
                              <g data-id="element-9748">
                                <circle cx={peak.x} cy={peak.y} r="1" fill="#16A34A" stroke="#0a0a0a" strokeWidth="0.3" data-id="element-9749" />
                                <text x={peak.x} y={peak.y - 1.8} fontSize="2.4" fontFamily="monospace" fill="#0a0a0a" fontWeight="700" textAnchor="middle" style={{
                          paintOrder: 'stroke',
                          stroke: '#FAF7F0',
                          strokeWidth: 0.8,
                          strokeLinejoin: 'round'
                        } as any} data-id="element-9750">
                                  ▲ {pivot.topography.maxElevation}m
                                </text>
                              </g>
                              {/* Lowest point marker (valley) */}
                              <g data-id="element-9751">
                                <circle cx={valley.x} cy={valley.y} r="1" fill="#DC2626" stroke="#0a0a0a" strokeWidth="0.3" data-id="element-9752" />
                                <text x={valley.x} y={valley.y + 3.4} fontSize="2.4" fontFamily="monospace" fill="#0a0a0a" fontWeight="700" textAnchor="middle" style={{
                          paintOrder: 'stroke',
                          stroke: '#FAF7F0',
                          strokeWidth: 0.8,
                          strokeLinejoin: 'round'
                        } as any} data-id="element-9753">
                                  ▼ {pivot.topography.minElevation}m
                                </text>
                              </g>
                            </g>
                          </>;
                })()}
                    {/* Radial profile lines (visible while the elevation panel is open) */}
                    {selectedPivotId === pivot.id && profilePanelOpen && profileType === 'radial' && pivot.topography.source !== 'none' && (() => {
                  const count = Math.max(1, Math.floor(360 / radialProfileConfig.angleStep));
                  const sel = radialProfileConfig.selectedIndex % count;
                  const selAngleRad = sel * radialProfileConfig.angleStep * Math.PI / 180;
                  return <g data-id="element-9754">
                            {Array.from({
                      length: count
                    }).map((_, i) => {
                      const a = i * radialProfileConfig.angleStep * Math.PI / 180;
                      const x2 = 50 + Math.cos(a) * 49;
                      const y2 = 50 + Math.sin(a) * 49;
                      // Label position just outside the pivot
                      const lx = 50 + Math.cos(a) * 53;
                      const ly = 50 + Math.sin(a) * 53;
                      const isSel = i === sel;
                      const angleDeg = i * radialProfileConfig.angleStep;
                      return <g key={`rad-${i}`} style={{
                        cursor: 'pointer'
                      }} data-id="element-9755">
                                  <line x1="50" y1="50" x2={x2.toFixed(2)} y2={y2.toFixed(2)} stroke={isSel ? '#00D4FF' : '#F1F5FB'} strokeWidth={isSel ? '0.7' : '0.32'} opacity={isSel ? '1' : '0.7'} strokeDasharray={isSel ? undefined : '1.2 0.9'} data-id="element-9756" />
                                  {/* Larger invisible hit area */}
                                  <line x1="50" y1="50" x2={x2.toFixed(2)} y2={y2.toFixed(2)} stroke="transparent" strokeWidth="2" onClick={e => {
                          e.stopPropagation();
                          setRadialProfileConfig({
                            ...radialProfileConfig,
                            selectedIndex: i
                          });
                          setProfileCursorT(null);
                        }} data-id="element-9757" />
                                  {isSel && <circle cx={x2.toFixed(2)} cy={y2.toFixed(2)} r="1.4" fill="#00D4FF" stroke="#0b1628" strokeWidth="0.3" data-id="element-9758" />}
                                  {/* Angle label */}
                                  <text x={lx.toFixed(2)} y={ly.toFixed(2)} fontSize="3.4" fontFamily="monospace" fontWeight={isSel ? '800' : '700'} fill={isSel ? '#00D4FF' : '#F1F5FB'} opacity="1" textAnchor="middle" dominantBaseline="middle" style={{
                          paintOrder: 'stroke',
                          stroke: '#000000',
                          strokeWidth: 1.1,
                          strokeLinejoin: 'round'
                        } as any} data-id="element-9759">
                                    {angleDeg}°
                                  </text>
                                </g>;
                    })}
                            {/* Center marker */}
                            <circle cx="50" cy="50" r="1.2" fill="#00D4FF" stroke="#0b1628" strokeWidth="0.3" data-id="element-9760" />
                            {/* Chart-driven cursor on the selected radial */}
                            {profileCursorT !== null && (() => {
                      const cx = 50 + Math.cos(selAngleRad) * profileCursorT * 49;
                      const cy = 50 + Math.sin(selAngleRad) * profileCursorT * 49;
                      return <g data-id="element-9761">
                                    <circle cx={cx.toFixed(2)} cy={cy.toFixed(2)} r="2.4" fill="#FBBF24" stroke="#0b1628" strokeWidth="0.5" data-id="element-9762" />
                                    <circle cx={cx.toFixed(2)} cy={cy.toFixed(2)} r="1.1" fill="#0b1628" data-id="element-9763" />
                                  </g>;
                    })()}
                          </g>;
                })()}
                    {/* Tower Path overlay — full circular trajectory of the selected tower.
                     Shows every tower's path dimly, highlights the selected tower's path,
                     and renders the cursor marker (amber) at the angle picked in the chart. */}
                    {selectedPivotId === pivot.id && profilePanelOpen && profileType === 'tower-path' && pivot.topography.source !== 'none' && (() => {
                  const n = Math.max(1, pivot.towers);
                  const sel = Math.max(1, Math.min(n, towerPathTowerIndex));
                  const selRPx = sel / n * 49; // viewBox space
                  return <g data-id="element-9764">
                            {/* Faint rings for every tower (context) */}
                            {Array.from({
                      length: n
                    }).map((_, i) => {
                      const r = (i + 1) / n * 49;
                      const isSel = i + 1 === sel;
                      if (isSel) return null; // selected drawn separately on top
                      return <circle key={`tp-ctx-${i}`} cx="50" cy="50" r={r} fill="none" stroke="#F1F5FB" strokeWidth="0.18" strokeDasharray="0.8 0.8" opacity="0.4" data-id="element-9765" />;
                    })}
                            {/* Highlighted path for the selected tower */}
                            <circle cx="50" cy="50" r={selRPx} fill="none" stroke="#00D4FF" strokeWidth="0.7" opacity="1" data-id="element-9766" />
                            {/* Cardinal angle markers around the highlighted path */}
                            {[0, 90, 180, 270].map(deg => {
                      const a = deg * Math.PI / 180;
                      const x = 50 + Math.cos(a) * selRPx;
                      const y = 50 + Math.sin(a) * selRPx;
                      const lx = 50 + Math.cos(a) * (selRPx + 4.2);
                      const ly = 50 + Math.sin(a) * (selRPx + 4.2);
                      return <g key={`tp-card-${deg}`} data-id="element-9767">
                                  <circle cx={x.toFixed(2)} cy={y.toFixed(2)} r="0.9" fill="#00D4FF" stroke="#0b1628" strokeWidth="0.25" data-id="element-9768" />
                                  <text x={lx.toFixed(2)} y={ly.toFixed(2)} fontSize="3" fontFamily="monospace" fontWeight="700" fill="#00D4FF" textAnchor="middle" dominantBaseline="middle" style={{
                          paintOrder: 'stroke',
                          stroke: '#000000',
                          strokeWidth: 1,
                          strokeLinejoin: 'round'
                        } as any} data-id="element-9769">
                                    {deg}°
                                  </text>
                                </g>;
                    })}
                            {/* Tower label (e.g. "T3") at the top of the selected path */}
                            {(() => {
                      const y = 50 - selRPx - 1.4;
                      return <text x="50" y={y.toFixed(2)} fontSize="3.4" fontFamily="monospace" fontWeight="800" fill="#00D4FF" textAnchor="middle" dominantBaseline="ideographic" style={{
                        paintOrder: 'stroke',
                        stroke: '#000000',
                        strokeWidth: 1.1,
                        strokeLinejoin: 'round'
                      } as any} data-id="element-9770">
                                  T{sel}
                                </text>;
                    })()}
                            {/* Cursor marker following the chart hover position */}
                            {profileCursorT !== null && (() => {
                      const a = profileCursorT * Math.PI * 2;
                      const cx = 50 + Math.cos(a) * selRPx;
                      const cy = 50 + Math.sin(a) * selRPx;
                      return <g data-id="element-9771">
                                    <circle cx={cx.toFixed(2)} cy={cy.toFixed(2)} r="2.4" fill="#FBBF24" stroke="#0b1628" strokeWidth="0.5" data-id="element-9772" />
                                    <circle cx={cx.toFixed(2)} cy={cy.toFixed(2)} r="1.1" fill="#0b1628" data-id="element-9773" />
                                  </g>;
                    })()}
                            {/* Center marker */}
                            <circle cx="50" cy="50" r="1.2" fill="#00D4FF" stroke="#0b1628" strokeWidth="0.3" data-id="element-9774" />
                          </g>;
                })()}
                    {/* Slope Analysis overlay — mirrors the Profile overlays so
                     the user sees the analysis path on the drawing AND the
                     orange cursor dot scrubbing along it as they hover the
                     horizontal chart panel.
                     · crop-clearance / longitudinal / structural-absorption:
                     arm-based → draw the selected radial line + orange dot
                     along the radial (radial is fixed at 0° for the two
                     latter analyses, configurable for crop-clearance).
                     · transverse / torsional: tower-path based → draw the
                     selected tower's circular path + orange dot moving
                     around it. */}
                    {selectedPivotId === pivot.id && slopeChartPanelOpen && activeSlopeAnalysis && !profilePanelOpen && pivot.topography.source !== 'none' && (() => {
                  const isAngleBased = activeSlopeAnalysis === 'transverse' || activeSlopeAnalysis === 'torsional';
                  // ---------- ARM-BASED (radial fan + cursor) ----------
                  // Mirrors the Profile Radial overlay: every radial at
                  // angleStep increments is drawn with its degree label,
                  // the selected one is highlighted, and clicking any
                  // dim radial selects it. For crop-clearance the
                  // selection drives the analysis; longitudinal and
                  // structural-absorption pin the selection to 0°.
                  if (!isAngleBased) {
                    const fanLocked = activeSlopeAnalysis === 'longitudinal' || activeSlopeAnalysis === 'structural-absorption';
                    const count = Math.max(1, Math.floor(360 / radialProfileConfig.angleStep));
                    const sel = fanLocked ? 0 : radialProfileConfig.selectedIndex % count;
                    const selAngleDeg = sel * radialProfileConfig.angleStep;
                    const selAngleRad = selAngleDeg * Math.PI / 180;
                    return <g data-id="element-9775">
                              {Array.from({
                        length: count
                      }).map((_, i) => {
                        const a = i * radialProfileConfig.angleStep * Math.PI / 180;
                        const x2 = 50 + Math.cos(a) * 49;
                        const y2 = 50 + Math.sin(a) * 49;
                        const lx = 50 + Math.cos(a) * 53;
                        const ly = 50 + Math.sin(a) * 53;
                        const isSel = i === sel;
                        const angleDeg = i * radialProfileConfig.angleStep;
                        return <g key={`sa-rad-${i}`} style={{
                          cursor: fanLocked ? 'default' : 'pointer'
                        }} data-id="element-9776">
                                    <line x1="50" y1="50" x2={x2.toFixed(2)} y2={y2.toFixed(2)} stroke={isSel ? '#00D4FF' : '#F1F5FB'} strokeWidth={isSel ? '0.7' : '0.32'} opacity={isSel ? '1' : '0.7'} strokeDasharray={isSel ? undefined : '1.2 0.9'} data-id="element-9777" />
                                    {!fanLocked && <line x1="50" y1="50" x2={x2.toFixed(2)} y2={y2.toFixed(2)} stroke="transparent" strokeWidth="2" onClick={e => {
                            e.stopPropagation();
                            setRadialProfileConfig({
                              ...radialProfileConfig,
                              selectedIndex: i
                            });
                            setSlopeCursorT(null);
                          }} data-id="element-9778" />}
                                    {isSel && <circle cx={x2.toFixed(2)} cy={y2.toFixed(2)} r="1.4" fill="#00D4FF" stroke="#0b1628" strokeWidth="0.3" data-id="element-9779" />}
                                    <text x={lx.toFixed(2)} y={ly.toFixed(2)} fontSize="3.4" fontFamily="monospace" fontWeight={isSel ? '800' : '700'} fill={isSel ? '#00D4FF' : '#F1F5FB'} opacity="1" textAnchor="middle" dominantBaseline="middle" style={{
                            paintOrder: 'stroke',
                            stroke: '#000000',
                            strokeWidth: 1.1,
                            strokeLinejoin: 'round'
                          } as any} data-id="element-9780">
                                      {angleDeg}°
                                    </text>
                                  </g>;
                      })}
                              {/* Center marker */}
                              <circle cx="50" cy="50" r="1.2" fill="#00D4FF" stroke="#0b1628" strokeWidth="0.3" data-id="element-9781" />
                              {/* Chart-driven cursor along the selected radial */}
                              {slopeCursorT !== null && (() => {
                        const cx = 50 + Math.cos(selAngleRad) * slopeCursorT * 49;
                        const cy = 50 + Math.sin(selAngleRad) * slopeCursorT * 49;
                        return <g data-id="element-9782">
                                      <circle cx={cx.toFixed(2)} cy={cy.toFixed(2)} r="2.4" fill="#FBBF24" stroke="#0b1628" strokeWidth="0.5" data-id="element-9783" />
                                      <circle cx={cx.toFixed(2)} cy={cy.toFixed(2)} r="1.1" fill="#0b1628" data-id="element-9784" />
                                    </g>;
                      })()}
                            </g>;
                  }
                  // ---------- ANGLE-BASED (tower path + cursor) ----------
                  const n = Math.max(1, pivot.towers);
                  const sel = Math.max(1, Math.min(n, slopeTowerIndex));
                  const selRPx = sel / n * 49;
                  return <g data-id="element-9785">
                            {/* Faint rings for context */}
                            {Array.from({
                      length: n
                    }).map((_, i) => {
                      const r = (i + 1) / n * 49;
                      if (i + 1 === sel) return null;
                      return <circle key={`sa-ctx-${i}`} cx="50" cy="50" r={r} fill="none" stroke="#F1F5FB" strokeWidth="0.18" strokeDasharray="0.8 0.8" opacity="0.4" data-id="element-9786" />;
                    })}
                            {/* Highlighted path for the selected tower */}
                            <circle cx="50" cy="50" r={selRPx} fill="none" stroke="#00D4FF" strokeWidth="0.7" opacity="1" data-id="element-9787" />
                            {/* Cardinal angle markers */}
                            {[0, 90, 180, 270].map(deg => {
                      const a = deg * Math.PI / 180;
                      const x = 50 + Math.cos(a) * selRPx;
                      const y = 50 + Math.sin(a) * selRPx;
                      const lx = 50 + Math.cos(a) * (selRPx + 4.2);
                      const ly = 50 + Math.sin(a) * (selRPx + 4.2);
                      return <g key={`sa-card-${deg}`} data-id="element-9788">
                                  <circle cx={x.toFixed(2)} cy={y.toFixed(2)} r="0.9" fill="#00D4FF" stroke="#0b1628" strokeWidth="0.25" data-id="element-9789" />
                                  <text x={lx.toFixed(2)} y={ly.toFixed(2)} fontSize="3" fontFamily="monospace" fontWeight="700" fill="#00D4FF" textAnchor="middle" dominantBaseline="middle" style={{
                          paintOrder: 'stroke',
                          stroke: '#000000',
                          strokeWidth: 1,
                          strokeLinejoin: 'round'
                        } as any} data-id="element-9790">
                                    {deg}°
                                  </text>
                                </g>;
                    })}
                            {/* Tower label at the top */}
                            {(() => {
                      const y = 50 - selRPx - 1.4;
                      return <text x="50" y={y.toFixed(2)} fontSize="3.4" fontFamily="monospace" fontWeight="800" fill="#00D4FF" textAnchor="middle" dominantBaseline="ideographic" style={{
                        paintOrder: 'stroke',
                        stroke: '#000000',
                        strokeWidth: 1.1,
                        strokeLinejoin: 'round'
                      } as any} data-id="element-9791">
                                  T{sel}
                                </text>;
                    })()}
                            {/* Chart-driven cursor around the wheel path */}
                            {slopeCursorT !== null && (() => {
                      const a = slopeCursorT * Math.PI * 2;
                      const cx = 50 + Math.cos(a) * selRPx;
                      const cy = 50 + Math.sin(a) * selRPx;
                      return <g data-id="element-9792">
                                    <circle cx={cx.toFixed(2)} cy={cy.toFixed(2)} r="2.4" fill="#FBBF24" stroke="#0b1628" strokeWidth="0.5" data-id="element-9793" />
                                    <circle cx={cx.toFixed(2)} cy={cy.toFixed(2)} r="1.1" fill="#0b1628" data-id="element-9794" />
                                  </g>;
                    })()}
                            {/* Center marker */}
                            <circle cx="50" cy="50" r="1.2" fill="#00D4FF" stroke="#0b1628" strokeWidth="0.3" data-id="element-9795" />
                          </g>;
                })()}
                    {/* Contours (per-span tower travel rings) */}
                    {pivot.layers.contours && (() => {
                  const totalLen = pivot.spans.reduce((sum, s) => sum + s.length, 0);
                  // Fallback to 3 generic rings when no spans
                  if (totalLen <= 0) {
                    return <g stroke={d.strokeColor} strokeWidth="0.25" fill="none" opacity="0.45" strokeDasharray="0.8 0.8" data-id="element-9796">
                              <circle cx="50" cy="50" r="36" data-id="element-9797" />
                              <circle cx="50" cy="50" r="24" data-id="element-9798" />
                              <circle cx="50" cy="50" r="12" data-id="element-9799" />
                            </g>;
                  }
                  const defaults = d.spanDefaults;
                  const dashFor = (style: SpanLineStyle) => style === 'dashed' ? '1.2 0.8' : style === 'dotted' ? '0.3 0.6' : '0.8 0.8';
                  let acc = 0;
                  return <g fill="none" opacity="0.55" data-id="element-9800">
                            {pivot.spans.map(span => {
                      acc += span.length;
                      const r = acc / totalLen * 49;
                      const effColor = span.style?.color || defaults.color;
                      const effLine = span.style?.lineStyle || defaults.lineStyle;
                      return <circle key={`contour-${span.id}`} cx="50" cy="50" r={r} stroke={effColor} strokeWidth="0.3" strokeDasharray={dashFor(effLine)} data-id="element-9801" />;
                    })}
                          </g>;
                })()}
                    {/* Topography flow direction arrow */}
                    {pivot.topography.showFlowDirection && <g stroke="#0b1628" strokeWidth="0.5" fill="#0b1628" data-id="element-9802">
                        <line x1="50" y1="50" x2="35" y2="65" strokeWidth="1" data-id="element-9803" />
                        <polygon points="32,68 36,62 38,66" data-id="element-9804" />
                      </g>}
                    {/* Elevation labels */}
                    {pivot.topography.showElevation && <g fontSize="3.5" fontFamily="monospace" fill="#0b1628" fontWeight="600" data-id="element-9805">
                        <text x="50" y="8" textAnchor="middle" data-id="element-9806">
                          {pivot.topography.maxElevation}m
                        </text>
                        <text x="50" y="96" textAnchor="middle" data-id="element-9807">
                          {pivot.topography.minElevation}m
                        </text>
                      </g>}
                    {/* Tower marks */}
                    {pivot.layers.towers && Array.from({
                  length: pivot.towers
                }).map((_, i) => {
                  const t = (i + 1) / pivot.towers;
                  const x = 50 + 49 * t;
                  return <circle key={i} cx={x} cy="50" r="1.2" fill={d.armColor} stroke="#0b1628" strokeWidth="0.3" data-id="element-9808" />;
                })}
                    {/* Lateral arm & per-span styled rendering */}
                    {pivot.layers.arm && (() => {
                  const totalLen = pivot.spans.reduce((sum, s) => sum + s.length, 0);
                  // No spans → fall back to the simple arm line
                  if (totalLen <= 0) {
                    return <line x1="50" y1="50" x2="99" y2="50" stroke={d.armColor} strokeWidth={sw * 0.7} opacity="0.85" data-id="element-9809" />;
                  }
                  const defaults = d.spanDefaults;
                  const dashFor = (style: SpanLineStyle) => style === 'dashed' ? '1.6 0.9' : style === 'dotted' ? '0.35 0.7' : undefined;
                  // Compute cumulative radii once
                  const radii: number[] = [];
                  let acc = 0;
                  pivot.spans.forEach(s => {
                    acc += s.length;
                    radii.push(acc / totalLen * 49);
                  });
                  return <g data-id="element-9810">
                            {/* Fill bands between consecutive spans (rendered first, lowest) */}
                            {pivot.spans.map((span, i) => {
                      const effFill = span.style?.fillColor !== undefined ? span.style.fillColor : defaults.fillColor;
                      if (!effFill) return null;
                      const rOuter = radii[i];
                      const rInner = i === 0 ? 0 : radii[i - 1];
                      const rMid = (rOuter + rInner) / 2;
                      const bandWidth = Math.max(0.4, rOuter - rInner);
                      return <circle key={`fill-${span.id}`} cx="50" cy="50" r={rMid} fill="none" stroke={effFill} strokeWidth={bandWidth} opacity="0.28" data-id="element-9811" />;
                    })}
                            {/* Per-span lateral arm segments */}
                            {pivot.spans.map((span, i) => {
                      const effColor = span.style?.color || defaults.color;
                      const effLine = span.style?.lineStyle || defaults.lineStyle;
                      const rOuter = radii[i];
                      const rInner = i === 0 ? 0 : radii[i - 1];
                      const x1 = 50 + rInner;
                      const x2 = 50 + rOuter;
                      return <line key={`seg-${span.id}`} x1={x1} y1="50" x2={x2} y2="50" stroke={effColor} strokeWidth={sw * 0.8} strokeDasharray={dashFor(effLine)} strokeLinecap="butt" opacity="0.95" data-id="element-9812" />;
                    })}
                          </g>;
                })()}
                    {/* Center pivot point */}
                    {pivot.layers.center && <>
                        <circle cx="50" cy="50" r="1.5" fill="#0b1628" data-id="element-9813" />
                        <circle cx="50" cy="50" r="1" fill={d.centerColor} data-id="element-9814" />
                      </>}
                  </svg>
                  {/* Label */}
                  {pivot.layers.label && <div className="absolute top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap pointer-events-none" style={{
                backgroundColor: d.showLabelBg ? hexToRgba(d.labelBg, 0.85) : 'transparent',
                color: d.labelText,
                textShadow: !d.showLabelBg ? '0 1px 2px rgba(0,0,0,0.6)' : 'none'
              }} data-id="element-9815">
                      {pivot.name}
                    </div>}
                </div>;
          })}

            {/* Hydraulic Network Overlay — Phase 1 */}
            {globalLayers.hydraulicNetwork && <>
                {/* Pipes + endpoint handles */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-9816">
                  {hydraulicObjects.filter((o): o is HydraulicPipe => o.kind === 'pipe' && o.visible).map(pipe => {
                // Use resolved positions (supports object IDs, "pivot:<id>", and free startPos/endPos)
                const liveStartDrag = draggingPipeEndpoint && draggingPipeEndpoint.pipeId === pipe.id && draggingPipeEndpoint.endpoint === 'start';
                const liveEndDrag = draggingPipeEndpoint && draggingPipeEndpoint.pipeId === pipe.id && draggingPipeEndpoint.endpoint === 'end';
                const start = liveStartDrag ? {
                  x: draggingPipeEndpoint!.x,
                  y: draggingPipeEndpoint!.y
                } : resolvePipeEndpoint(pipe, 'start');
                const end = liveEndDrag ? {
                  x: draggingPipeEndpoint!.x,
                  y: draggingPipeEndpoint!.y
                } : resolvePipeEndpoint(pipe, 'end');
                const startConnected = pipe.startObjectId !== null;
                const endConnected = pipe.endObjectId !== null;
                const fullyConnected = startConnected && endConnected;
                const isSelected = selectedHydraulic?.kind === 'pipe' && selectedHydraulic.id === pipe.id;
                const stroke = !fullyConnected ? '#FBBF24' : isSelected ? '#00D4FF' : pipe.pipeType === 'main' ? '#22D3EE' : '#0EA5E9';
                const dash = pipe.pipeType === 'pivot-connection' ? '1.2 0.8' : undefined;
                return <g key={pipe.id} data-id="element-9817">
                          {/* Invisible hit area for easier clicking */}
                          <line x1={start.x} y1={start.y} x2={end.x} y2={end.y} stroke="transparent" strokeWidth={2.5} className="pointer-events-auto cursor-pointer" onClick={e => {
                    e.stopPropagation();
                    setSelectedHydraulic({
                      kind: 'pipe',
                      id: pipe.id
                    });
                    setSelectedPivotId(null);
                  }} data-id="element-9818" />
                          {/* Visible pipe stroke */}
                          <line x1={start.x} y1={start.y} x2={end.x} y2={end.y} stroke={stroke} strokeWidth={isSelected ? 0.8 : 0.5} strokeDasharray={dash} strokeLinecap="round" className="pointer-events-none" data-id="element-9819" />
                          {/* Endpoint handles (always visible — let users drag to connect/move) */}
                          {(['start', 'end'] as const).map(which => {
                    const pt = which === 'start' ? start : end;
                    const linked = which === 'start' ? startConnected : endConnected;
                    const beingDragged = draggingPipeEndpoint?.pipeId === pipe.id && draggingPipeEndpoint?.endpoint === which;
                    const willSnap = beingDragged && draggingPipeEndpoint?.snap !== null;
                    const handleFill = beingDragged ? willSnap ? '#22C55E' : '#F59E0B' : linked ? '#22D3EE' : '#FBBF24';
                    return <g key={which} data-id="element-9820">
                                {willSnap && <circle cx={pt.x} cy={pt.y} r={2.2} fill="none" stroke="#22C55E" strokeWidth={0.3} strokeDasharray="0.5 0.4" className="pointer-events-none" data-id="element-9821" />}
                                <circle cx={pt.x} cy={pt.y} r={beingDragged ? 1.2 : 0.95} fill={handleFill} stroke="#0b1628" strokeWidth={0.25} className="pointer-events-auto cursor-grab active:cursor-grabbing" onPointerDown={e => startPipeEndpointDrag(e, pipe, which)} style={{
                        touchAction: 'none'
                      }} data-id="element-9822" />
                              </g>;
                  })}
                        </g>;
              })}
                </svg>
                {/* Hydraulic point objects (HTML icons) */}
                {hydraulicObjects.filter(o => o.kind !== 'pipe' && o.visible).map(o => {
              if (!('xPct' in o)) return null;
              const isSelected = selectedHydraulic?.id === o.id;
              const isDragging = draggingObject?.id === o.id;
              let Icon = CircleIcon;
              let bg = 'bg-cyan-500/30';
              let ring = 'ring-cyan-400/60';
              let iconColor = 'text-cyan-200';
              let label = o.name;
              if (o.kind === 'water-source') {
                Icon = DropletIcon;
                bg = 'bg-blue-500/40';
                ring = 'ring-blue-300/70';
                iconColor = 'text-blue-100';
              } else if (o.kind === 'valve') {
                Icon = HexagonIcon;
                bg = 'bg-amber-500/40';
                ring = 'ring-amber-300/70';
                iconColor = 'text-amber-100';
              } else if (o.kind === 'pivot-connection') {
                Icon = ZapIcon;
                const connected = (o as HydraulicPivotConnection).pivotId !== null;
                bg = connected ? 'bg-violet-500/40' : 'bg-amber-500/40';
                ring = connected ? 'ring-violet-300/70' : 'ring-amber-300/70';
                iconColor = connected ? 'text-violet-100' : 'text-amber-100';
              }
              // Validation flag: disconnected (no pipe references this object)
              const referenced = hydraulicObjects.some(other => other.kind === 'pipe' && (other.startObjectId === o.id || other.endObjectId === o.id));
              // Highlight if this object is the current snap candidate
              const isSnapCandidate = draggingPipeEndpoint?.snap?.kind === 'hydraulic' && draggingPipeEndpoint.snap.objectId === o.id;
              return <button key={o.id} onPointerDown={e => startObjectDrag(e, o as HydraulicObject & {
                xPct: number;
                yPct: number;
              })} onClick={e => {
                e.stopPropagation();
                setSelectedHydraulic({
                  kind: o.kind,
                  id: o.id
                });
                setSelectedPivotId(null);
              }} className={`absolute transform -translate-x-1/2 -translate-y-1/2 rounded-full p-1.5 border ${bg} ${iconColor} backdrop-blur-sm transition-shadow ${isSelected || isSnapCandidate ? `ring-2 ${isSnapCandidate ? 'ring-green-400' : ring} shadow-[0_0_12px_rgba(0,212,255,0.5)] border-[#00D4FF]` : 'border-white/30 hover:border-white/70'} ${isDragging ? 'cursor-grabbing shadow-[0_0_18px_rgba(0,212,255,0.6)] scale-110' : 'cursor-grab'}`} style={{
                left: `${o.xPct}%`,
                top: `${o.yPct}%`,
                zIndex: isSelected || isDragging ? 30 : 20,
                touchAction: 'none'
              }} title={`${label} — drag to move`} data-id="element-9823">
                        <Icon size={14} data-id="element-9824" />
                        {!referenced && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-500 border border-[#0b1628]" title="Not connected to any pipe" data-id="element-9825" />}
                        <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1 text-[9px] font-medium text-white whitespace-nowrap pointer-events-none drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]" data-id="element-9826">
                          {label}
                        </span>
                      </button>;
            })}
                {/* Pivot snap highlight while dragging a pipe endpoint */}
                {draggingPipeEndpoint?.snap?.kind === 'pivot' && (() => {
              const snap = draggingPipeEndpoint.snap;
              return <div className="absolute pointer-events-none rounded-full border-2 border-green-400 animate-pulse" style={{
                left: `${snap.x}%`,
                top: `${snap.y}%`,
                width: 28,
                height: 28,
                transform: 'translate(-50%, -50%)',
                zIndex: 25
              }} data-id="element-9827" />;
            })()}
              </>}

            {/* Canvas placeholder text */}
            {pivots.length === 0 && hydraulicObjects.length === 0 && <div className={`absolute top-8 left-8 text-sm ${baseMap !== 'none' ? 'text-white/60' : 'text-gray-400'}`} data-id="element-9828">
                <p className="text-[#F1F5FB]" data-id="element-9829">Drawing Canvas</p>
                <p className="text-xs mt-1 text-[#8A9BBA]" data-id="element-9830">
                  Click tools in the ribbon to start
                </p>
              </div>}
          </div>

          {/* Horizontal Profile Panel — stacked BELOW the canvas inside the left column so it
           spans only the canvas width and never overlaps the right pivot-properties sidebar. */}
          {selectedPivot && profilePanelOpen && selectedPivot.topography.source !== 'none' && profileType === 'radial' && (() => {
          const count = Math.max(1, Math.floor(360 / radialProfileConfig.angleStep));
          const sel = radialProfileConfig.selectedIndex % count;
          const angleRad = sel * radialProfileConfig.angleStep * Math.PI / 180;
          const samples = radialSampleCount(selectedPivot, profileSamplingDistance);
          const profile = buildRadialProfile(selectedPivot, angleRad, samples);
          return <HorizontalElevationPanel profile={profile} kind="radial" titleRight={<>
                      <span className="text-[10px] text-[#8A9BBA]" data-id="element-9832">·</span>
                      <span className="text-[10px] text-[#C5D1E4] font-mono" data-id="element-9833">
                        Radial {sel + 1} / {count}
                      </span>
                      <span className="text-[10px] text-[#8A9BBA]" data-id="element-9834">·</span>
                      <span className="text-[10px] text-[#00D4FF] font-mono font-semibold" data-id="element-9835">
                        {(sel * radialProfileConfig.angleStep).toFixed(0)}°
                      </span>
                    </>} onPrev={() => setRadialProfileConfig({
            ...radialProfileConfig,
            selectedIndex: (sel - 1 + count) % count
          })} onNext={() => setRadialProfileConfig({
            ...radialProfileConfig,
            selectedIndex: (sel + 1) % count
          })} onClose={() => {
            setProfilePanelOpen(false);
            setProfileCursorT(null);
          }} source={selectedPivot.topography.source} cursorT={profileCursorT} onCursorChange={setProfileCursorT} footerRight={`${profile.points[profile.points.length - 1].d.toFixed(0)} m radius · ${profileSamplingDistance} m sampling · ${profile.points.length} pts`} data-id="element-9831" />;
        })()}
          {selectedPivot && profilePanelOpen && selectedPivot.topography.source !== 'none' && profileType === 'tower-path' && (() => {
          const n = Math.max(1, selectedPivot.towers);
          const sel = Math.max(1, Math.min(n, towerPathTowerIndex));
          const samples = towerPathSampleCount(selectedPivot, sel, profileSamplingDistance);
          const profile = buildTowerPathProfile(selectedPivot, sel, samples);
          return <HorizontalElevationPanel profile={profile} kind="tower-path" titleRight={<>
                      <span className="text-[10px] text-[#8A9BBA]" data-id="element-9837">·</span>
                      <span className="text-[10px] text-[#C5D1E4] font-mono" data-id="element-9838">
                        Tower {sel} / {n}
                      </span>
                      <span className="text-[10px] text-[#8A9BBA]" data-id="element-9839">·</span>
                      <span className="text-[10px] text-[#00D4FF] font-mono font-semibold" data-id="element-9840">
                        r {profile.radiusM.toFixed(0)} m
                      </span>
                    </>} onPrev={() => setTowerPathTowerIndex((sel - 2 + n) % n + 1)} onNext={() => setTowerPathTowerIndex(sel % n + 1)} onClose={() => {
            setProfilePanelOpen(false);
            setProfileCursorT(null);
          }} source={selectedPivot.topography.source} cursorT={profileCursorT} onCursorChange={setProfileCursorT} footerRight={`${profile.circumferenceM.toFixed(0)} m circumference · ${profileSamplingDistance} m sampling · ${profile.points.length} pts`} data-id="element-9836" />;
        })()}
          {/* Slope Analysis chart panel — same horizontal layout as the profile panel,
           but overlays analysis-specific markers on top of the elevation curve. */}
          {selectedPivot && slopeChartPanelOpen && activeSlopeAnalysis && selectedPivot.topography.source !== 'none' && <SlopeAnalysisChartPanel pivot={selectedPivot} analysis={activeSlopeAnalysis} towerIndex={slopeTowerIndex} onTowerIndexChange={setSlopeTowerIndex} cursorT={slopeCursorT} onCursorChange={setSlopeCursorT} onClose={() => {
          setSlopeChartPanelOpen(false);
          setSlopeCursorT(null);
        }} cropClearanceConfig={cropClearanceConfig} radialProfileConfig={radialProfileConfig} onRadialProfileConfigChange={setRadialProfileConfig} samplingDistance={profileSamplingDistance} data-id="element-9841" />}
        </div>

        {/* Right Side Panel — Pivot Properties OR Layer Control */}
        {selectedPivot ? <>
            {catalogOpen && <SpansCatalogPanel onAddSpan={span => handleAddSpan(selectedPivot.id, span)} onClose={() => setCatalogOpen(false)} data-id="element-9842" />}
            {spanStyleEditorOpen && <SpanStyleEditorPanel spans={selectedPivot.spans} spanDefaults={selectedPivot.design.spanDefaults} onUpdateSpansStyle={(spanIds, style) => handleUpdateSpansStyle(selectedPivot.id, spanIds, style)} onClose={() => setSpanStyleEditorOpen(false)} data-id="element-9843" />}
            <PivotPropertiesPanel pivot={selectedPivot} onUpdate={updates => handleUpdatePivot(selectedPivot.id, updates)} onDelete={() => handleDeletePivot(selectedPivot.id)} onClose={() => setSelectedPivotId(null)} unitSystem={unitSystem} selectedSpanId={selectedSpanId} onSelectSpan={setSelectedSpanId} onAddSpan={span => handleAddSpan(selectedPivot.id, span)} onDeleteSpan={spanId => handleDeleteSpan(selectedPivot.id, spanId)} onReorderSpan={(spanId, direction) => handleReorderSpan(selectedPivot.id, spanId, direction)} onRecalculateSpans={() => handleRecalculateSpans(selectedPivot.id)} onUpdateSpansStyle={(spanIds, style) => handleUpdateSpansStyle(selectedPivot.id, spanIds, style)} catalogOpen={catalogOpen} onToggleCatalog={() => setCatalogOpen(v => !v)} spanStyleEditorOpen={spanStyleEditorOpen} onOpenSpanStyleEditor={() => setSpanStyleEditorOpen(v => !v)} onTogglePivotLayer={key => handleTogglePivotLayer(selectedPivot.id, key)} panelMode={panelMode} onPanelModeChange={setPanelMode} radialProfileConfig={radialProfileConfig} onRadialProfileConfigChange={setRadialProfileConfig} profilePanelOpen={profilePanelOpen} onProfilePanelOpenChange={open => {
          setProfilePanelOpen(open);
          if (!open) setProfileCursorT(null);
        }} profileType={profileType} onProfileTypeChange={t => {
          setProfileType(t);
          setProfileCursorT(null);
        }} towerPathTowerIndex={towerPathTowerIndex} onTowerPathTowerIndexChange={i => {
          setTowerPathTowerIndex(i);
          setProfileCursorT(null);
        }} profileSamplingDistance={profileSamplingDistance} onProfileSamplingDistanceChange={m => {
          setProfileSamplingDistance(m);
          setProfileCursorT(null);
        }} activeSlopeAnalysis={activeSlopeAnalysis} onActiveSlopeAnalysisChange={a => {
          setActiveSlopeAnalysis(a);
          setSlopeCursorT(null);
        }} slopeTowerIndex={slopeTowerIndex} onSlopeTowerIndexChange={i => {
          setSlopeTowerIndex(i);
          setSlopeCursorT(null);
        }} slopeChartPanelOpen={slopeChartPanelOpen} onSlopeChartPanelOpenChange={setSlopeChartPanelOpen} cropClearanceConfig={cropClearanceConfig} onCropClearanceConfigChange={setCropClearanceConfig} data-id="element-9844" />
          </> : selectedHydraulic ? <HydraulicPropertiesPanel selection={selectedHydraulic} objects={hydraulicObjects} pivots={pivots} onUpdate={handleUpdateHydraulicObject} onDelete={handleDeleteHydraulicObject} onClose={() => setSelectedHydraulic(null)} onSelect={sel => setSelectedHydraulic(sel)} data-id="element-9845" /> : <LayerControlPanel pivots={pivots} globalLayers={globalLayers} onTogglePivotVisibility={handleTogglePivotVisibility} onTogglePivotLayer={handleTogglePivotLayer} onToggleGlobalLayer={handleToggleGlobalLayer} onSelectPivot={id => setSelectedPivotId(id)} hydraulicObjects={hydraulicObjects} onSelectHydraulic={sel => setSelectedHydraulic(sel)} data-id="element-9846" />}
      </div>

      {/* Bottom Status Bar */}
      <div className="bg-[#0B1628] border-t border-[#2F4268] flex-shrink-0 h-[36px] flex items-center justify-between px-2" data-id="element-9847">
        {/* Version Tabs — left */}
        <div className="flex items-center gap-0 overflow-x-auto no-scrollbar h-full" data-id="element-9848">
          {versions.map(version => <div key={version.id} onClick={() => setActiveVersion(version.id)} className={`flex items-center gap-1.5 px-3 h-full cursor-pointer transition-colors text-xs whitespace-nowrap relative ${activeVersion === version.id ? 'bg-[#1C2B47] text-[#F1F5FB] font-semibold' : 'text-[#C5D1E4] font-medium hover:text-[#F1F5FB] hover:bg-[#1C2B47]/50'}`} data-id="element-9849">
              {activeVersion === version.id && <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#00D4FF]" data-id="element-9850" />}
              <span data-id="element-9851">{version.name}</span>
              {versions.length > 1 && <button onClick={e => {
            e.stopPropagation();
            handleRemoveVersion(version.id);
          }} className="text-[#8A9BBA] hover:text-white hover:bg-[#2A3B5C] transition-colors p-0.5 -mr-1 rounded" data-id="element-9852">
                  <XIcon size={10} data-id="element-9853" />
                </button>}
            </div>)}
          <button onClick={handleAddVersion} className="text-[#8A9BBA] hover:text-[#00D4FF] hover:bg-[#2A3B5C] p-1 rounded transition-colors ml-1" title="Add new version" data-id="element-9854">
            <PlusIcon size={14} data-id="element-9855" />
          </button>
        </div>

        {/* Right side: Base Map | Projection | Units | Coordinates */}
        <div className="flex items-center gap-0.5 flex-shrink-0 ml-4" data-id="element-9856">
          {/* Base Map Selector */}
          <div className="relative" ref={baseMapRef} data-id="element-9857">
            <button onClick={() => setBaseMapMenuOpen(!baseMapMenuOpen)} className="flex items-center gap-1.5 px-2.5 py-0.5 rounded text-[11px] font-medium text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5 transition-colors group" title="Base map source" data-id="element-9858">
              <MapIcon size={13} className="opacity-80 group-hover:opacity-100" data-id="element-9859" />
              <span data-id="element-9860">{currentBaseMapLabel}</span>
              <ChevronUpIcon size={10} className={`transition-transform duration-150 ${baseMapMenuOpen ? 'rotate-180' : ''}`} data-id="element-9861" />
            </button>

            {/* Dropdown menu — opens upward */}
            {baseMapMenuOpen && <div className="absolute bottom-full left-0 mb-1 bg-[#0f1d3a] rounded-lg border border-white/10 shadow-2xl overflow-hidden min-w-[160px] z-50" data-id="element-9862">
                {BASE_MAP_OPTIONS.map(option => <button key={option.id} onClick={() => {
              setBaseMap(option.id);
              setBaseMapMenuOpen(false);
            }} className={`w-full flex items-center gap-2 px-3 py-2 text-[11px] font-medium transition-colors text-left ${baseMap === option.id ? 'bg-white/10 text-white' : 'text-blue-200/70 hover:bg-white/5 hover:text-white'}`} data-id="element-9863">
                    {baseMap === option.id && <div className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" data-id="element-9864" />}
                    {baseMap !== option.id && <div className="w-1.5 h-1.5 flex-shrink-0" data-id="element-9865" />}
                    <span data-id="element-9866">{option.label}</span>
                  </button>)}
              </div>}
          </div>

          {/* Separator */}
          <div className="w-px h-4 bg-white/10 mx-1" data-id="element-9867" />

          {/* Projection */}
          <button className="flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-medium text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5 transition-colors group" title="Coordinate Reference System" data-id="element-9868">
            <GlobeIcon size={13} className="opacity-80 group-hover:opacity-100" data-id="element-9869" />
            <span data-id="element-9870">CRS:84</span>
          </button>

          {/* Separator */}
          <div className="w-px h-4 bg-white/10 mx-1" data-id="element-9871" />

          {/* Unit System */}
          <button onClick={() => setUnitSystem(unitSystem === 'metric' ? 'us-standard' : 'metric')} className="flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-medium text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5 transition-colors group" title="Toggle unit system" data-id="element-9872">
            <RulerIcon size={13} className="opacity-80 group-hover:opacity-100" data-id="element-9873" />
            <span data-id="element-9874">{unitSystem === 'metric' ? 'Metric' : 'US Standard'}</span>
          </button>

          {/* Separator */}
          <div className="w-px h-4 bg-white/10 mx-1" data-id="element-9875" />

          {/* Coordinates */}
          <span className="text-[#C5D1E4] text-[11px] font-mono px-1" data-id="element-9876">
            80.3455 ; 23.4899
          </span>
        </div>
      </div>
    </div>;
}
function RibbonSection({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div className="flex flex-col border-r border-white/10 last:border-r-0" data-id="element-9877">
      {/* Tools row */}
      <div className="flex gap-0.5 px-2 pt-2 pb-1.5 flex-1" data-id="element-9878">{children}</div>
      {/* Section label */}
      <div className="border-t border-white/[0.06] px-2 py-1" data-id="element-9879">
        <span className="text-[10px] text-[#8A9BBA] font-medium tracking-wide text-center block uppercase" data-id="element-9880">
          {label}
        </span>
      </div>
    </div>;
}
function RibbonTool({
  icon,
  label,
  onClick
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return <button onClick={onClick} className="flex flex-col items-center justify-center px-2.5 py-1 rounded hover:bg-white/10 transition-colors group min-w-[60px]" data-id="element-9881">
      <div className="text-[#C5D1E4] group-hover:text-[#F1F5FB] transition-colors mb-0.5" data-id="element-9882">
        {icon}
      </div>
      <span className="text-[#C5D1E4] group-hover:text-[#F1F5FB] transition-colors text-center text-[10px] leading-tight" data-id="element-9883">
        {label}
      </span>
    </button>;
}
// Custom SVG Icons for Irrigation Machines
function PivotFullCircleIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/t426MnDWi6vKjFroEFiiEG/pivot_full_circle.svg" alt="Pivot Full Circle" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9884" />;
}
function PivotPartCircleIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/vJGELGZ5TYT3TmRbBxZn4K/pivot_part_circle.svg" alt="Pivot Part Circle" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9885" />;
}
function LinealEndFeedIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/d6Rj7H3BJJpaY4ruiNQFqN/lineal_end_feed.svg" alt="Lineal EndFeed" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9886" />;
}
function LinealCenterFeedIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/n6vjXYtJr6nPr7QKMk8SnZ/center_feed.svg" alt="Lineal Center Feed" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9887" />;
}
function LinealSwingAroundIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/wjVXtWu3bButTsLqniBh5H/swing_around.svg" alt="Swing Around" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9888" />;
}
function FarmBoundaryIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/rpr57N2xXozSKDxU4Etw92/farm_boundary.svg" alt="Farm Boundary" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9889" />;
}
function PolylineIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/e3BxfS7zZq2ejBPrgvzuqV/polyline.svg" alt="Polyline" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9890" />;
}
function ArcIcon() {
  return <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" data-id="element-9891">
      <path d="M 4 16 Q 12 4 20 16" strokeWidth="2" fill="none" data-id="element-9892" />
      <circle cx="4" cy="16" r="1.5" fill="currentColor" data-id="element-9893" />
      <circle cx="20" cy="16" r="1.5" fill="currentColor" data-id="element-9894" />
    </svg>;
}
function LineIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/3rn8zQqNKSCCRuFaxiwtrt/line.svg" alt="Line" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9895" />;
}
function DrawArcIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/ipKcENdRXvKgjo7TJ5WjD4/arc.svg" alt="Arc" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9896" />;
}
function PointIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/irw4Jimuo9s35j33KJs4o7/point.svg" alt="Point" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9897" />;
}
function DrawCircleIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/qRfyeFFE35CpHYRoVg6GDq/circle.svg" alt="Circle" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9898" />;
}
function PolygonIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/qVQz9RaTSRThGRRUCLxZ9K/polygon.svg" alt="Polygon" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9899" />;
}
function ContoursIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/xnCsqnVPDZK6en6PRu1o3f/curvas-de-nivel.svg" alt="Contours" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9900" />;
}
function CotaIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/gHHxDe9GZqDww2733aW29d/cota-de-nivel.svg" alt="Cota" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9901" />;
}
function ProfileIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/iNF4ZB4CT5chcDJtatWSBg/profile.svg" alt="Profile" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9902" />;
}
function ElevationMapIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/gZzmiExYfzhM3qSpvbLZr4/mapa-de-elevaciones.svg" alt="Elevation Map" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9903" />;
}
// ============================================================
// Topographic contour generator — marching squares over a
// multi-peak Gaussian heightfield. Produces real-looking iso-
// elevation curves like a hand-drawn topo map.
// ============================================================
function buildTopoContours(pivot: Pivot) {
  const eq = Math.max(0.1, pivot.topography.contourEquidistance);
  const minE = pivot.topography.minElevation;
  const maxE = pivot.topography.maxElevation;
  const elevDelta = maxE - minE;
  if (elevDelta <= 0) return null;
  const lineCount = Math.min(60, Math.max(2, Math.floor(elevDelta / eq)));
  if (lineCount < 2) return null;
  // Precision drives both grid resolution and number of terrain features
  const precCfg: Record<ContourPrecision, {
    grid: number;
    peaks: number;
  }> = {
    '30m': {
      grid: 36,
      peaks: 3
    },
    '10m': {
      grid: 52,
      peaks: 5
    },
    '5m': {
      grid: 68,
      peaks: 7
    },
    '1m': {
      grid: 88,
      peaks: 9
    }
  };
  const {
    grid: N,
    peaks: peakCount
  } = precCfg[pivot.topography.contourPrecision] || precCfg['30m'];
  // Deterministic PRNG seeded by pivot.id so each pivot has stable terrain
  const mulberry32 = (a: number) => () => {
    a |= 0;
    a = a + 0x6d2b79f5 | 0;
    let t = a;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
  const rand = mulberry32(pivot.id * 9973 + 12345);
  // Build a Gaussian-bump heightfield: several positive (peaks) + negative (basins)
  type Bump = {
    cx: number;
    cy: number;
    amp: number;
    sigma: number;
  };
  const bumps: Bump[] = [];
  for (let i = 0; i < peakCount; i++) {
    // Spread bumps inside the unit disc
    const r = 0.15 + rand() * 0.7;
    const theta = rand() * Math.PI * 2;
    bumps.push({
      cx: 0.5 + Math.cos(theta) * r * 0.5,
      cy: 0.5 + Math.sin(theta) * r * 0.5,
      amp: (rand() < 0.55 ? 1 : -1) * (0.4 + rand() * 1),
      sigma: 0.08 + rand() * 0.18
    });
  }
  // Ensure at least one strong peak and one strong valley
  bumps[0] = {
    ...bumps[0],
    amp: 1.2,
    sigma: 0.18
  };
  if (bumps.length > 1) bumps[1] = {
    ...bumps[1],
    amp: -1.1,
    sigma: 0.2
  };
  // Sample field on N+1 × N+1 grid in unit square [0..1]
  const field = new Float32Array((N + 1) * (N + 1));
  let fMin = Infinity;
  let fMax = -Infinity;
  for (let j = 0; j <= N; j++) {
    for (let i = 0; i <= N; i++) {
      const u = i / N;
      const v = j / N;
      let h = 0;
      for (const b of bumps) {
        const dx = u - b.cx;
        const dy = v - b.cy;
        const d2 = dx * dx + dy * dy;
        h += b.amp * Math.exp(-d2 / (2 * b.sigma * b.sigma));
      }
      field[j * (N + 1) + i] = h;
      if (h < fMin) fMin = h;
      if (h > fMax) fMax = h;
    }
  }
  const fRange = fMax - fMin || 1;
  // Map unit square [0..1] → SVG viewBox space [0..100] centered, clipped to circle r=49
  const toX = (u: number) => u * 100;
  const toY = (v: number) => v * 100;
  // Marching squares for a single iso-value
  const isoSegments = (iso: number): number[][] => {
    const segs: number[][] = [];
    for (let j = 0; j < N; j++) {
      for (let i = 0; i < N; i++) {
        const tl = field[j * (N + 1) + i];
        const tr = field[j * (N + 1) + (i + 1)];
        const br = field[(j + 1) * (N + 1) + (i + 1)];
        const bl = field[(j + 1) * (N + 1) + i];
        let idx = 0;
        if (tl > iso) idx |= 1;
        if (tr > iso) idx |= 2;
        if (br > iso) idx |= 4;
        if (bl > iso) idx |= 8;
        if (idx === 0 || idx === 15) continue;
        // Cell coords in unit space
        const x0 = i / N,
          x1 = (i + 1) / N;
        const y0 = j / N,
          y1 = (j + 1) / N;
        // Linear interpolation for crossings
        const lerp = (a: number, b: number) => (iso - a) / (b - a);
        const top = () => [x0 + lerp(tl, tr) * (x1 - x0), y0];
        const right = () => [x1, y0 + lerp(tr, br) * (y1 - y0)];
        const bottom = () => [x0 + lerp(bl, br) * (x1 - x0), y1];
        const left = () => [x0, y0 + lerp(tl, bl) * (y1 - y0)];
        const pushSeg = (a: number[], b: number[]) => segs.push([a[0], a[1], b[0], b[1]]);
        switch (idx) {
          case 1:
          case 14:
            pushSeg(left(), top());
            break;
          case 2:
          case 13:
            pushSeg(top(), right());
            break;
          case 3:
          case 12:
            pushSeg(left(), right());
            break;
          case 4:
          case 11:
            pushSeg(right(), bottom());
            break;
          case 5:
            pushSeg(left(), top());
            pushSeg(right(), bottom());
            break;
          case 6:
          case 9:
            pushSeg(top(), bottom());
            break;
          case 7:
          case 8:
            pushSeg(left(), bottom());
            break;
          case 10:
            pushSeg(left(), bottom());
            pushSeg(top(), right());
            break;
        }
      }
    }
    return segs;
  };
  // Build path data for regular vs index lines
  let regularD = '';
  let indexD = '';
  const labels: {
    x: number;
    y: number;
    text: string;
    angle: number;
  }[] = [];
  for (let k = 0; k < lineCount; k++) {
    const t = k / (lineCount - 1); // 0..1
    const iso = fMin + t * fRange;
    const elev = minE + t * elevDelta;
    const isIndex = k % 5 === 0 || k === lineCount - 1;
    const segs = isoSegments(iso);
    let segPath = '';
    for (const s of segs) {
      segPath += `M${toX(s[0]).toFixed(2)},${toY(s[1]).toFixed(2)}L${toX(s[2]).toFixed(2)},${toY(s[3]).toFixed(2)} `;
    }
    if (isIndex) {
      indexD += segPath;
      // Pick a label on the longest segment of this level, near pivot center
      if (segs.length > 0 && k > 0 && k < lineCount - 1) {
        let best = segs[0];
        let bestDist = Infinity;
        for (const s of segs) {
          const mx = (s[0] + s[2]) / 2;
          const my = (s[1] + s[3]) / 2;
          const dCenter = Math.hypot(mx - 0.5, my - 0.5);
          // Prefer mid-radius positions (more readable, less crowded)
          const score = Math.abs(dCenter - 0.32);
          if (score < bestDist) {
            bestDist = score;
            best = s;
          }
        }
        const mx = toX((best[0] + best[2]) / 2);
        const my = toY((best[1] + best[3]) / 2);
        const angle = Math.atan2(best[3] - best[1], best[2] - best[0]) * 180 / Math.PI;
        // Skip labels that would land outside the pivot circle
        if (Math.hypot(mx - 50, my - 50) < 44) {
          labels.push({
            x: mx,
            y: my,
            text: `${Math.round(elev)}`,
            angle: Math.abs(angle) > 90 ? angle + 180 : angle
          });
        }
      }
    } else {
      regularD += segPath;
    }
  }
  // Find global peak & valley in viewBox coords
  let peakX = 50,
    peakY = 50,
    valleyX = 50,
    valleyY = 50;
  let pMax = -Infinity,
    pMin = Infinity;
  for (let j = 0; j <= N; j++) {
    for (let i = 0; i <= N; i++) {
      const v = field[j * (N + 1) + i];
      const u = i / N;
      const w = j / N;
      // Only consider points inside the circle
      if (Math.hypot(u - 0.5, w - 0.5) > 0.42) continue;
      if (v > pMax) {
        pMax = v;
        peakX = toX(u);
        peakY = toY(w);
      }
      if (v < pMin) {
        pMin = v;
        valleyX = toX(u);
        valleyY = toY(w);
      }
    }
  }
  return {
    paths: regularD,
    indexPaths: indexD,
    labels,
    peak: {
      x: peakX,
      y: peakY
    },
    valley: {
      x: valleyX,
      y: valleyY
    }
  };
}
// Sample elevation (in meters) at unit-square coords (u, v) ∈ [0..1]² inside the
// pivot's heightfield. Mirrors the bump/seed logic of buildTopoContours so the
// values shown in radial profiles match the contour map exactly.
function samplePivotElevation(pivot: Pivot, u: number, v: number): number {
  const precCfg: Record<ContourPrecision, {
    peaks: number;
  }> = {
    '30m': {
      peaks: 3
    },
    '10m': {
      peaks: 5
    },
    '5m': {
      peaks: 7
    },
    '1m': {
      peaks: 9
    }
  };
  const peakCount = (precCfg[pivot.topography.contourPrecision] || precCfg['30m']).peaks;
  const mulberry32 = (a: number) => () => {
    a |= 0;
    a = a + 0x6d2b79f5 | 0;
    let t = a;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
  const rand = mulberry32(pivot.id * 9973 + 12345);
  const bumps: {
    cx: number;
    cy: number;
    amp: number;
    sigma: number;
  }[] = [];
  for (let i = 0; i < peakCount; i++) {
    const r = 0.15 + rand() * 0.7;
    const theta = rand() * Math.PI * 2;
    bumps.push({
      cx: 0.5 + Math.cos(theta) * r * 0.5,
      cy: 0.5 + Math.sin(theta) * r * 0.5,
      amp: (rand() < 0.55 ? 1 : -1) * (0.4 + rand() * 1),
      sigma: 0.08 + rand() * 0.18
    });
  }
  bumps[0] = {
    ...bumps[0],
    amp: 1.2,
    sigma: 0.18
  };
  if (bumps.length > 1) bumps[1] = {
    ...bumps[1],
    amp: -1.1,
    sigma: 0.2
  };
  // Compute scalar field at (u,v) — macro shape from Gaussian bumps
  let h = 0;
  for (const b of bumps) {
    const dx = u - b.cx;
    const dy = v - b.cy;
    const d2 = dx * dx + dy * dy;
    h += b.amp * Math.exp(-d2 / (2 * b.sigma * b.sigma));
  }
  // Normalize against a coarse sample of the macro field range — same approach as
  // buildTopoContours so peak/valley values match the rendered contours.
  let fMin = Infinity;
  let fMax = -Infinity;
  const SAMPLE = 12;
  for (let j = 0; j <= SAMPLE; j++) {
    for (let i = 0; i <= SAMPLE; i++) {
      const uu = i / SAMPLE;
      const vv = j / SAMPLE;
      let hh = 0;
      for (const b of bumps) {
        const dx = uu - b.cx;
        const dy = vv - b.cy;
        const d2 = dx * dx + dy * dy;
        hh += b.amp * Math.exp(-d2 / (2 * b.sigma * b.sigma));
      }
      if (hh < fMin) fMin = hh;
      if (hh > fMax) fMax = hh;
    }
  }
  const t = (h - fMin) / (fMax - fMin || 1);
  const macroElev = pivot.topography.minElevation + t * (pivot.topography.maxElevation - pivot.topography.minElevation);
  // Add deterministic multi-octave high-frequency micro-detail so radial elevation
  // profiles read like real terrain (small spikes / wiggles) instead of a smooth blob.
  // Seeded by pivot.id so it's stable per pivot.
  const seed = pivot.id * 9973 + 12345;
  const ph = (n: number) => Math.sin(seed * n * 0.123 + n * 7.91) * 1000 - Math.floor(Math.sin(seed * n * 0.123 + n * 7.91) * 1000);
  const range = pivot.topography.maxElevation - pivot.topography.minElevation || 1;
  // Three octaves: medium, high, very-high frequency. Each at diminishing amplitude.
  // Total micro amplitude ≈ 14% of range — enough to feel natural without dominating
  // the macro slope.
  const ampMicro = range * 0.14;
  const oct1 = Math.sin(u * 18.7 + ph(1) * 6.28) * Math.cos(v * 17.3 + ph(2) * 6.28) * 0.55;
  const oct2 = Math.sin(u * 41.2 + ph(3) * 6.28) * Math.cos(v * 38.6 + ph(4) * 6.28) * 0.3;
  const oct3 = Math.sin(u * 89.7 + ph(5) * 6.28) * Math.cos(v * 83.1 + ph(6) * 6.28) * 0.15;
  const micro = (oct1 + oct2 + oct3) * ampMicro;
  return macroElev + micro;
}
// Sample-count helpers — convert a sampling distance (meters) into a sample count
// for each profile type. We clamp to a sane band so the chart stays usable at
// either extreme.
const MIN_SAMPLES = 8;
const MAX_SAMPLES = 800;
function radialSampleCount(pivot: Pivot, samplingDistanceM: number): number {
  const lengthM = pivot.radiusPx; // mock: 1 px ≈ 1 m
  const raw = Math.ceil(lengthM / Math.max(0.1, samplingDistanceM));
  return Math.min(MAX_SAMPLES, Math.max(MIN_SAMPLES, raw));
}
function towerPathSampleCount(pivot: Pivot, towerIndex: number, samplingDistanceM: number): number {
  const r = Math.max(1, Math.min(pivot.towers, towerIndex)) / Math.max(1, pivot.towers) * pivot.radiusPx;
  const lengthM = 2 * Math.PI * r;
  const raw = Math.ceil(lengthM / Math.max(0.1, samplingDistanceM));
  return Math.min(MAX_SAMPLES, Math.max(MIN_SAMPLES, raw));
}
// Build elevation profile along a radial from pivot center (u=0.5, v=0.5) at given angle.
// Returns N+1 samples along the radial, distances in meters.
function buildRadialProfile(pivot: Pivot, angleRad: number, samples: number = 60): {
  points: {
    d: number;
    elev: number;
    t: number;
  }[]; // d = distance in meters
  minElev: number;
  maxElev: number;
  totalElev: number;
  maxAscent: number; // % positive slope
  maxDescent: number; // % negative slope
} {
  const radiusM = pivot.radiusPx; // px ≈ meters in this mock
  const cosA = Math.cos(angleRad);
  const sinA = Math.sin(angleRad);
  const pts: {
    d: number;
    elev: number;
    t: number;
  }[] = [];
  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    // Stay inside the pivot circle (radius 0.5 in unit-square space)
    const u = 0.5 + cosA * t * 0.5;
    const v = 0.5 + sinA * t * 0.5;
    const elev = samplePivotElevation(pivot, u, v);
    pts.push({
      d: t * radiusM,
      elev,
      t
    });
  }
  let minElev = Infinity;
  let maxElev = -Infinity;
  for (const p of pts) {
    if (p.elev < minElev) minElev = p.elev;
    if (p.elev > maxElev) maxElev = p.elev;
  }
  let maxAscent = 0;
  let maxDescent = 0;
  for (let i = 1; i < pts.length; i++) {
    const dd = pts[i].d - pts[i - 1].d;
    const de = pts[i].elev - pts[i - 1].elev;
    if (dd > 0) {
      const slope = de / dd * 100;
      if (slope > maxAscent) maxAscent = slope;
      if (slope < maxDescent) maxDescent = slope;
    }
  }
  return {
    points: pts,
    minElev,
    maxElev,
    totalElev: maxElev - minElev,
    maxAscent,
    maxDescent
  };
}
// ============================================================
// Tower Path Profile — sample elevation around the FULL circular
// path of a single tower (the trajectory it traces from 0° → 360°).
// ============================================================
// Returns the radius (in meters / px in this mock) of the i-th tower (1-based)
// measured from the pivot center. Distributes towers evenly along the pivot
// radius, matching the canvas tower-marker placement.
function towerRadius(pivot: Pivot, towerIndex: number): number {
  const n = Math.max(1, pivot.towers);
  const i = Math.max(1, Math.min(n, towerIndex));
  return i / n * pivot.radiusPx;
}
function buildTowerPathProfile(pivot: Pivot, towerIndex: number, samples: number = 240): {
  points: {
    d: number; // here d = angle in DEGREES (0..360) for chart X-axis
    elev: number;
    t: number; // 0..1 around the circle
  }[];
  minElev: number;
  maxElev: number;
  totalElev: number;
  maxAscent: number;
  maxDescent: number;
  radiusM: number; // tower's path radius (meters)
  circumferenceM: number;
} {
  const radiusM = towerRadius(pivot, towerIndex);
  // Unit-square fraction of the pivot radius (radius=0.5 in unit space)
  const unitR = radiusM / pivot.radiusPx * 0.5;
  const pts: {
    d: number;
    elev: number;
    t: number;
  }[] = [];
  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    const angleRad = t * Math.PI * 2;
    const u = 0.5 + Math.cos(angleRad) * unitR;
    const v = 0.5 + Math.sin(angleRad) * unitR;
    const elev = samplePivotElevation(pivot, u, v);
    pts.push({
      d: t * 360,
      elev,
      t
    });
  }
  let minElev = Infinity;
  let maxElev = -Infinity;
  for (const p of pts) {
    if (p.elev < minElev) minElev = p.elev;
    if (p.elev > maxElev) maxElev = p.elev;
  }
  // Slope is measured per meter of arc, not per degree, so convert deltas.
  const circumferenceM = 2 * Math.PI * radiusM;
  let maxAscent = 0;
  let maxDescent = 0;
  for (let i = 1; i < pts.length; i++) {
    const dAngle = pts[i].d - pts[i - 1].d; // in degrees
    const dArcM = dAngle / 360 * circumferenceM;
    const de = pts[i].elev - pts[i - 1].elev;
    if (dArcM > 0) {
      const slope = de / dArcM * 100;
      if (slope > maxAscent) maxAscent = slope;
      if (slope < maxDescent) maxDescent = slope;
    }
  }
  return {
    points: pts,
    minElev,
    maxElev,
    totalElev: maxElev - minElev,
    maxAscent,
    maxDescent,
    radiusM,
    circumferenceM
  };
}
function hexToRgba(hex: string, alpha: number) {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
let _spanUid = 1000;
const nextSpanUid = () => ++_spanUid;
function createDefaultSpans(): Span[] {
  return [{
    id: nextSpanUid(),
    kind: 'elbow',
    diameter: 'Elbow',
    length: 0.61
  }, {
    id: nextSpanUid(),
    kind: 'span',
    diameter: '8"5/8',
    length: 56.08
  }, {
    id: nextSpanUid(),
    kind: 'span',
    diameter: '8"5/8',
    length: 56.08
  }, {
    id: nextSpanUid(),
    kind: 'span',
    diameter: '8"5/8',
    length: 56.08
  }, {
    id: nextSpanUid(),
    kind: 'span',
    diameter: '6"5/8',
    length: 62.03
  }, {
    id: nextSpanUid(),
    kind: 'span',
    diameter: '6"5/8',
    length: 62.03
  }, {
    id: nextSpanUid(),
    kind: 'span',
    diameter: '6"5/8',
    length: 62.03
  }, {
    id: nextSpanUid(),
    kind: 'span',
    diameter: '6"5/8',
    length: 62.03
  }, {
    id: nextSpanUid(),
    kind: 'span',
    diameter: '6"5/8',
    length: 62.03
  }, {
    id: nextSpanUid(),
    kind: 'overhang',
    diameter: '6"5/8',
    length: 19.7
  }];
}
const SPAN_CATALOG: {
  kind: SpanKind;
  diameter: string;
  length: number;
}[] = [{
  kind: 'elbow',
  diameter: 'Elbow',
  length: 0.61
}, {
  kind: 'span',
  diameter: '8"5/8',
  length: 56.08
}, {
  kind: 'span',
  diameter: '6"5/8',
  length: 62.03
}, {
  kind: 'span',
  diameter: '6"5/8',
  length: 48.77
}, {
  kind: 'span',
  diameter: '5"9/16',
  length: 38.1
}, {
  kind: 'overhang',
  diameter: '6"5/8',
  length: 19.7
}, {
  kind: 'overhang',
  diameter: '6"5/8',
  length: 25.3
}];
function SpanStyleEditorPanel({
  spans,
  spanDefaults,
  onUpdateSpansStyle,
  onClose
}: {
  spans: Span[];
  spanDefaults: SpanDefaults;
  onUpdateSpansStyle: (spanIds: number[], style: SpanStyle | null) => void;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [color, setColor] = useState<string>(spanDefaults.color);
  const [lineStyle, setLineStyle] = useState<SpanLineStyle>(spanDefaults.lineStyle);
  const [fillEnabled, setFillEnabled] = useState<boolean>(spanDefaults.fillColor !== null);
  const [fillColor, setFillColor] = useState<string>(spanDefaults.fillColor || '#FBBF24');
  const toggleSpan = (id: number) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);else next.add(id);
    return next;
  });
  const apply = () => {
    if (selected.size === 0) return;
    onUpdateSpansStyle(Array.from(selected), {
      color,
      lineStyle,
      fillColor: fillEnabled ? fillColor : null
    });
  };
  const resetSelected = () => {
    if (selected.size === 0) return;
    onUpdateSpansStyle(Array.from(selected), null);
  };
  return <aside className="w-[300px] bg-[#0a1322] border-l border-[#2F4268] flex flex-col flex-shrink-0" data-id="element-9904">
      {/* Header */}
      <div className="flex items-center justify-between px-3 h-[42px] border-b border-[#2F4268] flex-shrink-0" data-id="element-9905">
        <div className="flex items-center gap-2 min-w-0" data-id="element-9906">
          <PaletteIcon size={14} className="text-[#00D4FF] flex-shrink-0" data-id="element-9907" />
          <div className="flex flex-col min-w-0" data-id="element-9908">
            <span className="text-[#F1F5FB] text-xs font-semibold truncate leading-tight" data-id="element-9909">
              Customize Spans
            </span>
            <span className="text-[9px] text-[#8A9BBA] uppercase tracking-wide leading-tight" data-id="element-9910">
              Per-span overrides
            </span>
          </div>
        </div>
        <button onClick={onClose} className="text-[#8A9BBA] hover:text-[#F1F5FB] hover:bg-white/5 p-1 rounded transition-colors flex-shrink-0" title="Close" data-id="element-9911">
          <XIcon size={13} data-id="element-9912" />
        </button>
      </div>

      {/* Selection toolbar */}
      <div className="flex items-center gap-1 px-3 py-1.5 border-b border-[#2F4268]/60 bg-[#0b1628]" data-id="element-9913">
        <button onClick={() => setSelected(new Set(spans.map(s => s.id)))} className="text-[10px] text-[#00D4FF] hover:underline" data-id="element-9914">
          Select all
        </button>
        <span className="text-[10px] text-[#2F4268]" data-id="element-9915">·</span>
        <button onClick={() => setSelected(new Set())} className="text-[10px] text-[#8A9BBA] hover:text-[#F1F5FB]" data-id="element-9916">
          Clear
        </button>
        <span className="ml-auto text-[10px] text-[#8A9BBA] font-mono" data-id="element-9917">
          {selected.size} / {spans.length}
        </span>
      </div>

      {/* Span list */}
      <div className="flex-1 overflow-y-auto" data-id="element-9918">
        {spans.length === 0 ? <div className="px-3 py-6 text-center" data-id="element-9919">
            <p className="text-[11px] text-[#8A9BBA] italic" data-id="element-9920">No spans.</p>
          </div> : <ul className="divide-y divide-[#2F4268]/30" data-id="element-9921">
            {spans.map((span, idx) => {
          const checked = selected.has(span.id);
          const hasOverride = !!span.style;
          const effColor = span.style?.color || spanDefaults.color;
          return <li key={span.id} onClick={() => toggleSpan(span.id)} className={`flex items-center gap-2 px-3 py-1 cursor-pointer transition-colors ${checked ? 'bg-[#1C2B47]' : 'hover:bg-white/[0.03]'}`} data-id="element-9922">
                  <input type="checkbox" checked={checked} readOnly className="w-3 h-3 accent-[#00D4FF] flex-shrink-0 cursor-pointer" data-id="element-9923" />
                  <span className={`inline-flex items-center justify-center w-4 h-4 rounded text-[8px] font-mono font-semibold flex-shrink-0 ${checked ? 'bg-[#00D4FF] text-[#0b1628]' : 'bg-[#132038] text-[#C5D1E4] border border-[#2F4268]'}`} data-id="element-9924">
                    {idx + 1}
                  </span>
                  <span className="text-[10px] text-[#F1F5FB] truncate flex-1" data-id="element-9925">
                    {span.kind === 'elbow' ? 'Elbow' : `Span ${span.diameter}`}
                  </span>
                  <span className="w-3 h-3 rounded-full border border-[#2F4268] flex-shrink-0" style={{
              backgroundColor: effColor
            }} title={hasOverride ? 'Custom style' : 'Inherits default'} data-id="element-9926" />
                  {hasOverride && <span className="w-1 h-1 rounded-full bg-[#FBBF24] flex-shrink-0" title="Has per-span override" data-id="element-9927" />}
                </li>;
        })}
          </ul>}
      </div>

      {/* Controls */}
      <div className="border-t border-[#2F4268] bg-[#0b1628]" data-id="element-9928">
        <PropRow label="Line Color" data-id="element-9929">
          <InlineColor value={color} onChange={setColor} data-id="element-9930" />
        </PropRow>
        <PropRow label="Line Type" data-id="element-9931">
          <div className="flex gap-1" data-id="element-9932">
            {(['solid', 'dashed', 'dotted'] as const).map(lt => {
            const isActive = lineStyle === lt;
            return <button key={lt} onClick={() => setLineStyle(lt)} className={`flex-1 px-1.5 py-1 rounded border transition-colors flex items-center justify-center ${isActive ? 'border-[#00D4FF] bg-[#1C2B47]' : 'border-[#2F4268] hover:border-[#4A5F8C] bg-[#132038]'}`} title={lt} data-id="element-9933">
                  <svg width="28" height="6" viewBox="0 0 28 6" data-id="element-9934">
                    <line x1="1" y1="3" x2="27" y2="3" stroke={isActive ? '#00D4FF' : '#C5D1E4'} strokeWidth="1.4" strokeDasharray={lt === 'dashed' ? '4 2' : lt === 'dotted' ? '1 2' : undefined} strokeLinecap="round" data-id="element-9935" />
                  </svg>
                </button>;
          })}
          </div>
        </PropRow>
        <PropRow label="Fill Color" data-id="element-9936">
          <div className="flex items-center gap-1.5" data-id="element-9937">
            <InlineToggle value={fillEnabled} onChange={setFillEnabled} data-id="element-9938" />
            {fillEnabled && <InlineColor value={fillColor} onChange={setFillColor} data-id="element-9939" />}
          </div>
        </PropRow>

        <div className="flex gap-1.5 px-3 py-2 border-t border-[#2F4268]/40" data-id="element-9940">
          <button disabled={selected.size === 0} onClick={apply} className="flex-1 px-2 py-1 rounded text-[10px] font-medium text-[#0b1628] bg-[#00D4FF] hover:bg-[#7AE5FF] disabled:bg-[#2F4268] disabled:text-[#8A9BBA] disabled:cursor-not-allowed transition-colors" data-id="element-9941">
            Apply to {selected.size || 0}
          </button>
          <button disabled={selected.size === 0} onClick={resetSelected} className="px-2 py-1 rounded text-[10px] font-medium text-[#C5D1E4] hover:text-[#F1F5FB] border border-[#2F4268] hover:border-[#4A5F8C] disabled:opacity-40 disabled:cursor-not-allowed transition-colors" title="Reset selected spans to defaults" data-id="element-9942">
            Reset
          </button>
        </div>
      </div>
    </aside>;
}
function SpansCatalogPanel({
  onAddSpan,
  onClose
}: {
  onAddSpan: (span: Omit<Span, 'id'>) => void;
  onClose: () => void;
}) {
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const setQty = (idx: number, v: number) => {
    setQuantities(prev => ({
      ...prev,
      [idx]: Math.max(0, v)
    }));
  };
  const handleAdd = (idx: number, item: {
    kind: SpanKind;
    diameter: string;
    length: number;
  }) => {
    const qty = quantities[idx] ?? 1;
    if (qty <= 0) return;
    for (let i = 0; i < qty; i++) {
      onAddSpan({
        kind: item.kind,
        diameter: item.diameter,
        length: item.length
      });
    }
    setQuantities(prev => ({
      ...prev,
      [idx]: 1
    }));
  };
  return <aside className="w-[280px] bg-[#0a1322] border-l border-[#2F4268] flex flex-col flex-shrink-0" data-id="element-9943">
      {/* Header */}
      <div className="flex items-center justify-between px-3 h-[42px] border-b border-[#2F4268] flex-shrink-0" data-id="element-9944">
        <div className="flex items-center gap-2 min-w-0" data-id="element-9945">
          <BookOpenIcon size={14} className="text-[#00D4FF] flex-shrink-0" data-id="element-9946" />
          <div className="flex flex-col min-w-0" data-id="element-9947">
            <span className="text-[#F1F5FB] text-xs font-semibold truncate leading-tight" data-id="element-9948">
              Spans Catalog
            </span>
            <span className="text-[9px] text-[#8A9BBA] uppercase tracking-wide leading-tight" data-id="element-9949">
              Add to active pivot
            </span>
          </div>
        </div>
        <button onClick={onClose} className="text-[#8A9BBA] hover:text-[#F1F5FB] hover:bg-white/5 p-1 rounded transition-colors flex-shrink-0" title="Close catalog" data-id="element-9950">
          <XIcon size={13} data-id="element-9951" />
        </button>
      </div>

      {/* Model selector (mock) */}
      <div className="px-3 py-2 border-b border-[#2F4268]/60 bg-[#0b1628]" data-id="element-9952">
        <label className="text-[9px] text-[#8A9BBA] uppercase tracking-wide font-semibold block mb-1" data-id="element-9953">
          Model
        </label>
        <select className="w-full bg-[#132038] border border-[#2F4268] rounded px-2 py-1 text-[11px] text-[#F1F5FB] focus:outline-none focus:border-[#00D4FF] transition-colors" defaultValue="standard" data-id="element-9954">
          <option value="standard" data-id="element-9955">Standard Pivot</option>
          <option value="zimmatic" data-id="element-9956">Zimmatic 9500</option>
          <option value="valley" data-id="element-9957">Valley 8000</option>
          <option value="reinke" data-id="element-9958">Reinke E2100</option>
        </select>
      </div>

      {/* Catalog list */}
      <div className="flex-1 overflow-y-auto" data-id="element-9959">
        <div className="grid grid-cols-[1fr_56px_36px_20px] items-center gap-1.5 px-3 py-1 border-b border-[#2F4268]/60 bg-[#0a1322]/60 sticky top-0" data-id="element-9960">
          <span className="text-[9px] text-[#8A9BBA] uppercase tracking-wide font-semibold" data-id="element-9961">
            Diameter
          </span>
          <span className="text-[9px] text-[#8A9BBA] uppercase tracking-wide font-semibold text-right" data-id="element-9962">
            Length
          </span>
          <span className="text-[9px] text-[#8A9BBA] uppercase tracking-wide font-semibold text-center" data-id="element-9963">
            Qty
          </span>
          <span className="sr-only" data-id="element-9964">Add</span>
        </div>
        <ul className="divide-y divide-[#2F4268]/30" data-id="element-9965">
          {SPAN_CATALOG.map((item, idx) => {
          const qty = quantities[idx] ?? 1;
          return <li key={idx} className="grid grid-cols-[1fr_56px_36px_20px] items-center gap-1.5 px-3 py-1 hover:bg-white/[0.02] transition-colors" data-id="element-9966">
                <div className="flex items-center gap-1.5 min-w-0" data-id="element-9967">
                  <span className={`text-[9px] uppercase tracking-wide font-semibold flex-shrink-0 ${item.kind === 'elbow' ? 'text-[#FBBF24]' : item.kind === 'overhang' ? 'text-[#A78BFA]' : 'text-[#8A9BBA]'}`} data-id="element-9968">
                    {item.kind === 'elbow' ? 'EL' : item.kind === 'overhang' ? 'OH' : 'SP'}
                  </span>
                  <span className="text-[11px] text-[#F1F5FB] truncate" data-id="element-9969">
                    {item.kind === 'elbow' ? 'Elbow' : item.kind === 'overhang' ? `OH ${item.diameter}` : item.diameter}
                  </span>
                </div>
                <span className="text-[11px] text-[#C5D1E4] font-mono text-right" data-id="element-9970">
                  {item.length.toFixed(2)}
                </span>
                <input type="number" min={1} value={qty} onChange={e => setQty(idx, parseInt(e.target.value) || 0)} className="w-full bg-transparent border border-[#2F4268] rounded px-1 py-0 h-5 text-[10px] text-[#F1F5FB] font-mono text-center focus:outline-none focus:border-[#00D4FF] focus:bg-[#132038] transition-colors" data-id="element-9971" />
                <button onClick={() => handleAdd(idx, item)} className="w-5 h-5 flex items-center justify-center rounded text-[#00D4FF] hover:text-[#0b1628] hover:bg-[#00D4FF] transition-colors" title={`Add ${qty > 1 ? `${qty} × ` : ''}${item.kind === 'elbow' ? 'elbow' : item.diameter}`} data-id="element-9972">
                  <PlusIcon size={11} data-id="element-9973" />
                </button>
              </li>;
        })}
        </ul>
      </div>
    </aside>;
}
const DESIGN_PRESETS: {
  name: string;
  design: Partial<PivotDesign>;
}[] = [{
  name: 'Default',
  design: {
    fillColor: '#00D4FF',
    fillOpacity: 8,
    strokeColor: '#1E88E5',
    strokeWidth: 2,
    centerColor: '#1E88E5',
    armColor: '#1E88E5'
  }
}, {
  name: 'Crop Green',
  design: {
    fillColor: '#22C55E',
    fillOpacity: 15,
    strokeColor: '#15803D',
    strokeWidth: 2,
    centerColor: '#15803D',
    armColor: '#15803D'
  }
}, {
  name: 'Soil',
  design: {
    fillColor: '#D97706',
    fillOpacity: 12,
    strokeColor: '#7C2D12',
    strokeWidth: 2,
    centerColor: '#7C2D12',
    armColor: '#7C2D12'
  }
}, {
  name: 'Mono',
  design: {
    fillColor: '#94A3B8',
    fillOpacity: 10,
    strokeColor: '#1E293B',
    strokeWidth: 2,
    centerColor: '#1E293B',
    armColor: '#1E293B'
  }
}];
function PivotPropertiesPanel({
  pivot,
  onUpdate,
  onDelete,
  onClose,
  unitSystem,
  selectedSpanId,
  onSelectSpan,
  onAddSpan,
  onDeleteSpan,
  onReorderSpan,
  onRecalculateSpans,
  onUpdateSpansStyle,
  catalogOpen,
  onToggleCatalog,
  spanStyleEditorOpen,
  onOpenSpanStyleEditor,
  onTogglePivotLayer,
  panelMode,
  onPanelModeChange,
  radialProfileConfig,
  onRadialProfileConfigChange,
  profilePanelOpen,
  onProfilePanelOpenChange,
  profileType,
  onProfileTypeChange,
  towerPathTowerIndex,
  onTowerPathTowerIndexChange,
  profileSamplingDistance,
  onProfileSamplingDistanceChange,
  activeSlopeAnalysis,
  onActiveSlopeAnalysisChange,
  slopeTowerIndex,
  onSlopeTowerIndexChange,
  slopeChartPanelOpen,
  onSlopeChartPanelOpenChange,
  cropClearanceConfig,
  onCropClearanceConfigChange
}: {
  pivot: Pivot;
  onUpdate: (updates: Partial<Pivot>) => void;
  onDelete: () => void;
  onClose: () => void;
  unitSystem: UnitSystem;
  selectedSpanId: number | null;
  onSelectSpan: (id: number | null) => void;
  onAddSpan: (span: Omit<Span, 'id'>) => void;
  onDeleteSpan: (id: number) => void;
  onReorderSpan: (id: number, direction: 'up' | 'down') => void;
  onRecalculateSpans: () => void;
  onUpdateSpansStyle: (spanIds: number[], style: SpanStyle | null) => void;
  catalogOpen: boolean;
  onToggleCatalog: () => void;
  spanStyleEditorOpen: boolean;
  onOpenSpanStyleEditor: () => void;
  onTogglePivotLayer: (key: PivotSubLayerKey) => void;
  panelMode: PivotPanelMode;
  onPanelModeChange: (m: PivotPanelMode) => void;
  radialProfileConfig: {
    angleStep: number;
    selectedIndex: number;
  };
  onRadialProfileConfigChange: (c: {
    angleStep: number;
    selectedIndex: number;
  }) => void;
  profilePanelOpen: boolean;
  onProfilePanelOpenChange: (open: boolean) => void;
  profileType: ProfileType;
  onProfileTypeChange: (t: ProfileType) => void;
  towerPathTowerIndex: number;
  onTowerPathTowerIndexChange: (i: number) => void;
  profileSamplingDistance: number;
  onProfileSamplingDistanceChange: (m: number) => void;
  activeSlopeAnalysis: SlopeAnalysisType | null;
  onActiveSlopeAnalysisChange: (a: SlopeAnalysisType | null) => void;
  slopeTowerIndex: number;
  onSlopeTowerIndexChange: (i: number) => void;
  slopeChartPanelOpen: boolean;
  onSlopeChartPanelOpenChange: (open: boolean) => void;
  cropClearanceConfig: {
    structHeightM: number;
    warnAtM: number;
    cropHeightM: number;
    cropName: string;
  };
  onCropClearanceConfigChange: (c: {
    structHeightM: number;
    warnAtM: number;
    cropHeightM: number;
    cropName: string;
  }) => void;
}) {
  const mode = panelMode;
  const setMode = onPanelModeChange;
  const [technicalTab, setTechnicalTab] = useState<'general' | 'spans' | 'endguns' | 'pivot-point'>('general');
  const [sourceMenuOpen, setSourceMenuOpen] = useState(false);
  const sourceMenuRef = useRef<HTMLDivElement>(null);
  // Profiles state — profileType lifted to ICADPage so canvas + panel react to it.
  const setProfileType = onProfileTypeChange;
  const radialAngleStep = radialProfileConfig.angleStep;
  const radialSelectedIndex = radialProfileConfig.selectedIndex;
  const setRadialAngleStep = (v: number) => onRadialProfileConfigChange({
    angleStep: v,
    selectedIndex: 0
  });
  const setRadialSelectedIndex = (v: number) => onRadialProfileConfigChange({
    ...radialProfileConfig,
    selectedIndex: v
  });
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (sourceMenuRef.current && !sourceMenuRef.current.contains(e.target as Node)) {
        setSourceMenuOpen(false);
      }
    }
    if (sourceMenuOpen) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [sourceMenuOpen]);
  const radiusM = pivot.radiusPx;
  const areaHa = Math.PI * radiusM * radiusM / 10000;
  const areaDisplay = unitSystem === 'metric' ? `${areaHa.toFixed(2)} ha` : `${(areaHa * 2.4711).toFixed(2)} ac`;
  const updateDesign = (updates: Partial<PivotDesign>) => onUpdate({
    design: {
      ...pivot.design,
      ...updates
    }
  });
  const updateTopo = (updates: Partial<PivotTopography>) => onUpdate({
    topography: {
      ...pivot.topography,
      ...updates
    }
  });
  return <aside className="w-[360px] bg-[#0b1628] border-l border-[#2F4268] flex flex-row flex-shrink-0" data-id="element-9974">
      {/* Vertical mode rail */}
      <div className="w-9 bg-[#0a1322] border-r border-[#2F4268] flex flex-col items-center py-2 flex-shrink-0" data-id="element-9975">
        <ModeRailButton active={mode === 'technical'} onClick={() => setMode('technical')} icon={<WrenchIcon size={14} data-id="element-9977" />} label="Technical" data-id="element-9976" />
        <ModeRailButton active={mode === 'design'} onClick={() => setMode('design')} icon={<PaletteIcon size={14} data-id="element-9979" />} label="Style" data-id="element-9978" />
        <ModeRailButton active={mode === 'topography'} onClick={() => setMode('topography')} icon={<MountainIcon size={14} data-id="element-9981" />} label="Topography" data-id="element-9980" />
        <ModeRailButton active={mode === 'contours'} onClick={() => setMode('contours')} icon={<LayersIcon size={14} data-id="element-9983" />} label="Contour lines" data-id="element-9982" />
        <ModeRailButton active={mode === 'profiles'} onClick={() => setMode('profiles')} icon={<LineChartIcon size={14} data-id="element-9985" />} label="Profiles" data-id="element-9984" />
        <ModeRailButton active={mode === 'slope-analysis'} onClick={() => setMode('slope-analysis')} icon={<AlertTriangleIcon size={14} data-id="element-9987" />} label="Slope Analysis" data-id="element-9986" />
      </div>

      <div className="flex-1 flex flex-col min-w-0" data-id="element-9988">
        {/* Header */}
        <div className="flex items-center justify-between px-3 h-[42px] border-b border-[#2F4268] flex-shrink-0" data-id="element-9989">
          <div className="flex items-center gap-2 min-w-0" data-id="element-9990">
            <img src="https://cdn.magicpatterns.com/uploads/t426MnDWi6vKjFroEFiiEG/pivot_full_circle.svg" alt="Full Circle" width={20} height={20} className="filter brightness-0 invert opacity-90 flex-shrink-0" draggable={false} data-id="element-9991" />
            <div className="flex flex-col min-w-0" data-id="element-9992">
              <span className="text-[#F1F5FB] text-xs font-semibold truncate leading-tight" data-id="element-9993">
                {pivot.name}
              </span>
              <span className="text-[9px] text-[#8A9BBA] uppercase tracking-wide leading-tight" data-id="element-9994">
                {mode === 'technical' ? 'Technical Properties' : mode === 'design' ? 'Style Properties' : mode === 'topography' ? 'Slope Analysis' : mode === 'contours' ? 'Topography' : 'Elevation Profiles'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-0.5 flex-shrink-0" data-id="element-9995">
            <button onClick={onDelete} className="text-[#8A9BBA] hover:text-[#F87171] hover:bg-[#F87171]/10 p-1 rounded transition-colors" title="Delete pivot" data-id="element-9996">
              <TrashIcon size={13} data-id="element-9997" />
            </button>
            <button onClick={onClose} className="text-[#8A9BBA] hover:text-[#F1F5FB] hover:bg-white/5 p-1 rounded transition-colors" title="Close panel" data-id="element-9998">
              <XIcon size={13} data-id="element-9999" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto" data-id="element-10000">
          {mode === 'technical' && technicalTab === 'general' && <>
              <CollapsibleSection label="Identification" data-id="element-10001">
                <PropRow label="Name" data-id="element-10002">
                  <input type="text" value={pivot.name} onChange={e => onUpdate({
                name: e.target.value
              })} className="w-full bg-[#132038] border border-[#2F4268] rounded px-2 py-0.5 text-[11px] text-[#F1F5FB] focus:outline-none focus:border-[#00D4FF] transition-colors" data-id="element-10003" />
                </PropRow>
                <PropRow label="Type" readonly data-id="element-10004">
                  <span className="text-[11px] text-[#C5D1E4]" data-id="element-10005">
                    Full Circle
                  </span>
                </PropRow>
              </CollapsibleSection>

              <CollapsibleSection label="Geometry" data-id="element-10006">
                <PropRow label="Radius" data-id="element-10007">
                  <InputWithUnit type="number" value={pivot.radiusPx} unit={unitSystem === 'metric' ? 'm' : 'ft'} onChange={v => onUpdate({
                radiusPx: Math.max(20, v || 0)
              })} data-id="element-10008" />
                </PropRow>
                <PropRow label="Diameter" readonly data-id="element-10009">
                  <span className="text-[11px] text-[#C5D1E4] font-mono" data-id="element-10010">
                    {radiusM * 2} m
                  </span>
                </PropRow>
                <PropRow label="Irrigated Area" readonly data-id="element-10011">
                  <span className="text-[11px] text-[#C5D1E4] font-mono" data-id="element-10012">
                    {areaDisplay}
                  </span>
                </PropRow>
                <PropRow label="Center (X, Y)" readonly data-id="element-10013">
                  <span className="text-[11px] text-[#C5D1E4] font-mono" data-id="element-10014">
                    {pivot.cxPct.toFixed(1)}, {pivot.cyPct.toFixed(1)}
                  </span>
                </PropRow>
              </CollapsibleSection>

              <CollapsibleSection label="Configuration" data-id="element-10015">
                <PropRow label="Number of Towers" data-id="element-10016">
                  <InputWithUnit type="number" value={pivot.towers} unit="" onChange={v => onUpdate({
                towers: Math.max(1, v || 1)
              })} data-id="element-10017" />
                </PropRow>
              </CollapsibleSection>

              <CollapsibleSection label="Hydraulics" data-id="element-10018">
                <PropRow label="Flow Rate" data-id="element-10019">
                  <InputWithUnit type="number" value={pivot.flowRate} unit="gpm" onChange={v => onUpdate({
                flowRate: v || 0
              })} data-id="element-10020" />
                </PropRow>
                <PropRow label="Operating Pressure" data-id="element-10021">
                  <InputWithUnit type="number" value={pivot.pressure} unit="psi" onChange={v => onUpdate({
                pressure: v || 0
              })} data-id="element-10022" />
                </PropRow>
              </CollapsibleSection>

              <CollapsibleSection label="Status" data-id="element-10023">
                <PropRow label="Active" data-id="element-10024">
                  <InlineToggle value={pivot.active} onChange={v => onUpdate({
                active: v
              })} data-id="element-10025" />
                </PropRow>
              </CollapsibleSection>
            </>}

          {mode === 'technical' && technicalTab === 'spans' && <div className="flex flex-col" data-id="element-10026">
              <div className="flex items-center gap-1 px-3 py-2 border-b border-[#2F4268] bg-[#0a1322] sticky top-0 z-10" data-id="element-10027">
                <button onClick={onRecalculateSpans} className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5 transition-colors" title="Recalculate spans" data-id="element-10028">
                  <RefreshCwIcon size={11} data-id="element-10029" />
                  <span data-id="element-10030">Recalculate</span>
                </button>
                <button onClick={onToggleCatalog} className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-colors ${catalogOpen ? 'text-[#00D4FF] bg-[#1C2B47]' : 'text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5'}`} title="Open spans catalog" data-id="element-10031">
                  <BookOpenIcon size={11} data-id="element-10032" />
                  <span data-id="element-10033">Catalog</span>
                </button>
                <button onClick={() => onAddSpan({
              kind: 'span',
              diameter: '6"5/8',
              length: 62.03
            })} className="ml-auto flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium text-[#0b1628] bg-[#00D4FF] hover:bg-[#7AE5FF] transition-colors" title="Add a default span" data-id="element-10034">
                  <PlusIcon size={11} data-id="element-10035" />
                  <span data-id="element-10036">Add</span>
                </button>
              </div>

              <div className="grid grid-cols-[28px_1fr_70px_56px] items-center gap-1 px-3 py-1.5 border-b border-[#2F4268]/60 bg-[#0a1322]/60" data-id="element-10037">
                <span className="text-[9px] text-[#8A9BBA] uppercase tracking-wide font-semibold" data-id="element-10038">
                  #
                </span>
                <span className="text-[9px] text-[#8A9BBA] uppercase tracking-wide font-semibold" data-id="element-10039">
                  Diameter
                </span>
                <span className="text-[9px] text-[#8A9BBA] uppercase tracking-wide font-semibold text-right" data-id="element-10040">
                  Length
                </span>
                <span className="text-[9px] text-[#8A9BBA] uppercase tracking-wide font-semibold text-right" data-id="element-10041">
                  Actions
                </span>
              </div>

              {pivot.spans.length === 0 ? <div className="px-3 py-6 text-center" data-id="element-10042">
                  <p className="text-[11px] text-[#8A9BBA] italic" data-id="element-10043">
                    No spans configured.
                  </p>
                  <button onClick={onToggleCatalog} className="mt-2 text-[10px] text-[#00D4FF] hover:underline" data-id="element-10044">
                    Open the catalog to add spans
                  </button>
                </div> : <ul className="divide-y divide-[#2F4268]/40" data-id="element-10045">
                  {pivot.spans.map((span, idx) => {
              const isSelected = selectedSpanId === span.id;
              const isFirst = idx === 0;
              const isLast = idx === pivot.spans.length - 1;
              return <li key={span.id} onClick={() => onSelectSpan(isSelected ? null : span.id)} className={`grid grid-cols-[28px_1fr_70px_56px] items-center gap-1 px-3 py-1.5 cursor-pointer transition-colors ${isSelected ? 'bg-[#1C2B47]' : 'hover:bg-white/[0.03]'}`} data-id="element-10046">
                        <span className={`inline-flex items-center justify-center w-5 h-5 rounded text-[9px] font-mono font-semibold ${isSelected ? 'bg-[#00D4FF] text-[#0b1628]' : 'bg-[#132038] text-[#C5D1E4] border border-[#2F4268]'}`} data-id="element-10047">
                          {idx + 1}
                        </span>
                        <div className="flex items-center gap-1.5 min-w-0" data-id="element-10048">
                          <span className="text-[11px] text-[#F1F5FB] truncate" data-id="element-10049">
                            {span.kind === 'elbow' ? 'Elbow' : `Span ${span.diameter}`}
                          </span>
                        </div>
                        <span className="text-[11px] text-[#C5D1E4] font-mono text-right" data-id="element-10050">
                          {span.length.toFixed(2)} m
                        </span>
                        <div className="flex items-center justify-end gap-0.5" onClick={e => e.stopPropagation()} data-id="element-10051">
                          <button onClick={() => onReorderSpan(span.id, 'up')} disabled={isFirst} className="p-0.5 rounded text-[#8A9BBA] hover:text-[#F1F5FB] hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Move up" data-id="element-10052">
                            <ArrowUpIcon size={11} data-id="element-10053" />
                          </button>
                          <button onClick={() => onReorderSpan(span.id, 'down')} disabled={isLast} className="p-0.5 rounded text-[#8A9BBA] hover:text-[#F1F5FB] hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Move down" data-id="element-10054">
                            <ArrowDownIcon size={11} data-id="element-10055" />
                          </button>
                          <button onClick={() => onDeleteSpan(span.id)} className="p-0.5 rounded text-[#8A9BBA] hover:text-[#F87171] hover:bg-[#F87171]/10 transition-colors" title="Delete span" data-id="element-10056">
                            <TrashIcon size={11} data-id="element-10057" />
                          </button>
                        </div>
                      </li>;
            })}
                </ul>}

              {pivot.spans.length > 0 && <div className="border-t border-[#2F4268] px-3 py-2 bg-[#0a1322]/60" data-id="element-10058">
                  <div className="flex items-center justify-between" data-id="element-10059">
                    <span className="text-[10px] text-[#8A9BBA] uppercase tracking-wide font-semibold" data-id="element-10060">
                      Total span length
                    </span>
                    <span className="text-[12px] text-[#00D4FF] font-mono font-semibold" data-id="element-10061">
                      {pivot.spans.reduce((sum, s) => sum + s.length, 0).toFixed(2)}{' '}
                      m
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-1" data-id="element-10062">
                    <span className="text-[10px] text-[#8A9BBA]" data-id="element-10063">
                      {pivot.spans.length} segment
                      {pivot.spans.length === 1 ? '' : 's'}
                    </span>
                  </div>
                </div>}
            </div>}

          {mode === 'technical' && technicalTab === 'endguns' && <div className="px-3 py-4" data-id="element-10064">
              <div className="text-[10px] text-[#8A9BBA] uppercase tracking-wide mb-2 font-semibold" data-id="element-10065">
                End Guns
              </div>
              <p className="text-[11px] text-[#C5D1E4] leading-relaxed" data-id="element-10066">
                Configure end gun coverage, throw distance, and on/off arc.
              </p>
              <div className="mt-3 text-[11px] text-[#8A9BBA] italic" data-id="element-10067">
                No end guns configured yet.
              </div>
            </div>}

          {mode === 'technical' && technicalTab === 'pivot-point' && <div className="px-3 py-4" data-id="element-10068">
              <div className="text-[10px] text-[#8A9BBA] uppercase tracking-wide mb-2 font-semibold" data-id="element-10069">
                Pivot Point
              </div>
              <p className="text-[11px] text-[#C5D1E4] leading-relaxed" data-id="element-10070">
                Configure pivot point hardware — control panel, power supply,
                water inlet, and tower base.
              </p>
              <div className="mt-3 text-[11px] text-[#8A9BBA] italic" data-id="element-10071">
                Default configuration applied.
              </div>
            </div>}

          {mode === 'design' && <>
              <CollapsibleSection label="Outline" data-id="element-10072">
                <PropRow label="Stroke Color" data-id="element-10073">
                  <InlineColor value={pivot.design.strokeColor} onChange={v => updateDesign({
                strokeColor: v
              })} data-id="element-10074" />
                </PropRow>
                <PropRow label="Stroke Width" data-id="element-10075">
                  <InlineSlider value={pivot.design.strokeWidth} min={1} max={5} step={0.5} unit="px" onChange={v => updateDesign({
                strokeWidth: v
              })} data-id="element-10076" />
                </PropRow>
                <PropRow label="Fill Color" data-id="element-10077">
                  <InlineColor value={pivot.design.fillColor} onChange={v => updateDesign({
                fillColor: v
              })} data-id="element-10078" />
                </PropRow>
                <PropRow label="Fill Opacity" data-id="element-10079">
                  <InlineSlider value={pivot.design.fillOpacity} min={0} max={100} step={1} unit="%" onChange={v => updateDesign({
                fillOpacity: v
              })} data-id="element-10080" />
                </PropRow>
              </CollapsibleSection>

              <CollapsibleSection label="Components" data-id="element-10081">
                <PropRow label="Center Point" data-id="element-10082">
                  <InlineColor value={pivot.design.centerColor} onChange={v => updateDesign({
                centerColor: v
              })} data-id="element-10083" />
                </PropRow>
                <PropRow label="Lateral Arm" data-id="element-10084">
                  <InlineColor value={pivot.design.armColor} onChange={v => updateDesign({
                armColor: v
              })} data-id="element-10085" />
                </PropRow>
              </CollapsibleSection>

              <CollapsibleSection label="Label" data-id="element-10086">
                <PropRow label="Text Color" data-id="element-10087">
                  <InlineColor value={pivot.design.labelText} onChange={v => updateDesign({
                labelText: v
              })} data-id="element-10088" />
                </PropRow>
                <PropRow label="Background" data-id="element-10089">
                  <InlineColor value={pivot.design.labelBg} onChange={v => updateDesign({
                labelBg: v
              })} data-id="element-10090" />
                </PropRow>
                <PropRow label="Show Background" data-id="element-10091">
                  <InlineToggle value={pivot.design.showLabelBg} onChange={v => updateDesign({
                showLabelBg: v
              })} data-id="element-10092" />
                </PropRow>
              </CollapsibleSection>

              <CollapsibleSection label="Presets" defaultOpen={false} data-id="element-10093">
                <div className="px-3 py-2 flex flex-wrap gap-1.5" data-id="element-10094">
                  {DESIGN_PRESETS.map(preset => <button key={preset.name} onClick={() => updateDesign(preset.design)} className="flex items-center gap-1.5 px-2 py-1 rounded border border-[#2F4268] hover:border-[#00D4FF] bg-[#132038] hover:bg-[#1C2B47] transition-colors" title={preset.name} data-id="element-10095">
                      <span className="w-2.5 h-2.5 rounded-full border" style={{
                  backgroundColor: hexToRgba(preset.design.fillColor!, 0.3),
                  borderColor: preset.design.strokeColor
                }} data-id="element-10096" />
                      <span className="text-[10px] text-[#C5D1E4]" data-id="element-10097">
                        {preset.name}
                      </span>
                    </button>)}
                </div>
              </CollapsibleSection>

              <CollapsibleSection label="Spans" defaultOpen={true} data-id="element-10098">
                <div className="px-3 pt-1 pb-1" data-id="element-10099"></div>
                <PropRow label="Line Color" data-id="element-10100">
                  <InlineColor value={pivot.design.spanDefaults.color} onChange={v => updateDesign({
                spanDefaults: {
                  ...pivot.design.spanDefaults,
                  color: v
                }
              })} data-id="element-10101" />
                </PropRow>
                <PropRow label="Line Type" data-id="element-10102">
                  <div className="flex gap-1" data-id="element-10103">
                    {(['solid', 'dashed', 'dotted'] as const).map(lt => {
                  const isActive = pivot.design.spanDefaults.lineStyle === lt;
                  return <button key={lt} onClick={() => updateDesign({
                    spanDefaults: {
                      ...pivot.design.spanDefaults,
                      lineStyle: lt
                    }
                  })} className={`flex-1 px-1.5 py-1 rounded border transition-colors flex items-center justify-center ${isActive ? 'border-[#00D4FF] bg-[#1C2B47]' : 'border-[#2F4268] hover:border-[#4A5F8C] bg-[#132038]'}`} title={lt} data-id="element-10104">
                          <svg width="28" height="6" viewBox="0 0 28 6" className="overflow-visible" data-id="element-10105">
                            <line x1="1" y1="3" x2="27" y2="3" stroke={isActive ? '#00D4FF' : '#C5D1E4'} strokeWidth="1.4" strokeDasharray={lt === 'dashed' ? '4 2' : lt === 'dotted' ? '1 2' : undefined} strokeLinecap="round" data-id="element-10106" />
                          </svg>
                        </button>;
                })}
                  </div>
                </PropRow>
                <PropRow label="Fill Color" data-id="element-10107">
                  <div className="flex items-center gap-1.5" data-id="element-10108">
                    {pivot.design.spanDefaults.fillColor === null ? <>
                        <button onClick={() => updateDesign({
                    spanDefaults: {
                      ...pivot.design.spanDefaults,
                      fillColor: '#FBBF24'
                    }
                  })} className="flex-1 px-2 py-0.5 rounded text-[10px] font-medium text-[#C5D1E4] hover:text-[#F1F5FB] border border-[#2F4268] hover:border-[#4A5F8C] transition-colors" data-id="element-10109">
                          Enable fill
                        </button>
                      </> : <>
                        <InlineColor value={pivot.design.spanDefaults.fillColor} onChange={v => updateDesign({
                    spanDefaults: {
                      ...pivot.design.spanDefaults,
                      fillColor: v
                    }
                  })} data-id="element-10110" />
                        <button onClick={() => updateDesign({
                    spanDefaults: {
                      ...pivot.design.spanDefaults,
                      fillColor: null
                    }
                  })} className="p-1 rounded text-[#8A9BBA] hover:text-[#F87171] hover:bg-[#F87171]/10 transition-colors flex-shrink-0" title="Disable fill" data-id="element-10111">
                          <XIcon size={12} data-id="element-10112" />
                        </button>
                      </>}
                  </div>
                </PropRow>

                <div className="px-3 py-2 border-t border-[#2F4268]/40 mt-1" data-id="element-10113">
                  <button onClick={onOpenSpanStyleEditor} disabled={pivot.spans.length === 0} className={`w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-[10px] font-medium transition-colors ${spanStyleEditorOpen ? 'text-[#00D4FF] bg-[#1C2B47] border border-[#00D4FF]/40' : 'text-[#C5D1E4] hover:text-[#F1F5FB] border border-[#2F4268] hover:border-[#4A5F8C] bg-[#132038]'} disabled:opacity-40 disabled:cursor-not-allowed`} data-id="element-10114">
                    <PaletteIcon size={11} data-id="element-10115" />
                    <span data-id="element-10116">
                      Customize per span
                      {pivot.spans.some(s => s.style) && <span className="ml-1 text-[#FBBF24]" data-id="element-10117">
                          · {pivot.spans.filter(s => s.style).length} override
                          {pivot.spans.filter(s => s.style).length === 1 ? '' : 's'}
                        </span>}
                    </span>
                  </button>
                  {pivot.spans.some(s => s.style) && <button onClick={() => onUpdateSpansStyle(pivot.spans.filter(s => s.style).map(s => s.id), null)} className="w-full mt-1 px-2 py-1 rounded text-[10px] font-medium text-[#8A9BBA] hover:text-[#F87171] transition-colors" data-id="element-10118">
                      Clear all per-span overrides
                    </button>}
                </div>
              </CollapsibleSection>

              <CollapsibleSection label="Layer Visibility" data-id="element-10119">
                <div className="px-3 pt-1 pb-1" data-id="element-10120">
                  <p className="text-[9px] text-[#8A9BBA] leading-relaxed" data-id="element-10121">
                    Toggle which parts of this pivot are drawn on the canvas.
                  </p>
                </div>
                <div className="px-1 pt-0.5 pb-1" data-id="element-10122">
                  {[{
                key: 'outline' as PivotSubLayerKey,
                label: 'Outline',
                icon: <CircleIcon size={11} data-id="element-10123" />
              }, {
                key: 'center' as PivotSubLayerKey,
                label: 'Center',
                icon: <DotIcon size={11} data-id="element-10124" />
              }, {
                key: 'arm' as PivotSubLayerKey,
                label: 'Lateral arm',
                icon: <MinusIcon size={11} data-id="element-10125" />
              }, {
                key: 'label' as PivotSubLayerKey,
                label: 'Label',
                icon: <TagIcon size={11} data-id="element-10126" />
              }, {
                key: 'towers' as PivotSubLayerKey,
                label: 'Towers',
                icon: <ZapIcon size={11} data-id="element-10127" />
              }, {
                key: 'contours' as PivotSubLayerKey,
                label: 'Span rings',
                icon: <HexagonIcon size={11} data-id="element-10128" />
              }].map(row => {
                const visible = pivot.layers[row.key];
                return <div key={row.key} className="flex items-center gap-2 px-2 py-1 hover:bg-white/[0.02] rounded transition-colors" data-id="element-10129">
                        <button onClick={() => onTogglePivotLayer(row.key)} className="text-[#8A9BBA] hover:text-[#F1F5FB] p-0.5 rounded transition-colors flex-shrink-0" title={visible ? `Hide ${row.label}` : `Show ${row.label}`} data-id="element-10130">
                          {visible ? <EyeIcon size={12} data-id="element-10131" /> : <EyeOffIcon size={12} data-id="element-10132" />}
                        </button>
                        <span className="text-[#8A9BBA] flex-shrink-0" data-id="element-10133">
                          {row.icon}
                        </span>
                        <span className={`text-[10px] flex-1 truncate ${visible ? 'text-[#C5D1E4]' : 'text-[#8A9BBA] line-through decoration-[#4A5F8C]'}`} data-id="element-10134">
                          {row.label}
                        </span>
                        <InlineToggle value={visible} onChange={() => onTogglePivotLayer(row.key)} data-id="element-10135" />
                      </div>;
              })}
                </div>
              </CollapsibleSection>
            </>}

          {mode === 'topography' && <>
              <CollapsibleSection label="Visualization" data-id="element-10136">
                <PropRow label="Slope Analysis" data-id="element-10137">
                  <InlineToggle value={pivot.topography.showSlope} onChange={v => updateTopo({
                showSlope: v
              })} data-id="element-10138" />
                </PropRow>
                <PropRow label="Elevation Markers" data-id="element-10139">
                  <InlineToggle value={pivot.topography.showElevation} onChange={v => updateTopo({
                showElevation: v
              })} data-id="element-10140" />
                </PropRow>
                <PropRow label="Flow Direction" data-id="element-10141">
                  <InlineToggle value={pivot.topography.showFlowDirection} onChange={v => updateTopo({
                showFlowDirection: v
              })} data-id="element-10142" />
                </PropRow>
              </CollapsibleSection>

              <CollapsibleSection label="Slope Color Ramp" data-id="element-10143">
                <div className="px-3 py-2 grid grid-cols-3 gap-1.5" data-id="element-10144">
                  {(['green-red', 'blue-yellow', 'mono'] as const).map(ramp => {
                const isActive = pivot.topography.slopeColorRamp === ramp;
                const gradient = {
                  'green-red': 'linear-gradient(90deg,#22c55e,#eab308,#ef4444)',
                  'blue-yellow': 'linear-gradient(90deg,#3b82f6,#facc15)',
                  mono: 'linear-gradient(90deg,#94a3b8,#1e293b)'
                }[ramp];
                return <button key={ramp} onClick={() => updateTopo({
                  slopeColorRamp: ramp
                })} className={`p-1 rounded border transition-colors ${isActive ? 'border-[#00D4FF] bg-[#1C2B47]' : 'border-[#2F4268] hover:border-[#4A5F8C] bg-[#132038]'}`} data-id="element-10145">
                          <div className="h-3.5 w-full rounded" style={{
                    background: gradient
                  }} data-id="element-10146" />
                          <span className="text-[9px] text-[#C5D1E4] mt-0.5 block capitalize" data-id="element-10147">
                            {ramp.replace('-', ' / ')}
                          </span>
                        </button>;
              })}
                </div>
              </CollapsibleSection>

              <CollapsibleSection label="Elevation Range" data-id="element-10148">
                <PropRow label="Min Elevation" data-id="element-10149">
                  <InputWithUnit type="number" value={pivot.topography.minElevation} unit="m" onChange={v => updateTopo({
                minElevation: v || 0
              })} data-id="element-10150" />
                </PropRow>
                <PropRow label="Max Elevation" data-id="element-10151">
                  <InputWithUnit type="number" value={pivot.topography.maxElevation} unit="m" onChange={v => updateTopo({
                maxElevation: v || 0
              })} data-id="element-10152" />
                </PropRow>
                <PropRow label="Elevation Δ" readonly data-id="element-10153">
                  <span className="text-[11px] text-[#C5D1E4] font-mono" data-id="element-10154">
                    {pivot.topography.maxElevation - pivot.topography.minElevation}{' '}
                    m
                  </span>
                </PropRow>
              </CollapsibleSection>

              <CollapsibleSection label="Slope Statistics" data-id="element-10155">
                <PropRow label="Average Slope" readonly data-id="element-10156">
                  <span className="text-[11px] text-[#C5D1E4] font-mono" data-id="element-10157">
                    {pivot.topography.avgSlope.toFixed(1)}%
                  </span>
                </PropRow>
                <PropRow label="Max Slope" readonly data-id="element-10158">
                  <span className="text-[11px] text-[#C5D1E4] font-mono" data-id="element-10159">
                    6.8%
                  </span>
                </PropRow>
                <PropRow label="Min Slope" readonly data-id="element-10160">
                  <span className="text-[11px] text-[#C5D1E4] font-mono" data-id="element-10161">
                    0.3%
                  </span>
                </PropRow>
                <PropRow label="Dominant Aspect" readonly data-id="element-10162">
                  <span className="text-[11px] text-[#C5D1E4] font-mono" data-id="element-10163">
                    SW
                  </span>
                </PropRow>
                <div className="px-3 py-2" data-id="element-10164">
                  <button className="w-full px-3 py-1.5 rounded text-[11px] font-medium text-[#0b1628] bg-[#00D4FF] hover:bg-[#7AE5FF] transition-colors" onClick={() => updateTopo({
                showSlope: true
              })} data-id="element-10165">
                    Run Slope Analysis
                  </button>
                </div>
              </CollapsibleSection>
            </>}

          {mode === 'contours' && (() => {
          const SOURCES: {
            id: TopographySource;
            label: string;
            sub: string;
            tier: 'free' | 'imported' | 'existing' | 'premium';
            precision: ContourPrecision;
            badge: string;
            icon: React.ReactNode;
          }[] = [{
            id: 'imported',
            label: 'Imported survey',
            sub: 'Surveyor file (.dwg, .csv, .xyz)',
            tier: 'imported',
            precision: '1m',
            badge: 'Best',
            icon: <CompassIcon size={12} data-id="element-10166" />
          }, {
            id: 'existing',
            label: 'Existing in drawing',
            sub: 'Reuse topography already in this project',
            tier: 'existing',
            precision: '5m',
            badge: 'In project',
            icon: <LayersIcon size={12} data-id="element-10167" />
          }, {
            id: 'free-30m',
            label: 'Free · SRTM 30 m',
            sub: 'Open-data global elevation',
            tier: 'free',
            precision: '30m',
            badge: 'Free',
            icon: <GlobeIcon size={12} data-id="element-10168" />
          }, {
            id: 'free-10m',
            label: 'Free · Regional 10 m',
            sub: 'Where available — open regional DEM',
            tier: 'free',
            precision: '10m',
            badge: 'Free',
            icon: <GlobeIcon size={12} data-id="element-10169" />
          }, {
            id: 'premium-5m',
            label: 'Premium · High-res 5 m',
            sub: 'Commercial DEM for this region',
            tier: 'premium',
            precision: '5m',
            badge: 'Premium',
            icon: <MountainIcon size={12} data-id="element-10170" />
          }, {
            id: 'premium-1m',
            label: 'Premium · LiDAR 1 m',
            sub: 'Aerial LiDAR survey, highest detail',
            tier: 'premium',
            precision: '1m',
            badge: 'Premium',
            icon: <MountainIcon size={12} data-id="element-10171" />
          }];
          const activeSource = SOURCES.find(s => s.id === pivot.topography.source) || SOURCES[2];
          const contourCount = Math.max(1, Math.floor((pivot.topography.maxElevation - pivot.topography.minElevation) / Math.max(0.1, pivot.topography.contourEquidistance)));
          return <>
                  {/* 1. Topography Source */}
                  <CollapsibleSection label="Topography Source" data-id="element-10172">
                    {pivot.topography.source === 'none' ? <>
                        <div className="px-3 pt-1 pb-1" data-id="element-10173">
                          <p className="text-[9px] text-[#8A9BBA] leading-relaxed" data-id="element-10174">
                            No topography assigned to this pivot yet. Pick a
                            source below to unlock contour lines and elevation
                            analysis.
                          </p>
                        </div>
                        {/* None option — selected by default */}
                        <div className="px-3 pt-1" data-id="element-10175">
                          <button onClick={() => updateTopo({
                    source: 'none'
                  })} className="w-full flex items-center gap-2 px-2 py-1.5 rounded border border-[#8A9BBA] bg-[#1C2B47] text-left" data-id="element-10176">
                            <div className="w-3 h-3 rounded-full border-2 border-[#8A9BBA] bg-[#8A9BBA] flex-shrink-0" data-id="element-10177" />
                            <div className="text-[#8A9BBA] flex-shrink-0" data-id="element-10178">
                              <XIcon size={12} data-id="element-10179" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-10180">
                              <div className="flex items-center gap-1.5 flex-wrap" data-id="element-10181">
                                <span className="text-[11px] font-semibold text-[#F1F5FB]" data-id="element-10182">
                                  No topography
                                </span>
                                <span className="inline-flex items-center px-1 py-[1px] rounded text-[8px] font-bold uppercase tracking-wide bg-[#2F4268] text-[#C5D1E4]" data-id="element-10183">
                                  Default
                                </span>
                              </div>
                              <span className="text-[9px] text-[#8A9BBA] leading-tight block mt-0.5" data-id="element-10184">
                                Skip topography for this pivot
                              </span>
                            </div>
                          </button>
                        </div>
                        <div className="px-3 pt-2 pb-1" data-id="element-10185">
                          <div className="flex items-center gap-2" data-id="element-10186">
                            <div className="flex-1 h-px bg-[#2F4268]/60" data-id="element-10187" />
                            <span className="text-[8px] text-[#8A9BBA] uppercase tracking-wider font-semibold" data-id="element-10188">
                              Or pick a source
                            </span>
                            <div className="flex-1 h-px bg-[#2F4268]/60" data-id="element-10189" />
                          </div>
                        </div>
                        <div className="px-3 pt-1 pb-2 space-y-1" data-id="element-10190">
                          {SOURCES.map(src => {
                    const tierStyle = src.tier === 'premium' ? 'bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] text-[#0b1628]' : src.tier === 'imported' ? 'bg-[#8B5CF6]/20 text-[#A78BFA] border border-[#8B5CF6]/40' : src.tier === 'existing' ? 'bg-[#00D4FF]/15 text-[#00D4FF] border border-[#00D4FF]/40' : 'bg-[#10B981]/20 text-[#34D399] border border-[#10B981]/40';
                    return <button key={src.id} onClick={() => updateTopo({
                      source: src.id,
                      contourPrecision: src.precision
                    })} className="w-full flex items-center gap-2 px-2 py-1.5 rounded border border-[#2F4268] hover:border-[#4A5F8C] bg-[#132038] transition-colors text-left" data-id="element-10191">
                                <div className="w-3 h-3 rounded-full border-2 border-[#4A5F8C] flex-shrink-0" data-id="element-10192" />
                                <div className="text-[#8A9BBA] flex-shrink-0" data-id="element-10193">
                                  {src.icon}
                                </div>
                                <div className="flex-1 min-w-0" data-id="element-10194">
                                  <div className="flex items-center gap-1.5 flex-wrap" data-id="element-10195">
                                    <span className="text-[11px] font-semibold text-[#C5D1E4]" data-id="element-10196">
                                      {src.label}
                                    </span>
                                    <span className={`inline-flex items-center px-1 py-[1px] rounded text-[8px] font-bold uppercase tracking-wide ${tierStyle}`} data-id="element-10197">
                                      {src.badge}
                                    </span>
                                  </div>
                                  <span className="text-[9px] text-[#8A9BBA] leading-tight block mt-0.5" data-id="element-10198">
                                    {src.sub}
                                  </span>
                                </div>
                              </button>;
                  })}
                        </div>
                      </> : <>
                        {/* Selected-source card with dropdown */}
                        <div className="px-3 pt-2 pb-2" ref={sourceMenuRef} data-id="element-10199">
                          <div className="relative" data-id="element-10200">
                            <button onClick={() => setSourceMenuOpen(v => !v)} className={`w-full flex items-center gap-2 px-2 py-1.5 rounded border bg-[#1C2B47] text-left transition-colors ${sourceMenuOpen ? 'border-[#00D4FF]' : 'border-[#00D4FF]/60 hover:border-[#00D4FF]'}`} data-id="element-10201">
                              <div className="text-[#00D4FF] flex-shrink-0" data-id="element-10202">
                                {activeSource.icon}
                              </div>
                              <div className="flex-1 min-w-0" data-id="element-10203">
                                <div className="flex items-center gap-1.5 flex-wrap" data-id="element-10204">
                                  <span className="text-[11px] font-semibold text-[#F1F5FB] truncate" data-id="element-10205">
                                    {activeSource.label}
                                  </span>
                                  <span className={`inline-flex items-center px-1 py-[1px] rounded text-[8px] font-bold uppercase tracking-wide ${activeSource.tier === 'premium' ? 'bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] text-[#0b1628]' : activeSource.tier === 'imported' ? 'bg-[#8B5CF6]/20 text-[#A78BFA] border border-[#8B5CF6]/40' : activeSource.tier === 'existing' ? 'bg-[#00D4FF]/15 text-[#00D4FF] border border-[#00D4FF]/40' : 'bg-[#10B981]/20 text-[#34D399] border border-[#10B981]/40'}`} data-id="element-10206">
                                    {activeSource.badge}
                                  </span>
                                </div>
                                <span className="text-[9px] text-[#8A9BBA] leading-tight block mt-0.5 truncate" data-id="element-10207">
                                  {activeSource.sub}
                                </span>
                              </div>
                              <ChevronDownIcon size={12} className={`text-[#8A9BBA] flex-shrink-0 transition-transform ${sourceMenuOpen ? 'rotate-180' : ''}`} data-id="element-10208" />
                            </button>

                            {/* Dropdown menu */}
                            {sourceMenuOpen && <div className="absolute left-0 right-0 top-full mt-1 bg-[#0a1322] border border-[#2F4268] rounded-md shadow-2xl z-50 max-h-[320px] overflow-y-auto" data-id="element-10209">
                                {/* None option */}
                                <button onClick={() => {
                        updateTopo({
                          source: 'none'
                        });
                        setSourceMenuOpen(false);
                      }} className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-white/[0.04] transition-colors text-left border-b border-[#2F4268]/60" data-id="element-10210">
                                  <div className="text-[#8A9BBA] flex-shrink-0" data-id="element-10211">
                                    <XIcon size={12} data-id="element-10212" />
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-10213">
                                    <div className="flex items-center gap-1.5 flex-wrap" data-id="element-10214">
                                      <span className="text-[11px] font-semibold text-[#C5D1E4]" data-id="element-10215">
                                        No topography
                                      </span>
                                      <span className="inline-flex items-center px-1 py-[1px] rounded text-[8px] font-bold uppercase tracking-wide bg-[#2F4268] text-[#C5D1E4]" data-id="element-10216">
                                        Default
                                      </span>
                                    </div>
                                    <span className="text-[9px] text-[#8A9BBA] leading-tight block mt-0.5" data-id="element-10217">
                                      Skip topography for this pivot
                                    </span>
                                  </div>
                                </button>
                                {/* Source options */}
                                {SOURCES.map(src => {
                        const isActive = pivot.topography.source === src.id;
                        const tierStyle = src.tier === 'premium' ? 'bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] text-[#0b1628]' : src.tier === 'imported' ? 'bg-[#8B5CF6]/20 text-[#A78BFA] border border-[#8B5CF6]/40' : src.tier === 'existing' ? 'bg-[#00D4FF]/15 text-[#00D4FF] border border-[#00D4FF]/40' : 'bg-[#10B981]/20 text-[#34D399] border border-[#10B981]/40';
                        return <button key={src.id} onClick={() => {
                          updateTopo({
                            source: src.id,
                            contourPrecision: src.precision
                          });
                          setSourceMenuOpen(false);
                        }} className={`w-full flex items-center gap-2 px-2 py-1.5 transition-colors text-left ${isActive ? 'bg-[#1C2B47]' : 'hover:bg-white/[0.04]'}`} data-id="element-10218">
                                      <div className="text-[#8A9BBA] flex-shrink-0" data-id="element-10219">
                                        {src.icon}
                                      </div>
                                      <div className="flex-1 min-w-0" data-id="element-10220">
                                        <div className="flex items-center gap-1.5 flex-wrap" data-id="element-10221">
                                          <span className={`text-[11px] font-semibold ${isActive ? 'text-[#F1F5FB]' : 'text-[#C5D1E4]'}`} data-id="element-10222">
                                            {src.label}
                                          </span>
                                          <span className={`inline-flex items-center px-1 py-[1px] rounded text-[8px] font-bold uppercase tracking-wide ${tierStyle}`} data-id="element-10223">
                                            {src.badge}
                                          </span>
                                        </div>
                                        <span className="text-[9px] text-[#8A9BBA] leading-tight block mt-0.5" data-id="element-10224">
                                          {src.sub}
                                        </span>
                                      </div>
                                      {isActive && <div className="w-1.5 h-1.5 rounded-full bg-[#00D4FF] flex-shrink-0" data-id="element-10225" />}
                                    </button>;
                      })}
                              </div>}
                          </div>
                        </div>
                        {activeSource.tier === 'imported' && <div className="px-3 pb-2" data-id="element-10226">
                            <button className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-[10px] font-medium text-[#0b1628] bg-[#A78BFA] hover:bg-[#C4B5FD] transition-colors" data-id="element-10227">
                              <PlusIcon size={11} data-id="element-10228" />
                              Upload survey file
                            </button>
                          </div>}
                        {activeSource.tier === 'premium' && <div className="mx-3 mb-2 px-2 py-1.5 rounded bg-gradient-to-r from-[#FBBF24]/10 to-[#F59E0B]/10 border border-[#FBBF24]/30" data-id="element-10229">
                            <p className="text-[9px] text-[#FBBF24] leading-relaxed" data-id="element-10230">
                              Premium source — one-time activation per project.
                            </p>
                          </div>}
                      </>}
                  </CollapsibleSection>

                  {pivot.topography.source === 'none' && <div className="px-4 py-6 text-center" data-id="element-10231">
                      <div className="mx-auto w-10 h-10 rounded-full bg-[#132038] border border-[#2F4268] flex items-center justify-center mb-2" data-id="element-10232">
                        <MountainIcon size={18} className="text-[#4A5F8C]" data-id="element-10233" />
                      </div>
                      <p className="text-[11px] text-[#C5D1E4] font-medium" data-id="element-10234">
                        No topography selected
                      </p>
                      <p className="text-[9px] text-[#8A9BBA] mt-1 leading-relaxed max-w-[220px] mx-auto" data-id="element-10235">
                        Pick a source above to display contour lines, elevation
                        summary and data-quality details.
                      </p>
                    </div>}

                  {pivot.topography.source !== 'none' && <>
                      {/* 2. Display / Visibility */}
                      <CollapsibleSection label="Display" data-id="element-10236">
                        <PropRow label="Show topography" data-id="element-10237">
                          <InlineToggle value={pivot.topography.topographyVisible} onChange={v => updateTopo({
                    topographyVisible: v
                  })} data-id="element-10238" />
                        </PropRow>
                        <div className="px-3 pt-0.5 pb-2" data-id="element-10239">
                          <p className="text-[9px] text-[#8A9BBA] leading-relaxed" data-id="element-10240">
                            Master switch for all topographic overlays inside
                            the pivot area.
                          </p>
                        </div>
                      </CollapsibleSection>

                      {/* 3. Contour Lines */}
                      <CollapsibleSection label="Contour Lines" data-id="element-10241">
                        <PropRow label="Show contours" data-id="element-10242">
                          <InlineToggle value={pivot.topography.contoursEnabled} onChange={v => updateTopo({
                    contoursEnabled: v
                  })} data-id="element-10243" />
                        </PropRow>
                        <PropRow label="Equidistance" data-id="element-10244">
                          <InputWithUnit type="number" value={pivot.topography.contourEquidistance} unit="m" onChange={v => updateTopo({
                    contourEquidistance: Math.max(0.1, v || 1)
                  })} data-id="element-10245" />
                        </PropRow>
                        <PropRow label="Show labels" data-id="element-10246">
                          <InlineToggle value={pivot.topography.showElevation} onChange={v => updateTopo({
                    showElevation: v
                  })} data-id="element-10247" />
                        </PropRow>
                        <div className="px-3 pt-0.5 pb-2" data-id="element-10248">
                          <p className="text-[9px] text-[#8A9BBA] leading-relaxed" data-id="element-10249">
                            Vertical distance between consecutive contour lines.
                            Affects only the rendering, not the underlying data.
                          </p>
                        </div>
                      </CollapsibleSection>

                      {/* 4. Elevation Summary */}
                      <CollapsibleSection label="Elevation Summary" data-id="element-10250">
                        <PropRow label="Highest point" readonly data-id="element-10251">
                          <div className="flex items-center gap-1.5" data-id="element-10252">
                            <ArrowUpIcon size={11} className="text-[#34D399]" data-id="element-10253" />
                            <span className="text-[11px] text-[#F1F5FB] font-mono font-semibold" data-id="element-10254">
                              {pivot.topography.maxElevation} m
                            </span>
                          </div>
                        </PropRow>
                        <PropRow label="Lowest point" readonly data-id="element-10255">
                          <div className="flex items-center gap-1.5" data-id="element-10256">
                            <ArrowDownIcon size={11} className="text-[#F87171]" data-id="element-10257" />
                            <span className="text-[11px] text-[#F1F5FB] font-mono font-semibold" data-id="element-10258">
                              {pivot.topography.minElevation} m
                            </span>
                          </div>
                        </PropRow>
                        <PropRow label="Elevation Δ" readonly data-id="element-10259">
                          <span className="text-[11px] text-[#00D4FF] font-mono font-semibold" data-id="element-10260">
                            {pivot.topography.maxElevation - pivot.topography.minElevation}{' '}
                            m
                          </span>
                        </PropRow>
                        <PropRow label="Contour count" readonly data-id="element-10261">
                          <span className="text-[11px] text-[#C5D1E4] font-mono" data-id="element-10262">
                            ~{contourCount} lines
                          </span>
                        </PropRow>
                        <div className="px-3 pt-0.5 pb-2" data-id="element-10263">
                          <p className="text-[9px] text-[#8A9BBA] leading-relaxed" data-id="element-10264">
                            Computed from the active topography clipped to the
                            pivot area.
                          </p>
                        </div>
                      </CollapsibleSection>

                      {/* 5. Precision / Data Quality */}
                      <CollapsibleSection label="Data Quality" defaultOpen={false} data-id="element-10265">
                        <div className="px-3 pt-1 pb-2" data-id="element-10266">
                          <div className="flex items-center gap-2 px-2 py-1.5 rounded bg-[#132038] border border-[#2F4268]" data-id="element-10267">
                            <div className="text-[#00D4FF] flex-shrink-0" data-id="element-10268">
                              {activeSource.icon}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-10269">
                              <div className="flex items-center gap-1.5" data-id="element-10270">
                                <span className="text-[11px] font-semibold text-[#F1F5FB] truncate" data-id="element-10271">
                                  {activeSource.label}
                                </span>
                              </div>
                              <span className="text-[9px] text-[#8A9BBA]" data-id="element-10272">
                                Resolution {activeSource.precision} ·{' '}
                                {activeSource.tier === 'imported' ? 'Surveyor-grade' : activeSource.tier === 'existing' ? 'Reused from project' : activeSource.tier === 'premium' ? 'Commercial dataset' : 'Open dataset'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="px-3 pt-1 pb-2 space-y-1" data-id="element-10273">
                          <p className="text-[9px] text-[#8A9BBA] uppercase tracking-wide font-semibold" data-id="element-10274">
                            Resolution reference
                          </p>
                          {[{
                    res: '30 m',
                    tier: 'Free',
                    desc: 'Global SRTM — good for early sizing'
                  }, {
                    res: '10 m',
                    tier: 'Free',
                    desc: 'Regional DEM when available'
                  }, {
                    res: '5 m',
                    tier: 'Premium',
                    desc: 'High-res commercial DEM'
                  }, {
                    res: '1 m',
                    tier: 'Premium / Survey',
                    desc: 'LiDAR or field-surveyed'
                  }].map(row => <div key={row.res} className="flex items-center gap-2 px-2 py-1 rounded bg-[#0a1322] border border-[#2F4268]/40" data-id="element-10275">
                              <span className="text-[10px] font-mono font-semibold text-[#C5D1E4] w-10 flex-shrink-0" data-id="element-10276">
                                {row.res}
                              </span>
                              <span className="text-[9px] text-[#8A9BBA] flex-1 truncate" data-id="element-10277">
                                {row.desc}
                              </span>
                              <span className={`text-[8px] font-semibold uppercase tracking-wide flex-shrink-0 ${row.tier.startsWith('Premium') ? 'text-[#FBBF24]' : 'text-[#34D399]'}`} data-id="element-10278">
                                {row.tier}
                              </span>
                            </div>)}
                        </div>
                        <div className="px-3 pb-2" data-id="element-10279">
                          <p className="text-[9px] text-[#8A9BBA] leading-relaxed italic" data-id="element-10280">
                            Higher resolution improves contour detail, slope
                            analysis, and water-distribution planning accuracy.
                          </p>
                        </div>
                      </CollapsibleSection>
                    </>}
                </>;
        })()}

          {mode === 'profiles' && (() => {
          const hasTopo = pivot.topography.source !== 'none';
          const ANGLE_OPTIONS = [5, 10, 15, 30];
          const radialCount = Math.max(1, Math.floor(360 / radialAngleStep));
          const selectedAngle = radialSelectedIndex * radialAngleStep * Math.PI / 180;
          const profile = hasTopo ? buildRadialProfile(pivot, selectedAngle, radialSampleCount(pivot, profileSamplingDistance)) : null;
          return <>
                  {/* Profile type selector */}
                  <CollapsibleSection label="Profile Type" data-id="element-10281">
                    <div className="px-3 pt-1 pb-1" data-id="element-10282">
                      <p className="text-[9px] text-[#8A9BBA] leading-relaxed" data-id="element-10283">
                        Choose how to analyze elevation across the pivot area.
                      </p>
                    </div>
                    <div className="px-3 pt-1 pb-2 space-y-1.5" data-id="element-10284">
                      {[{
                  id: 'radial' as ProfileType,
                  label: 'Radial Profiles',
                  sub: 'From the center to the last tower, at angular intervals.',
                  icon: <CompassIcon size={14} data-id="element-10285" />
                }, {
                  id: 'tower-path' as ProfileType,
                  label: 'Tower Path Profile',
                  sub: 'Around the circular path of a selected tower.',
                  icon: <ActivityIcon size={14} data-id="element-10286" />
                }].map(opt => {
                  const isActive = profileType === opt.id;
                  const isStub = false;
                  return <button key={opt.id} onClick={() => {
                    setProfileType(opt.id);
                    onProfilePanelOpenChange(true);
                  }} disabled={!hasTopo} className={`w-full flex items-start gap-2 px-2 py-2 rounded border transition-colors text-left ${!hasTopo ? 'border-[#2F4268]/60 bg-[#132038]/40 opacity-60 cursor-not-allowed' : isActive ? 'border-[#00D4FF] bg-[#1C2B47]' : 'border-[#2F4268] hover:border-[#4A5F8C] bg-[#132038]'}`} data-id="element-10287">
                            <div className={`mt-0.5 flex-shrink-0 ${!hasTopo ? 'text-[#4A5F8C]' : isActive ? 'text-[#00D4FF]' : 'text-[#8A9BBA]'}`} data-id="element-10288">
                              {opt.icon}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-10289">
                              <div className="flex items-center gap-1.5 flex-wrap" data-id="element-10290">
                                <span className={`text-[11px] font-semibold ${!hasTopo ? 'text-[#8A9BBA]' : isActive ? 'text-[#F1F5FB]' : 'text-[#C5D1E4]'}`} data-id="element-10291">
                                  {opt.label}
                                </span>
                                {!hasTopo && <span className="inline-flex items-center gap-0.5 px-1 py-[1px] rounded text-[8px] font-bold uppercase tracking-wide bg-[#2F4268] text-[#8A9BBA]" data-id="element-10292">
                                    <LockIcon size={8} data-id="element-10293" />
                                    Topography required
                                  </span>}
                                {hasTopo && isStub && <span className="inline-flex items-center px-1 py-[1px] rounded text-[8px] font-bold uppercase tracking-wide bg-[#FBBF24]/15 text-[#FBBF24] border border-[#FBBF24]/40" data-id="element-10294">
                                    Coming next
                                  </span>}
                              </div>
                              <span className="text-[9px] text-[#8A9BBA] leading-tight block mt-1" data-id="element-10295">
                                {opt.sub}
                              </span>
                            </div>
                          </button>;
                })}
                    </div>
                    {!hasTopo && <div className="mx-3 mb-2 px-2 py-2 rounded bg-[#FBBF24]/5 border border-[#FBBF24]/30 flex items-start gap-2" data-id="element-10296">
                        <LockIcon size={12} className="text-[#FBBF24] flex-shrink-0 mt-0.5" data-id="element-10297" />
                        <div className="flex-1" data-id="element-10298">
                          <p className="text-[10px] text-[#F1F5FB] font-semibold leading-snug" data-id="element-10299">
                            A topography source is required
                          </p>
                          <p className="text-[9px] text-[#FBBF24]/90 leading-relaxed mt-0.5" data-id="element-10300">
                            Pick a topography source in the Topography panel to
                            unlock radial and tower-path elevation profiles.
                          </p>
                          <button onClick={() => setMode('contours')} className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold text-[#0b1628] bg-[#FBBF24] hover:bg-[#FCD34D] px-2 py-1 rounded transition-colors" data-id="element-10301">
                            <MountainIcon size={11} data-id="element-10302" />
                            Go to Topography
                          </button>
                        </div>
                      </div>}
                  </CollapsibleSection>

                  {hasTopo && <CollapsibleSection label="Sampling Distance" data-id="element-10303">
                      <div className="px-3 pt-1 pb-1" data-id="element-10304">
                        <p className="text-[9px] text-[#8A9BBA] leading-relaxed" data-id="element-10305">
                          Distance between elevation sample points along the
                          profile path.
                        </p>
                      </div>
                      <div className="px-3 pt-1 pb-2 grid grid-cols-5 gap-1" data-id="element-10306">
                        {[1, 2, 5, 10, 20].map(m => {
                  const isActive = profileSamplingDistance === m;
                  return <button key={m} onClick={() => onProfileSamplingDistanceChange(m)} className={`px-1 py-1.5 rounded border text-[10px] font-mono font-semibold transition-colors ${isActive ? 'border-[#00D4FF] bg-[#1C2B47] text-[#F1F5FB]' : 'border-[#2F4268] hover:border-[#4A5F8C] bg-[#132038] text-[#C5D1E4]'}`} data-id="element-10307">
                              {m} m
                            </button>;
                })}
                      </div>
                      <div className="px-3 pb-2" data-id="element-10308">
                        <p className="text-[9px] text-[#8A9BBA] leading-relaxed italic" data-id="element-10309">
                          Smaller intervals generate more elevation points and
                          produce a more detailed profile curve.
                        </p>
                        <div className="mt-1.5 flex items-center gap-2 text-[9px] font-mono" data-id="element-10310">
                          <span className="text-[#8A9BBA]" data-id="element-10311">Points:</span>
                          <span className="text-[#00D4FF] font-semibold" data-id="element-10312">
                            {profileType === 'radial' ? radialSampleCount(pivot, profileSamplingDistance) + 1 : towerPathSampleCount(pivot, Math.max(1, Math.min(pivot.towers, towerPathTowerIndex)), profileSamplingDistance) + 1}
                          </span>
                          <span className="text-[#8A9BBA]" data-id="element-10313">·</span>
                          <span className="text-[#8A9BBA]" data-id="element-10314">Path:</span>
                          <span className="text-[#C5D1E4] font-semibold" data-id="element-10315">
                            {profileType === 'radial' ? `${pivot.radiusPx.toFixed(0)} m` : `${(2 * Math.PI * (Math.max(1, Math.min(pivot.towers, towerPathTowerIndex)) / Math.max(1, pivot.towers) * pivot.radiusPx)).toFixed(0)} m`}
                          </span>
                        </div>
                      </div>
                    </CollapsibleSection>}

                  {hasTopo && profileType === 'radial' && profile && <>
                      {/* Angular interval */}
                      <CollapsibleSection label="Angular Interval" data-id="element-10316">
                        <div className="px-3 pt-1 pb-1" data-id="element-10317">
                          <p className="text-[9px] text-[#8A9BBA] leading-relaxed" data-id="element-10318">
                            Spacing between consecutive radials around the
                            pivot.
                          </p>
                        </div>
                        <div className="px-3 pt-1 pb-2 grid grid-cols-4 gap-1" data-id="element-10319">
                          {ANGLE_OPTIONS.map(deg => {
                    const isActive = radialAngleStep === deg;
                    return <button key={deg} onClick={() => {
                      setRadialAngleStep(deg);
                      setRadialSelectedIndex(0);
                    }} className={`px-1 py-1.5 rounded border text-[10px] font-mono font-semibold transition-colors ${isActive ? 'border-[#00D4FF] bg-[#1C2B47] text-[#F1F5FB]' : 'border-[#2F4268] hover:border-[#4A5F8C] bg-[#132038] text-[#C5D1E4]'}`} data-id="element-10320">
                                {deg}°
                              </button>;
                  })}
                        </div>
                        <div className="px-3 pb-2" data-id="element-10321">
                          <p className="text-[9px] text-[#8A9BBA]" data-id="element-10322">
                            {radialCount} radials generated
                          </p>
                        </div>
                      </CollapsibleSection>

                      {/* Radial selector */}
                      <CollapsibleSection label="Selected Radial" data-id="element-10323">
                        <div className="px-3 pt-1 pb-2" data-id="element-10324">
                          <div className="flex items-center gap-1.5 mb-2" data-id="element-10325">
                            <button onClick={() => setRadialSelectedIndex((radialSelectedIndex - 1 + radialCount) % radialCount)} className="p-1 rounded text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5 border border-[#2F4268] transition-colors" title="Previous radial" data-id="element-10326">
                              <ChevronRightIcon size={12} className="rotate-180" data-id="element-10327" />
                            </button>
                            <div className="flex-1 px-2 py-1 rounded bg-[#1C2B47] border border-[#00D4FF]/40 text-center" data-id="element-10328">
                              <span className="text-[10px] text-[#8A9BBA]" data-id="element-10329">
                                Angle
                              </span>
                              <div className="text-[14px] text-[#F1F5FB] font-mono font-bold leading-tight" data-id="element-10330">
                                {(radialSelectedIndex * radialAngleStep).toFixed(0)}
                                °
                              </div>
                            </div>
                            <button onClick={() => setRadialSelectedIndex((radialSelectedIndex + 1) % radialCount)} className="p-1 rounded text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5 border border-[#2F4268] transition-colors" title="Next radial" data-id="element-10331">
                              <ChevronRightIcon size={12} data-id="element-10332" />
                            </button>
                          </div>
                          <p className="text-[9px] text-[#8A9BBA] leading-relaxed" data-id="element-10333">
                            Radial {radialSelectedIndex + 1} of {radialCount}.
                            Pick another radial in the top-down view by clicking
                            it.
                          </p>
                        </div>
                      </CollapsibleSection>

                      {/* Inline summary — full chart is shown in the horizontal panel below the canvas */}
                      <CollapsibleSection label="Profile Summary" data-id="element-10334">
                        <div className="px-3 py-2 grid grid-cols-3 gap-1.5" data-id="element-10335">
                          <div className="px-2 py-1.5 rounded bg-[#132038] border border-[#2F4268]/60" data-id="element-10336">
                            <div className="flex items-center gap-1" data-id="element-10337">
                              <ArrowUpIcon size={9} className="text-[#34D399]" data-id="element-10338" />
                              <span className="text-[8px] text-[#8A9BBA] uppercase tracking-wide font-semibold" data-id="element-10339">
                                High
                              </span>
                            </div>
                            <div className="text-[10px] text-[#F1F5FB] font-mono font-semibold mt-0.5" data-id="element-10340">
                              {profile.maxElev.toFixed(0)} m
                            </div>
                          </div>
                          <div className="px-2 py-1.5 rounded bg-[#132038] border border-[#2F4268]/60" data-id="element-10341">
                            <div className="flex items-center gap-1" data-id="element-10342">
                              <ArrowDownIcon size={9} className="text-[#F87171]" data-id="element-10343" />
                              <span className="text-[8px] text-[#8A9BBA] uppercase tracking-wide font-semibold" data-id="element-10344">
                                Low
                              </span>
                            </div>
                            <div className="text-[10px] text-[#F1F5FB] font-mono font-semibold mt-0.5" data-id="element-10345">
                              {profile.minElev.toFixed(0)} m
                            </div>
                          </div>
                          <div className="px-2 py-1.5 rounded bg-[#1C2B47] border border-[#00D4FF]/30" data-id="element-10346">
                            <span className="text-[8px] text-[#8A9BBA] uppercase tracking-wide font-semibold" data-id="element-10347">
                              Δ Total
                            </span>
                            <div className="text-[10px] text-[#00D4FF] font-mono font-bold mt-0.5" data-id="element-10348">
                              {profile.totalElev.toFixed(1)} m
                            </div>
                          </div>
                        </div>
                        <div className="px-3 py-1 flex items-center justify-between text-[9px] font-mono border-t border-[#2F4268]/40 mx-3" data-id="element-10349">
                          <span className="text-[#8A9BBA]" data-id="element-10350">
                            Sampling distance
                          </span>
                          <span className="text-[#C5D1E4] font-semibold" data-id="element-10351">
                            {profileSamplingDistance} m
                          </span>
                        </div>
                        <div className="px-3 pb-1 flex items-center justify-between text-[9px] font-mono" data-id="element-10352">
                          <span className="text-[#8A9BBA]" data-id="element-10353">
                            Elevation points
                          </span>
                          <span className="text-[#00D4FF] font-semibold" data-id="element-10354">
                            {profile.points.length}
                          </span>
                        </div>
                        <div className="px-3 pb-2 flex items-center gap-1.5" data-id="element-10355">
                          <LineChartIcon size={11} className="text-[#00D4FF] flex-shrink-0" data-id="element-10356" />
                          <p className="text-[9px] text-[#8A9BBA] leading-relaxed" data-id="element-10357">
                            Full elevation chart is shown in the bottom panel
                            below the canvas.
                          </p>
                        </div>
                      </CollapsibleSection>
                    </>}

                  {hasTopo && profileType === 'tower-path' && (() => {
              const n = Math.max(1, pivot.towers);
              const sel = Math.max(1, Math.min(n, towerPathTowerIndex));
              const tpSamples = towerPathSampleCount(pivot, sel, profileSamplingDistance);
              const tpProfile = buildTowerPathProfile(pivot, sel, tpSamples);
              return <>
                          {/* Explanation */}
                          <CollapsibleSection label="About this analysis" data-id="element-10358">
                            <div className="px-3 py-2" data-id="element-10359">
                              <p className="text-[9px] text-[#C5D1E4] leading-relaxed" data-id="element-10360">
                                Tower Path Profile traces the elevation along
                                the{' '}
                                <span className="text-[#F1F5FB] font-semibold" data-id="element-10361">
                                  complete circular path
                                </span>{' '}
                                a tower follows around the pivot — from 0° all
                                the way to 360°.
                              </p>
                              <p className="text-[9px] text-[#8A9BBA] leading-relaxed mt-1.5 italic" data-id="element-10362">
                                This is not the radial distance between two
                                towers — it's one full lap of a single tower.
                              </p>
                            </div>
                          </CollapsibleSection>

                          {/* Tower selector */}
                          <CollapsibleSection label="Selected Tower" data-id="element-10363">
                            <div className="px-3 pt-1 pb-1" data-id="element-10364">
                              <p className="text-[9px] text-[#8A9BBA] leading-relaxed" data-id="element-10365">
                                Pick which tower's circular path to analyze. The
                                highlighted ring on the canvas shows its
                                trajectory.
                              </p>
                            </div>
                            <div className="px-3 pt-1 pb-2" data-id="element-10366">
                              <div className="flex items-center gap-1.5 mb-2" data-id="element-10367">
                                <button onClick={() => onTowerPathTowerIndexChange((sel - 2 + n) % n + 1)} className="p-1 rounded text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5 border border-[#2F4268] transition-colors" title="Previous tower" data-id="element-10368">
                                  <ChevronRightIcon size={12} className="rotate-180" data-id="element-10369" />
                                </button>
                                <div className="flex-1 px-2 py-1 rounded bg-[#1C2B47] border border-[#00D4FF]/40 text-center" data-id="element-10370">
                                  <span className="text-[10px] text-[#8A9BBA]" data-id="element-10371">
                                    Tower
                                  </span>
                                  <div className="text-[14px] text-[#F1F5FB] font-mono font-bold leading-tight" data-id="element-10372">
                                    T{sel}
                                  </div>
                                </div>
                                <button onClick={() => onTowerPathTowerIndexChange(sel % n + 1)} className="p-1 rounded text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5 border border-[#2F4268] transition-colors" title="Next tower" data-id="element-10373">
                                  <ChevronRightIcon size={12} data-id="element-10374" />
                                </button>
                              </div>
                              {/* Tower chip grid */}
                              <div className="grid grid-cols-7 gap-1 mb-2" data-id="element-10375">
                                {Array.from({
                        length: n
                      }).map((_, i) => {
                        const isActive = i + 1 === sel;
                        return <button key={`tw-${i}`} onClick={() => onTowerPathTowerIndexChange(i + 1)} className={`px-1 py-1 rounded text-[10px] font-mono font-semibold transition-colors border ${isActive ? 'border-[#00D4FF] bg-[#1C2B47] text-[#F1F5FB]' : 'border-[#2F4268] hover:border-[#4A5F8C] bg-[#132038] text-[#C5D1E4]'}`} title={`Tower ${i + 1}`} data-id="element-10376">
                                      T{i + 1}
                                    </button>;
                      })}
                              </div>
                              <div className="grid grid-cols-2 gap-1.5" data-id="element-10377">
                                <div className="px-2 py-1 rounded bg-[#132038] border border-[#2F4268]/60" data-id="element-10378">
                                  <span className="text-[8px] text-[#8A9BBA] uppercase tracking-wide font-semibold" data-id="element-10379">
                                    Radius
                                  </span>
                                  <div className="text-[10px] text-[#F1F5FB] font-mono font-semibold mt-0.5" data-id="element-10380">
                                    {tpProfile.radiusM.toFixed(1)} m
                                  </div>
                                </div>
                                <div className="px-2 py-1 rounded bg-[#132038] border border-[#2F4268]/60" data-id="element-10381">
                                  <span className="text-[8px] text-[#8A9BBA] uppercase tracking-wide font-semibold" data-id="element-10382">
                                    Lap length
                                  </span>
                                  <div className="text-[10px] text-[#F1F5FB] font-mono font-semibold mt-0.5" data-id="element-10383">
                                    {tpProfile.circumferenceM.toFixed(0)} m
                                  </div>
                                </div>
                              </div>
                            </div>
                          </CollapsibleSection>

                          {/* Profile Summary */}
                          <CollapsibleSection label="Path Summary" data-id="element-10384">
                            <div className="px-3 py-2 grid grid-cols-3 gap-1.5" data-id="element-10385">
                              <div className="px-2 py-1.5 rounded bg-[#132038] border border-[#2F4268]/60" data-id="element-10386">
                                <div className="flex items-center gap-1" data-id="element-10387">
                                  <ArrowUpIcon size={9} className="text-[#34D399]" data-id="element-10388" />
                                  <span className="text-[8px] text-[#8A9BBA] uppercase tracking-wide font-semibold" data-id="element-10389">
                                    High
                                  </span>
                                </div>
                                <div className="text-[10px] text-[#F1F5FB] font-mono font-semibold mt-0.5" data-id="element-10390">
                                  {tpProfile.maxElev.toFixed(0)} m
                                </div>
                              </div>
                              <div className="px-2 py-1.5 rounded bg-[#132038] border border-[#2F4268]/60" data-id="element-10391">
                                <div className="flex items-center gap-1" data-id="element-10392">
                                  <ArrowDownIcon size={9} className="text-[#F87171]" data-id="element-10393" />
                                  <span className="text-[8px] text-[#8A9BBA] uppercase tracking-wide font-semibold" data-id="element-10394">
                                    Low
                                  </span>
                                </div>
                                <div className="text-[10px] text-[#F1F5FB] font-mono font-semibold mt-0.5" data-id="element-10395">
                                  {tpProfile.minElev.toFixed(0)} m
                                </div>
                              </div>
                              <div className="px-2 py-1.5 rounded bg-[#1C2B47] border border-[#00D4FF]/30" data-id="element-10396">
                                <span className="text-[8px] text-[#8A9BBA] uppercase tracking-wide font-semibold" data-id="element-10397">
                                  Δ Total
                                </span>
                                <div className="text-[10px] text-[#00D4FF] font-mono font-bold mt-0.5" data-id="element-10398">
                                  {tpProfile.totalElev.toFixed(1)} m
                                </div>
                              </div>
                              <div className="px-2 py-1.5 rounded bg-[#132038] border border-[#2F4268]/60" data-id="element-10399">
                                <div className="flex items-center gap-1" data-id="element-10400">
                                  <TrendingUpIcon size={9} className="text-[#34D399]" data-id="element-10401" />
                                  <span className="text-[8px] text-[#8A9BBA] uppercase tracking-wide font-semibold" data-id="element-10402">
                                    Max ascent
                                  </span>
                                </div>
                                <div className="text-[10px] text-[#34D399] font-mono font-semibold mt-0.5" data-id="element-10403">
                                  +{tpProfile.maxAscent.toFixed(1)}%
                                </div>
                              </div>
                              <div className="px-2 py-1.5 rounded bg-[#132038] border border-[#2F4268]/60" data-id="element-10404">
                                <div className="flex items-center gap-1" data-id="element-10405">
                                  <TrendingUpIcon size={9} className="text-[#F87171] rotate-180" data-id="element-10406" />
                                  <span className="text-[8px] text-[#8A9BBA] uppercase tracking-wide font-semibold" data-id="element-10407">
                                    Max descent
                                  </span>
                                </div>
                                <div className="text-[10px] text-[#F87171] font-mono font-semibold mt-0.5" data-id="element-10408">
                                  {tpProfile.maxDescent.toFixed(1)}%
                                </div>
                              </div>
                              <div className="px-2 py-1.5 rounded bg-[#132038] border border-[#2F4268]/60 flex items-center" data-id="element-10409">
                                <button onClick={() => onProfilePanelOpenChange(true)} className="w-full flex items-center justify-center gap-1 px-1 py-1 rounded bg-[#00D4FF]/15 hover:bg-[#00D4FF]/25 border border-[#00D4FF]/40 text-[#00D4FF] text-[9px] font-semibold transition-colors" data-id="element-10410">
                                  <LineChartIcon size={10} data-id="element-10411" />
                                  Show chart
                                </button>
                              </div>
                            </div>
                            <div className="px-3 py-1 flex items-center justify-between text-[9px] font-mono border-t border-[#2F4268]/40 mx-3" data-id="element-10412">
                              <span className="text-[#8A9BBA]" data-id="element-10413">
                                Sampling distance
                              </span>
                              <span className="text-[#C5D1E4] font-semibold" data-id="element-10414">
                                {profileSamplingDistance} m
                              </span>
                            </div>
                            <div className="px-3 pb-1 flex items-center justify-between text-[9px] font-mono" data-id="element-10415">
                              <span className="text-[#8A9BBA]" data-id="element-10416">
                                Elevation points
                              </span>
                              <span className="text-[#00D4FF] font-semibold" data-id="element-10417">
                                {tpProfile.points.length}
                              </span>
                            </div>
                            <div className="px-3 pb-2 flex items-center gap-1.5" data-id="element-10418">
                              <LineChartIcon size={11} className="text-[#00D4FF] flex-shrink-0" data-id="element-10419" />
                              <p className="text-[9px] text-[#8A9BBA] leading-relaxed" data-id="element-10420">
                                Full chart (0° → 360°) shown in the bottom panel
                                below the canvas.
                              </p>
                            </div>
                          </CollapsibleSection>
                        </>;
            })()}
                </>;
        })()}

          {mode === 'slope-analysis' && (() => {
          const hasTopo = pivot.topography.source !== 'none';
          const isPremium = pivot.topography.source === 'premium-5m' || pivot.topography.source === 'premium-1m';
          const n = Math.max(1, pivot.towers);
          const towerIdx = Math.max(1, Math.min(n, slopeTowerIndex));
          return <SlopeAnalysisPanel pivot={pivot} hasTopo={hasTopo} isPremium={isPremium} active={activeSlopeAnalysis} onActiveChange={a => {
            onActiveSlopeAnalysisChange(a);
            onSlopeChartPanelOpenChange(a !== null);
          }} towerIndex={towerIdx} onTowerIndexChange={onSlopeTowerIndexChange} onGoToTopography={() => setMode('contours')} chartPanelOpen={slopeChartPanelOpen} onShowChart={() => onSlopeChartPanelOpenChange(true)} cropClearanceConfig={cropClearanceConfig} onCropClearanceConfigChange={onCropClearanceConfigChange} radialProfileConfig={radialProfileConfig} onRadialProfileConfigChange={onRadialProfileConfigChange} profileSamplingDistance={profileSamplingDistance} data-id="element-10421" />;
        })()}
        </div>

        {/* Footer — technical sub-tab strip */}
        {mode === 'technical' && <div className="border-t border-[#2F4268] flex-shrink-0 bg-[#0a1322]" data-id="element-10422">
            <div className="flex" data-id="element-10423">
              {([{
            id: 'general',
            label: 'General'
          }, {
            id: 'spans',
            label: 'Spans'
          }, {
            id: 'endguns',
            label: 'Endguns'
          }, {
            id: 'pivot-point',
            label: 'Pivot Point'
          }] as const).map(t => {
            const isActive = technicalTab === t.id;
            return <button key={t.id} onClick={() => setTechnicalTab(t.id)} className={`flex-1 px-2 py-2 text-[10px] font-medium transition-colors border-r border-[#2F4268]/60 last:border-r-0 ${isActive ? 'text-[#00D4FF] bg-[#132038]' : 'text-[#8A9BBA] hover:text-[#F1F5FB] hover:bg-white/5'}`} data-id="element-10424">
                    {t.label}
                  </button>;
          })}
            </div>
          </div>}
      </div>
    </aside>;
}
function ModeRailButton({
  active,
  onClick,
  icon,
  label
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return <div className="relative group w-full flex justify-center mb-1" data-id="element-10425">
      <button onClick={onClick} className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${active ? 'bg-[#1C2B47] text-[#00D4FF]' : 'text-[#8A9BBA] hover:text-[#F1F5FB] hover:bg-white/5'}`} title={label} data-id="element-10426">
        {icon}
      </button>
      <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-0.5 rounded text-[10px] font-medium text-[#F1F5FB] bg-[#0a1322] border border-[#2F4268] opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50" data-id="element-10427">
        {label}
      </div>
    </div>;
}
function CollapsibleSection({
  label,
  children,
  defaultOpen = true
}: {
  label: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return <div className="border-b border-[#2F4268]/60" data-id="element-10428">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-3 py-1.5 hover:bg-white/[0.02] transition-colors" data-id="element-10429">
        <span className="text-[10px] text-[#8A9BBA] uppercase tracking-wide font-semibold" data-id="element-10430">
          {label}
        </span>
        {open ? <ChevronDownIcon size={11} className="text-[#8A9BBA]" data-id="element-10431" /> : <ChevronRightIcon size={11} className="text-[#8A9BBA]" data-id="element-10432" />}
      </button>
      {open && <div className="pb-1" data-id="element-10433">{children}</div>}
    </div>;
}
function ProfileStat({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return <div className="px-2 py-1.5 rounded bg-[#132038] border border-[#2F4268]/60 flex items-center gap-1.5" data-id="element-10434">
      <div className="flex-shrink-0" data-id="element-10435">{icon}</div>
      <div className="flex-1 min-w-0" data-id="element-10436">
        <div className="text-[8px] text-[#8A9BBA] uppercase tracking-wide font-semibold leading-tight" data-id="element-10437">
          {label}
        </div>
        <div className="text-[11px] text-[#F1F5FB] font-mono font-semibold leading-tight" data-id="element-10438">
          {value}
        </div>
      </div>
    </div>;
}
// ============================================================
// Slope Analysis Panel — 5 sub-analyses derived from the active topography.
// Mock thresholds reflect typical pivot manufacturer guidelines.
// ============================================================
type SlopeAnalysisDef = {
  id: SlopeAnalysisType;
  label: string;
  short: string;
  description: string;
  icon: React.ReactNode;
  requiresPremium?: boolean;
};
const SLOPE_ANALYSES: SlopeAnalysisDef[] = [{
  id: 'crop-clearance',
  label: 'Crop Clearance Analysis',
  short: 'Clearance under spans, trussing, overhang',
  description: 'Checks available clearance between the structure, the crop, and the terrain. Flags ridges, dips and ravines that may reduce clearance under any span.',
  icon: <Maximize2Icon size={14} data-id="element-10439" />
}, {
  id: 'transverse',
  label: 'Transverse Slope Analysis',
  short: 'Side-to-side slope along a tower wheel path',
  description: 'Evaluates side-to-side slope along the circular wheel path of a tower. Highlights zones with relevant high-side / low-side terrain.',
  icon: <MoveHorizontalIcon size={14} data-id="element-10440" />
}, {
  id: 'torsional',
  label: 'Torsional Slope Analysis',
  short: 'Twisting stress on opposing wheel paths',
  description: 'Detects spans whose inner and outer wheel paths run on opposing slopes — a primary cause of structural torsion damage.',
  icon: <RotateCcwIcon size={14} data-id="element-10441" />
}, {
  id: 'longitudinal',
  label: 'Longitudinal Slope Analysis',
  short: 'Slope along the span between supports',
  description: 'Measures slope along the span direction between consecutive supports. Identifies tension (descent) and compression (ascent) zones.',
  icon: <WavesIcon size={14} data-id="element-10442" />
}, {
  id: 'structural-absorption',
  label: 'Structural Slope Absorption',
  short: 'Combined slope changes vs. structural capacity',
  description: 'Checks whether the cumulative slope change across consecutive spans stays within the configuration’s allowable flex envelope.',
  icon: <LayoutGridIcon size={14} data-id="element-10443" />,
  requiresPremium: true
}];
// Mature-canopy heights for common pivot-irrigated crops (in meters).
// Source: typical agronomic max canopy heights (rounded). Edit-friendly.
const CROP_OPTIONS: {
  name: string;
  heightM: number;
  group: string;
}[] = [
// Cereals
{
  name: 'Alfalfa',
  heightM: 0.9,
  group: 'Forage'
}, {
  name: 'Barley',
  heightM: 1.0,
  group: 'Cereals'
}, {
  name: 'Buckwheat',
  heightM: 1.1,
  group: 'Cereals'
}, {
  name: 'Canola',
  heightM: 1.5,
  group: 'Oilseeds'
}, {
  name: 'Carrot',
  heightM: 0.5,
  group: 'Vegetables'
}, {
  name: 'Cassava',
  heightM: 2.5,
  group: 'Roots & Tubers'
}, {
  name: 'Chickpea',
  heightM: 0.7,
  group: 'Pulses'
}, {
  name: 'Cotton',
  heightM: 1.6,
  group: 'Fibers'
}, {
  name: 'Cowpea',
  heightM: 0.8,
  group: 'Pulses'
}, {
  name: 'Corn (mature)',
  heightM: 2.4,
  group: 'Cereals'
}, {
  name: 'Corn (silage)',
  heightM: 2.8,
  group: 'Cereals'
}, {
  name: 'Cucumber',
  heightM: 0.6,
  group: 'Vegetables'
}, {
  name: 'Faba bean',
  heightM: 1.2,
  group: 'Pulses'
}, {
  name: 'Flax',
  heightM: 1.0,
  group: 'Oilseeds'
}, {
  name: 'Garlic',
  heightM: 0.5,
  group: 'Vegetables'
}, {
  name: 'Grass pasture',
  heightM: 0.7,
  group: 'Forage'
}, {
  name: 'Hemp (fiber)',
  heightM: 3.5,
  group: 'Fibers'
}, {
  name: 'Hops',
  heightM: 5.5,
  group: 'Specialty'
}, {
  name: 'Lentil',
  heightM: 0.5,
  group: 'Pulses'
}, {
  name: 'Millet',
  heightM: 1.8,
  group: 'Cereals'
}, {
  name: 'Oat',
  heightM: 1.2,
  group: 'Cereals'
}, {
  name: 'Onion',
  heightM: 0.6,
  group: 'Vegetables'
}, {
  name: 'Pea',
  heightM: 0.9,
  group: 'Pulses'
}, {
  name: 'Peanut',
  heightM: 0.6,
  group: 'Pulses'
}, {
  name: 'Pepper',
  heightM: 0.9,
  group: 'Vegetables'
}, {
  name: 'Potato',
  heightM: 0.8,
  group: 'Roots & Tubers'
}, {
  name: 'Rice',
  heightM: 1.3,
  group: 'Cereals'
}, {
  name: 'Rye',
  heightM: 1.6,
  group: 'Cereals'
}, {
  name: 'Safflower',
  heightM: 1.2,
  group: 'Oilseeds'
}, {
  name: 'Sesame',
  heightM: 1.5,
  group: 'Oilseeds'
}, {
  name: 'Sorghum',
  heightM: 2.0,
  group: 'Cereals'
}, {
  name: 'Soybean',
  heightM: 1.1,
  group: 'Pulses'
}, {
  name: 'Spinach',
  heightM: 0.3,
  group: 'Vegetables'
}, {
  name: 'Sugar beet',
  heightM: 0.6,
  group: 'Roots & Tubers'
}, {
  name: 'Sugarcane',
  heightM: 4.0,
  group: 'Specialty'
}, {
  name: 'Sunflower',
  heightM: 2.3,
  group: 'Oilseeds'
}, {
  name: 'Sweet potato',
  heightM: 0.5,
  group: 'Roots & Tubers'
}, {
  name: 'Tobacco',
  heightM: 1.7,
  group: 'Specialty'
}, {
  name: 'Tomato',
  heightM: 1.5,
  group: 'Vegetables'
}, {
  name: 'Triticale',
  heightM: 1.2,
  group: 'Cereals'
}, {
  name: 'Watermelon',
  heightM: 0.4,
  group: 'Vegetables'
}, {
  name: 'Wheat',
  heightM: 1.2,
  group: 'Cereals'
}, {
  name: 'Custom (none)',
  heightM: 0.0,
  group: 'Other'
}];
// Helper: classify a numeric value vs. safe/warning/exceeded thresholds.
function classifySlope(absValue: number, warnAt: number, exceedAt: number): 'safe' | 'warning' | 'exceeded' {
  if (absValue >= exceedAt) return 'exceeded';
  if (absValue >= warnAt) return 'warning';
  return 'safe';
}
function SlopeAnalysisPanel({
  pivot,
  hasTopo,
  isPremium,
  active,
  onActiveChange,
  towerIndex,
  onTowerIndexChange,
  onGoToTopography,
  chartPanelOpen,
  onShowChart,
  cropClearanceConfig,
  onCropClearanceConfigChange,
  radialProfileConfig,
  onRadialProfileConfigChange,
  profileSamplingDistance
}: {
  pivot: Pivot;
  hasTopo: boolean;
  isPremium: boolean;
  active: SlopeAnalysisType | null;
  onActiveChange: (a: SlopeAnalysisType | null) => void;
  towerIndex: number;
  onTowerIndexChange: (i: number) => void;
  onGoToTopography: () => void;
  chartPanelOpen: boolean;
  onShowChart: () => void;
  cropClearanceConfig: {
    structHeightM: number;
    warnAtM: number;
    cropHeightM: number;
    cropName: string;
  };
  onCropClearanceConfigChange: (c: {
    structHeightM: number;
    warnAtM: number;
    cropHeightM: number;
    cropName: string;
  }) => void;
  radialProfileConfig: {
    angleStep: number;
    selectedIndex: number;
  };
  onRadialProfileConfigChange: (c: {
    angleStep: number;
    selectedIndex: number;
  }) => void;
  profileSamplingDistance: number;
}) {
  // Card list view
  if (!active) {
    return <>
        <CollapsibleSection label="About Slope Analysis" data-id="element-10444">
          <div className="px-3 py-2" data-id="element-10445">
            <p className="text-[9px] text-[#C5D1E4] leading-relaxed" data-id="element-10446">
              <span className="text-[#F1F5FB] font-semibold" data-id="element-10447">Profiles</span>{' '}
              shows how elevation changes along a path.{' '}
              <span className="text-[#F1F5FB] font-semibold" data-id="element-10448">
                Slope Analysis
              </span>{' '}
              interprets those changes as structural and operational risk for
              the pivot.
            </p>
          </div>
        </CollapsibleSection>

        {!hasTopo && <div className="mx-3 mt-2 mb-2 px-2 py-2 rounded bg-[#FBBF24]/5 border border-[#FBBF24]/30 flex items-start gap-2" data-id="element-10449">
            <LockIcon size={12} className="text-[#FBBF24] flex-shrink-0 mt-0.5" data-id="element-10450" />
            <div className="flex-1" data-id="element-10451">
              <p className="text-[10px] text-[#F1F5FB] font-semibold leading-snug" data-id="element-10452">
                Topography required
              </p>
              <p className="text-[9px] text-[#FBBF24]/90 leading-relaxed mt-0.5" data-id="element-10453">
                Pick a topography source to unlock the slope analyses.
              </p>
              <button onClick={onGoToTopography} className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold text-[#0b1628] bg-[#FBBF24] hover:bg-[#FCD34D] px-2 py-1 rounded transition-colors" data-id="element-10454">
                <MountainIcon size={11} data-id="element-10455" />
                Go to Topography
              </button>
            </div>
          </div>}

        <CollapsibleSection label="Select Analysis" data-id="element-10456">
          <div className="px-3 pt-1 pb-2 space-y-1.5" data-id="element-10457">
            {SLOPE_ANALYSES.map((def, i) => {
            const needsPremium = def.requiresPremium && !isPremium;
            const disabled = !hasTopo || needsPremium;
            return <button key={def.id} onClick={() => !disabled && onActiveChange(def.id)} disabled={disabled} className={`w-full flex items-start gap-2 px-2 py-2 rounded border transition-colors text-left ${disabled ? 'border-[#2F4268]/60 bg-[#132038]/40 opacity-60 cursor-not-allowed' : 'border-[#2F4268] hover:border-[#00D4FF] hover:bg-[#1C2B47] bg-[#132038]'}`} data-id="element-10458">
                  <div className="flex items-center justify-center w-5 h-5 rounded bg-[#0a1322] text-[#00D4FF] flex-shrink-0 text-[9px] font-bold font-mono" data-id="element-10459">
                    {i + 1}
                  </div>
                  <div className="flex-shrink-0 text-[#8A9BBA] mt-0.5" data-id="element-10460">
                    {def.icon}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-10461">
                    <div className="flex items-center gap-1.5 flex-wrap mb-0.5" data-id="element-10462">
                      <span className="text-[11px] font-semibold text-[#F1F5FB]" data-id="element-10463">
                        {def.label}
                      </span>
                      {!hasTopo && <span className="inline-flex items-center gap-0.5 px-1 py-[1px] rounded text-[8px] font-bold uppercase tracking-wide bg-[#2F4268] text-[#8A9BBA]" data-id="element-10464">
                          <LockIcon size={8} data-id="element-10465" />
                          Requires topography
                        </span>}
                      {hasTopo && needsPremium && <span className="inline-flex items-center gap-0.5 px-1 py-[1px] rounded text-[8px] font-bold uppercase tracking-wide bg-[#FBBF24]/15 text-[#FBBF24] border border-[#FBBF24]/40" data-id="element-10466">
                          <LockIcon size={8} data-id="element-10467" />
                          Premium topography
                        </span>}
                      {!disabled && <span className="inline-flex items-center px-1 py-[1px] rounded text-[8px] font-bold uppercase tracking-wide bg-[#34D399]/15 text-[#34D399] border border-[#34D399]/40" data-id="element-10468">
                          Available
                        </span>}
                    </div>
                    <p className="text-[9px] text-[#8A9BBA] leading-tight" data-id="element-10469">
                      {def.description}
                    </p>
                  </div>
                  {!disabled && <ChevronRightIcon size={12} className="text-[#8A9BBA] flex-shrink-0 mt-1" data-id="element-10470" />}
                </button>;
          })}
          </div>
        </CollapsibleSection>
      </>;
  }
  // Detail view
  const def = SLOPE_ANALYSES.find(d => d.id === active)!;
  return <>
      {/* Back bar */}
      <div className="px-3 py-2 border-b border-[#2F4268] flex items-center gap-2" data-id="element-10471">
        <button onClick={() => onActiveChange(null)} className="p-1 rounded text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5 border border-[#2F4268] transition-colors flex-shrink-0" title="Back to slope analyses" data-id="element-10472">
          <ArrowLeftIcon size={12} data-id="element-10473" />
        </button>
        <div className="flex-shrink-0 text-[#00D4FF]" data-id="element-10474">{def.icon}</div>
        <div className="flex-1 min-w-0" data-id="element-10475">
          <div className="text-[11px] font-semibold text-[#F1F5FB] leading-tight truncate" data-id="element-10476">
            {def.label}
          </div>
          <div className="text-[9px] text-[#8A9BBA] leading-tight truncate" data-id="element-10477">
            {def.short}
          </div>
        </div>
        {!chartPanelOpen && <button onClick={onShowChart} className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-semibold bg-[#00D4FF]/15 hover:bg-[#00D4FF]/25 border border-[#00D4FF]/40 text-[#00D4FF] transition-colors" title="Re-open the analysis chart" data-id="element-10478">
            <LineChartIcon size={10} data-id="element-10479" />
            Chart
          </button>}
      </div>

      {active === 'crop-clearance' && <CropClearanceView pivot={pivot} config={cropClearanceConfig} onConfigChange={onCropClearanceConfigChange} radialProfileConfig={radialProfileConfig} onRadialProfileConfigChange={onRadialProfileConfigChange} samplingDistance={profileSamplingDistance} data-id="element-10480" />}
      {active === 'transverse' && <TransverseSlopeView pivot={pivot} towerIndex={towerIndex} onTowerIndexChange={onTowerIndexChange} data-id="element-10481" />}
      {active === 'torsional' && <TorsionalSlopeView pivot={pivot} data-id="element-10482" />}
      {active === 'longitudinal' && <LongitudinalSlopeView pivot={pivot} data-id="element-10483" />}
      {active === 'structural-absorption' && <StructuralAbsorptionView pivot={pivot} data-id="element-10484" />}
    </>;
}
// Shared verdict badge
function VerdictBadge({
  level
}: {
  level: 'safe' | 'warning' | 'exceeded';
}) {
  const cfg = {
    safe: {
      bg: 'bg-[#34D399]/15',
      border: 'border-[#34D399]/40',
      text: 'text-[#34D399]',
      label: 'Safe',
      icon: <ShieldCheckIcon size={11} data-id="element-10485" />
    },
    warning: {
      bg: 'bg-[#FBBF24]/15',
      border: 'border-[#FBBF24]/40',
      text: 'text-[#FBBF24]',
      label: 'Warning',
      icon: <AlertTriangleIcon size={11} data-id="element-10486" />
    },
    exceeded: {
      bg: 'bg-[#F87171]/15',
      border: 'border-[#F87171]/40',
      text: 'text-[#F87171]',
      label: 'Exceeded',
      icon: <AlertTriangleIcon size={11} data-id="element-10487" />
    }
  }[level];
  return <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide ${cfg.bg} ${cfg.border} ${cfg.text} border`} data-id="element-10488">
      {cfg.icon}
      {cfg.label}
    </span>;
}
// Stat tile with optional verdict color
function SlopeStat({
  label,
  value,
  tone
}: {
  label: string;
  value: string;
  tone?: 'neutral' | 'good' | 'warn' | 'bad' | 'accent';
}) {
  const color = tone === 'good' ? 'text-[#34D399]' : tone === 'warn' ? 'text-[#FBBF24]' : tone === 'bad' ? 'text-[#F87171]' : tone === 'accent' ? 'text-[#00D4FF]' : 'text-[#F1F5FB]';
  return <div className="px-2 py-1.5 rounded bg-[#132038] border border-[#2F4268]/60" data-id="element-10489">
      <div className="text-[8px] text-[#8A9BBA] uppercase tracking-wide font-semibold leading-tight" data-id="element-10490">
        {label}
      </div>
      <div className={`text-[12px] font-mono font-semibold mt-0.5 ${color}`} data-id="element-10491">
        {value}
      </div>
    </div>;
}
// =====================
// 1. Crop Clearance
// =====================
// Mid-span sag between supporting towers (meters). Each span is anchored at the
// terrain height of its two towers and dips by SAG_M at mid-span.
const SAG_M = 0.35;
// Compute the segmented structure-underside elevation at sample point `t` along
// the selected radial. The envelope is anchored at every tower at
// `terrain_at_tower + structHeightM` and dips toward mid-span — i.e. it follows
// the real machine geometry, not a flat reference line.
function segmentedStructureUnderside(pivot: Pivot, angleRad: number, structHeightM: number, t: number): number {
  const n = Math.max(1, pivot.towers);
  const idx = Math.min(n - 1, Math.floor(t * n));
  const tA = idx / n;
  const tB = (idx + 1) / n;
  const localT = (t - tA) / Math.max(0.0001, tB - tA);
  const cosA = Math.cos(angleRad);
  const sinA = Math.sin(angleRad);
  const elevAtFrac = (frac: number) => samplePivotElevation(pivot, 0.5 + cosA * frac * 0.5, 0.5 + sinA * frac * 0.5);
  const elevA = elevAtFrac(tA);
  const elevB = elevAtFrac(tB);
  const underA = elevA + structHeightM;
  const underB = elevB + structHeightM;
  const baseLine = underA + (underB - underA) * localT;
  const sag = Math.sin(Math.PI * localT) * SAG_M;
  return baseLine - sag;
}
function CropClearanceView({
  pivot,
  config,
  onConfigChange,
  radialProfileConfig,
  onRadialProfileConfigChange,
  samplingDistance
}: {
  pivot: Pivot;
  config: {
    structHeightM: number;
    warnAtM: number;
    cropHeightM: number;
    cropName: string;
  };
  onConfigChange: (c: {
    structHeightM: number;
    warnAtM: number;
    cropHeightM: number;
    cropName: string;
  }) => void;
  radialProfileConfig: {
    angleStep: number;
    selectedIndex: number;
  };
  onRadialProfileConfigChange: (c: {
    angleStep: number;
    selectedIndex: number;
  }) => void;
  samplingDistance: number;
}) {
  const [cropPickerOpen, setCropPickerOpen] = useState(false);
  const STRUCT_HEIGHT = config.structHeightM;
  const CROP_HEIGHT_M = config.cropHeightM;
  const WARN_M = config.warnAtM;
  const EXCEED_M = Math.max(0.1, WARN_M * 0.5);
  const n = Math.max(1, pivot.towers);
  // Radial direction — same logic as Radial Profiles
  const radialCount = Math.max(1, Math.floor(360 / radialProfileConfig.angleStep));
  const selIdx = radialProfileConfig.selectedIndex % radialCount;
  const selectedAngleDeg = selIdx * radialProfileConfig.angleStep;
  const selectedAngleRad = selectedAngleDeg * Math.PI / 180;
  const SAMPLES = radialSampleCount(pivot, samplingDistance);
  const profile = buildRadialProfile(pivot, selectedAngleRad, SAMPLES);
  const ANGLE_OPTIONS = [5, 10, 15, 30];
  // For each sample compute terrain elevation, segmented structure underside,
  // crop top and clearance. Structure follows the tower-to-tower envelope, not a
  // flat global line.
  const samples = profile.points.map(p => {
    const structureUnderside = segmentedStructureUnderside(pivot, selectedAngleRad, STRUCT_HEIGHT, p.t);
    const cropTop = p.elev + CROP_HEIGHT_M;
    const groundClearance = structureUnderside - p.elev;
    const cropClearance = structureUnderside - cropTop;
    return {
      t: p.t,
      d: p.d,
      elev: p.elev,
      structureUnderside,
      cropTop,
      groundClearance,
      cropClearance
    };
  });
  const minGround = samples.reduce((m, s) => s.groundClearance < m.groundClearance ? s : m, samples[0]);
  const minCrop = samples.reduce((m, s) => s.cropClearance < m.cropClearance ? s : m, samples[0]);
  const verdict = classifySlope(Math.max(0, WARN_M - minCrop.cropClearance), 0.01, WARN_M - EXCEED_M);
  // Critical zones: contiguous ranges where cropClearance < WARN_M
  const zones: {
    from: number;
    to: number;
    min: number;
  }[] = [];
  let cur: {
    from: number;
    to: number;
    min: number;
  } | null = null;
  for (const s of samples) {
    if (s.cropClearance < WARN_M) {
      if (!cur) cur = {
        from: s.d,
        to: s.d,
        min: s.cropClearance
      };else {
        cur.to = s.d;
        cur.min = Math.min(cur.min, s.cropClearance);
      }
    } else if (cur) {
      zones.push(cur);
      cur = null;
    }
  }
  if (cur) zones.push(cur);
  return <>
      <CollapsibleSection label="Radial Direction" data-id="element-10492">
        <div className="px-3 pt-1 pb-1" data-id="element-10493">
          <p className="text-[9px] text-[#8A9BBA] leading-relaxed" data-id="element-10494">
            Clearance is evaluated from the pivot center to the last tower along
            the selected radial.
          </p>
        </div>
        <div className="px-3 pt-1 pb-2" data-id="element-10495">
          <div className="grid grid-cols-4 gap-1 mb-2" data-id="element-10496">
            {ANGLE_OPTIONS.map(deg => {
            const isActive = radialProfileConfig.angleStep === deg;
            return <button key={deg} onClick={() => onRadialProfileConfigChange({
              angleStep: deg,
              selectedIndex: 0
            })} className={`px-1 py-1.5 rounded border text-[10px] font-mono font-semibold transition-colors ${isActive ? 'border-[#00D4FF] bg-[#1C2B47] text-[#F1F5FB]' : 'border-[#2F4268] hover:border-[#4A5F8C] bg-[#132038] text-[#C5D1E4]'}`} data-id="element-10497">
                  {deg}°
                </button>;
          })}
          </div>
          <div className="flex items-center gap-1.5" data-id="element-10498">
            <button onClick={() => onRadialProfileConfigChange({
            ...radialProfileConfig,
            selectedIndex: (selIdx - 1 + radialCount) % radialCount
          })} className="p-1 rounded text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5 border border-[#2F4268] transition-colors" title="Previous radial" data-id="element-10499">
              <ChevronRightIcon size={12} className="rotate-180" data-id="element-10500" />
            </button>
            <div className="flex-1 px-2 py-1 rounded bg-[#1C2B47] border border-[#00D4FF]/40 text-center" data-id="element-10501">
              <span className="text-[10px] text-[#8A9BBA]" data-id="element-10502">Angle</span>
              <div className="text-[14px] text-[#F1F5FB] font-mono font-bold leading-tight" data-id="element-10503">
                {selectedAngleDeg.toFixed(0)}°
              </div>
            </div>
            <button onClick={() => onRadialProfileConfigChange({
            ...radialProfileConfig,
            selectedIndex: (selIdx + 1) % radialCount
          })} className="p-1 rounded text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5 border border-[#2F4268] transition-colors" title="Next radial" data-id="element-10504">
              <ChevronRightIcon size={12} data-id="element-10505" />
            </button>
          </div>
          <p className="text-[9px] text-[#8A9BBA] mt-2 leading-relaxed" data-id="element-10506">
            Radial {selIdx + 1} of {radialCount} · {SAMPLES + 1} sample points (
            {samplingDistance} m spacing)
          </p>
        </div>
      </CollapsibleSection>
      <CollapsibleSection label="Configuration" data-id="element-10507">
        <div className="px-3 pt-1 pb-1" data-id="element-10508">
          <p className="text-[9px] text-[#8A9BBA] leading-relaxed" data-id="element-10509">
            Edit the structural and agronomic inputs. The clearance curve
            recomputes immediately.
          </p>
        </div>
        <div className="px-3 pt-1 pb-2 space-y-1.5" data-id="element-10510">
          {/* Structure height */}
          <NumberInputRow label="Structure height" value={STRUCT_HEIGHT} unit="m" min={1.5} max={6} step={0.1} tone="accent" onChange={v => onConfigChange({
          ...config,
          structHeightM: v
        })} help="Span underside height above the ground at each tower." data-id="element-10511" />
          {/* Crop top — clickable to open modal */}
          <button onClick={() => setCropPickerOpen(true)} className="w-full px-2 py-1.5 rounded bg-[#132038] border border-[#2F4268] hover:border-[#00D4FF] hover:bg-[#1C2B47] transition-colors text-left" title="Pick a crop or set a custom height" data-id="element-10512">
            <div className="flex items-center justify-between" data-id="element-10513">
              <div className="flex items-center gap-1.5 min-w-0" data-id="element-10514">
                <div className="w-2 h-2 rounded-full bg-[#34D399] flex-shrink-0" data-id="element-10515" />
                <span className="text-[8px] text-[#8A9BBA] uppercase tracking-wide font-semibold" data-id="element-10516">
                  Crop top
                </span>
              </div>
              <ChevronRightIcon size={11} className="text-[#8A9BBA]" data-id="element-10517" />
            </div>
            <div className="flex items-baseline justify-between mt-1" data-id="element-10518">
              <span className="text-[11px] text-[#F1F5FB] truncate" data-id="element-10519">
                {config.cropName}
              </span>
              <span className="text-[12px] text-[#34D399] font-mono font-semibold" data-id="element-10520">
                {CROP_HEIGHT_M.toFixed(2)} m
              </span>
            </div>
          </button>
          <NumberInputRow label="Warn at" value={WARN_M} unit="m" min={0.1} max={2} step={0.05} tone="warn" onChange={v => onConfigChange({
          ...config,
          warnAtM: v
        })} help="Clearance below this value triggers a warning. Half this value triggers an exceeded state." data-id="element-10521" />
        </div>
      </CollapsibleSection>
      <CollapsibleSection label="Critical Points" data-id="element-10522">
        <div className="px-3 py-2 grid grid-cols-2 gap-1.5" data-id="element-10523">
          <SlopeStat label="Min ground clr." value={`${minGround.groundClearance.toFixed(2)} m`} tone={minGround.groundClearance < WARN_M ? 'bad' : 'good'} data-id="element-10524" />
          <SlopeStat label="Min crop clr." value={`${minCrop.cropClearance.toFixed(2)} m`} tone={minCrop.cropClearance < EXCEED_M ? 'bad' : minCrop.cropClearance < WARN_M ? 'warn' : 'good'} data-id="element-10525" />
          <SlopeStat label="At distance" value={`${minCrop.d.toFixed(0)} m`} tone="accent" data-id="element-10526" />
          <SlopeStat label="Crit. zones" value={`${zones.length}`} data-id="element-10527" />
        </div>
      </CollapsibleSection>
      <CollapsibleSection label="Critical Zones" data-id="element-10528">
        <div className="px-3 py-2 space-y-1" data-id="element-10529">
          {zones.length === 0 ? <p className="text-[10px] text-[#34D399] flex items-center gap-1" data-id="element-10530">
              <ShieldCheckIcon size={12} data-id="element-10531" />
              No critical clearance zones detected.
            </p> : zones.map((z, i) => <div key={i} className="px-2 py-1.5 rounded bg-[#132038] border border-[#F87171]/40 flex items-center justify-between" data-id="element-10532">
                <div data-id="element-10533">
                  <div className="text-[10px] text-[#F1F5FB] font-mono" data-id="element-10534">
                    {z.from.toFixed(0)} – {z.to.toFixed(0)} m
                  </div>
                  <div className="text-[9px] text-[#8A9BBA]" data-id="element-10535">
                    Min crop clearance {z.min.toFixed(2)} m
                  </div>
                </div>
                <VerdictBadge level={z.min < EXCEED_M ? 'exceeded' : 'warning'} data-id="element-10536" />
              </div>)}
        </div>
      </CollapsibleSection>
      <SectionVerdict verdict={verdict} data-id="element-10537" />
      {cropPickerOpen && <CropPickerModal currentName={config.cropName} currentHeightM={config.cropHeightM} onClose={() => setCropPickerOpen(false)} onSelect={(name, h) => {
      onConfigChange({
        ...config,
        cropName: name,
        cropHeightM: h
      });
      setCropPickerOpen(false);
    }} data-id="element-10538" />}
    </>;
}
// Editable number row used in the Crop Clearance config card.
function NumberInputRow({
  label,
  value,
  unit,
  min,
  max,
  step,
  onChange,
  tone,
  help
}: {
  label: string;
  value: number;
  unit: string;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  tone?: 'accent' | 'warn';
  help?: string;
}) {
  const color = tone === 'accent' ? 'text-[#00D4FF]' : tone === 'warn' ? 'text-[#FBBF24]' : 'text-[#F1F5FB]';
  return <div className="px-2 py-1.5 rounded bg-[#132038] border border-[#2F4268]" data-id="element-10539">
      <div className="flex items-center justify-between gap-2" data-id="element-10540">
        <span className="text-[8px] text-[#8A9BBA] uppercase tracking-wide font-semibold" data-id="element-10541">
          {label}
        </span>
        <div className="flex items-center gap-1" data-id="element-10542">
          <button onClick={() => onChange(Math.max(min, Math.round((value - step) * 100) / 100))} className="w-5 h-5 rounded text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5 border border-[#2F4268] flex items-center justify-center transition-colors" title={`Decrease by ${step}`} data-id="element-10543">
            <MinusIcon size={10} data-id="element-10544" />
          </button>
          <input type="number" value={value} min={min} max={max} step={step} onChange={e => {
          const v = parseFloat(e.target.value);
          if (!isNaN(v)) onChange(Math.max(min, Math.min(max, v)));
        }} className={`w-14 text-center text-[12px] font-mono font-semibold bg-[#0a1322] border border-[#2F4268] rounded px-1 py-0.5 ${color} focus:outline-none focus:border-[#00D4FF]`} data-id="element-10545" />
          <span className="text-[10px] text-[#8A9BBA] font-mono" data-id="element-10546">{unit}</span>
          <button onClick={() => onChange(Math.min(max, Math.round((value + step) * 100) / 100))} className="w-5 h-5 rounded text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5 border border-[#2F4268] flex items-center justify-center transition-colors" title={`Increase by ${step}`} data-id="element-10547">
            <PlusIcon size={10} data-id="element-10548" />
          </button>
        </div>
      </div>
      {help && <p className="text-[9px] text-[#8A9BBA] mt-1 leading-relaxed" data-id="element-10549">{help}</p>}
    </div>;
}
function CropPickerModal({
  currentName,
  currentHeightM,
  onClose,
  onSelect
}: {
  currentName: string;
  currentHeightM: number;
  onClose: () => void;
  onSelect: (name: string, heightM: number) => void;
}) {
  const [filter, setFilter] = useState('');
  const grouped = CROP_OPTIONS.filter(c => c.name.toLowerCase().includes(filter.toLowerCase())).reduce((acc, c) => {
    ;
    (acc[c.group] = acc[c.group] || []).push(c);
    return acc;
  }, {} as Record<string, typeof CROP_OPTIONS>);
  return <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-id="element-10550">
      <div className="bg-[#0b1628] border border-[#2F4268] rounded-lg w-full max-w-[520px] max-h-[80vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-10551">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#2F4268]" data-id="element-10552">
          <div className="flex items-center gap-2" data-id="element-10553">
            <div className="w-2 h-2 rounded-full bg-[#34D399]" data-id="element-10554" />
            <h3 className="text-[13px] font-semibold text-[#F1F5FB]" data-id="element-10555">
              Select Crop
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded text-[#8A9BBA] hover:text-[#F87171] hover:bg-white/5 transition-colors" data-id="element-10556">
            <XIcon size={14} data-id="element-10557" />
          </button>
        </div>
        <div className="px-4 py-2 border-b border-[#2F4268]" data-id="element-10558">
          <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder="Search crops…" className="w-full text-[11px] bg-[#0a1322] border border-[#2F4268] rounded px-2 py-1.5 text-[#F1F5FB] placeholder:text-[#8A9BBA] focus:outline-none focus:border-[#00D4FF]" autoFocus data-id="element-10559" />
        </div>
        <div className="flex-1 overflow-y-auto p-2" data-id="element-10560">
          {Object.entries(grouped).map(([group, items]) => <div key={group} className="mb-3" data-id="element-10561">
              <div className="px-2 py-1 text-[9px] text-[#8A9BBA] uppercase tracking-wide font-semibold" data-id="element-10562">
                {group}
              </div>
              <div className="space-y-0.5" data-id="element-10563">
                {items.map(c => {
              const isSel = c.name === currentName;
              return <button key={c.name} onClick={() => onSelect(c.name, c.heightM)} className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-left transition-colors ${isSel ? 'bg-[#00D4FF]/15 border border-[#00D4FF]/40' : 'border border-transparent hover:bg-white/5'}`} data-id="element-10564">
                      <span className="text-[11px] text-[#F1F5FB]" data-id="element-10565">
                        {c.name}
                      </span>
                      <span className="text-[11px] text-[#34D399] font-mono font-semibold" data-id="element-10566">
                        {c.heightM.toFixed(2)} m
                      </span>
                    </button>;
            })}
              </div>
            </div>)}
          {Object.keys(grouped).length === 0 && <p className="text-[10px] text-[#8A9BBA] px-2 py-4 text-center" data-id="element-10567">
              No crops match "{filter}"
            </p>}
        </div>
        <div className="px-4 py-2 border-t border-[#2F4268] text-[9px] text-[#8A9BBA] italic" data-id="element-10568">
          Current: {currentName} ({currentHeightM.toFixed(2)} m)
        </div>
      </div>
    </div>;
}
// =====================
// 2. Transverse Slope
// =====================
function TransverseSlopeView({
  pivot,
  towerIndex,
  onTowerIndexChange
}: {
  pivot: Pivot;
  towerIndex: number;
  onTowerIndexChange: (i: number) => void;
}) {
  const n = Math.max(1, pivot.towers);
  const sel = Math.max(1, Math.min(n, towerIndex));
  const radius = towerRadius(pivot, sel);
  const SAMPLES = 180;
  const WARN_PCT = 8;
  const EXCEED_PCT = 15;
  // Transverse slope = elevation change perpendicular to motion at each angle.
  // For tower path, motion is tangential; perpendicular is the radial direction.
  // We estimate by sampling elevation at two points along the radius at each angle.
  const sampleAt = (u: number, v: number) => samplePivotElevation(pivot, u, v);
  const points: {
    angle: number;
    slopePct: number;
  }[] = [];
  for (let i = 0; i <= SAMPLES; i++) {
    const t = i / SAMPLES;
    const angle = t * 360;
    const aRad = angle * Math.PI / 180;
    // Sample 1m inside and 1m outside the wheel path
    const ringR1 = Math.max(0, radius - 1);
    const ringR2 = Math.min(pivot.radiusPx, radius + 1);
    const u1 = 0.5 + Math.cos(aRad) * ringR1 / pivot.radiusPx / 2 * 2;
    const v1 = 0.5 + Math.sin(aRad) * ringR1 / pivot.radiusPx / 2 * 2;
    const u2 = 0.5 + Math.cos(aRad) * ringR2 / pivot.radiusPx / 2 * 2;
    const v2 = 0.5 + Math.sin(aRad) * ringR2 / pivot.radiusPx / 2 * 2;
    // Convert to unit-square (radius 0.5 = pivot.radiusPx)
    const u1c = 0.5 + Math.cos(aRad) * ringR1 / pivot.radiusPx * 0.5;
    const v1c = 0.5 + Math.sin(aRad) * ringR1 / pivot.radiusPx * 0.5;
    const u2c = 0.5 + Math.cos(aRad) * ringR2 / pivot.radiusPx * 0.5;
    const v2c = 0.5 + Math.sin(aRad) * ringR2 / pivot.radiusPx * 0.5;
    const e1 = sampleAt(u1c, v1c);
    const e2 = sampleAt(u2c, v2c);
    const dx = ringR2 - ringR1;
    const slopePct = dx > 0 ? (e2 - e1) / dx * 100 : 0;
    points.push({
      angle,
      slopePct
    });
  }
  let maxAbs = 0;
  let maxAt = 0;
  let signedExtreme = 0;
  for (const p of points) {
    if (Math.abs(p.slopePct) > maxAbs) {
      maxAbs = Math.abs(p.slopePct);
      signedExtreme = p.slopePct;
      maxAt = p.angle;
    }
  }
  const avgAbs = points.reduce((s, p) => s + Math.abs(p.slopePct), 0) / points.length;
  const verdict = classifySlope(maxAbs, WARN_PCT, EXCEED_PCT);
  // Critical angle ranges where |slope| > WARN_PCT
  const zones: {
    from: number;
    to: number;
    max: number;
  }[] = [];
  let cur: {
    from: number;
    to: number;
    max: number;
  } | null = null;
  for (const p of points) {
    if (Math.abs(p.slopePct) > WARN_PCT) {
      if (!cur) cur = {
        from: p.angle,
        to: p.angle,
        max: Math.abs(p.slopePct)
      };else {
        cur.to = p.angle;
        cur.max = Math.max(cur.max, Math.abs(p.slopePct));
      }
    } else if (cur) {
      zones.push(cur);
      cur = null;
    }
  }
  if (cur) zones.push(cur);
  return <>
      <CollapsibleSection label="Selected Tower" data-id="element-10569">
        <div className="px-3 pt-1 pb-2" data-id="element-10570">
          <div className="grid grid-cols-7 gap-1 mb-2" data-id="element-10571">
            {Array.from({
            length: n
          }).map((_, i) => {
            const isActive = i + 1 === sel;
            return <button key={i} onClick={() => onTowerIndexChange(i + 1)} className={`px-1 py-1 rounded text-[10px] font-mono font-semibold transition-colors border ${isActive ? 'border-[#00D4FF] bg-[#1C2B47] text-[#F1F5FB]' : 'border-[#2F4268] hover:border-[#4A5F8C] bg-[#132038] text-[#C5D1E4]'}`} data-id="element-10572">
                  T{i + 1}
                </button>;
          })}
          </div>
          <SlopeStat label="Wheel-path radius" value={`${radius.toFixed(1)} m`} tone="accent" data-id="element-10573" />
        </div>
      </CollapsibleSection>
      <CollapsibleSection label="Side-Slope Readings" data-id="element-10574">
        <div className="px-3 py-2 grid grid-cols-3 gap-1.5" data-id="element-10575">
          <SlopeStat label="Max |slope|" value={`${maxAbs.toFixed(1)}%`} tone={maxAbs >= EXCEED_PCT ? 'bad' : maxAbs >= WARN_PCT ? 'warn' : 'good'} data-id="element-10576" />
          <SlopeStat label="At angle" value={`${maxAt.toFixed(0)}°`} tone="accent" data-id="element-10577" />
          <SlopeStat label="Avg |slope|" value={`${avgAbs.toFixed(1)}%`} data-id="element-10578" />
        </div>
        <div className="px-3 pb-2 text-[9px] text-[#8A9BBA] leading-relaxed" data-id="element-10579">
          {signedExtreme >= 0 ? 'Outer wheel runs higher than inner — load shifts to inner wheel.' : 'Inner wheel runs higher than outer — load shifts to outer wheel.'}
        </div>
      </CollapsibleSection>
      <CollapsibleSection label="Critical Angle Ranges" data-id="element-10580">
        <div className="px-3 py-2 space-y-1" data-id="element-10581">
          {zones.length === 0 ? <p className="text-[10px] text-[#34D399] flex items-center gap-1" data-id="element-10582">
              <ShieldCheckIcon size={12} data-id="element-10583" />
              No side-slope warnings around the wheel path.
            </p> : zones.map((z, i) => <div key={i} className="px-2 py-1.5 rounded bg-[#132038] border border-[#FBBF24]/40 flex items-center justify-between" data-id="element-10584">
                <div data-id="element-10585">
                  <div className="text-[10px] text-[#F1F5FB] font-mono" data-id="element-10586">
                    {z.from.toFixed(0)}° – {z.to.toFixed(0)}°
                  </div>
                  <div className="text-[9px] text-[#8A9BBA]" data-id="element-10587">
                    Peak side slope {z.max.toFixed(1)}%
                  </div>
                </div>
                <VerdictBadge level={z.max >= EXCEED_PCT ? 'exceeded' : 'warning'} data-id="element-10588" />
              </div>)}
        </div>
      </CollapsibleSection>
      <SectionVerdict verdict={verdict} data-id="element-10589" />
    </>;
}
// =====================
// 3. Torsional Slope
// =====================
function TorsionalSlopeView({
  pivot
}: {
  pivot: Pivot;
}) {
  const n = Math.max(1, pivot.towers);
  const WARN_DELTA = 4; // % combined opposing slope
  const EXCEED_DELTA = 8;
  const SAMPLES = 120;
  // For each span (between consecutive tower paths), find the worst case where the
  // inner and outer paths run on opposing slopes (one ascending while the other descends).
  type Span = {
    spanIndex: number;
    innerR: number;
    outerR: number;
    worstAngle: number;
    innerSlope: number;
    outerSlope: number;
    delta: number; // magnitude of opposition (|inner - outer| when they have opposite signs)
  };
  const spans: Span[] = [];
  for (let s = 0; s < n; s++) {
    const innerR = s === 0 ? 0 : s / n * pivot.radiusPx;
    const outerR = (s + 1) / n * pivot.radiusPx;
    let worstDelta = 0;
    let worstAngle = 0;
    let innerAt = 0;
    let outerAt = 0;
    for (let i = 0; i < SAMPLES; i++) {
      const angle = i / SAMPLES * 360;
      const aRad = angle * Math.PI / 180;
      // Tangential slope at innerR and outerR: small angular delta along arc
      const dAng = 1 * (Math.PI / 180); // 1°
      const sampleArc = (r: number) => {
        const u1 = 0.5 + Math.cos(aRad - dAng) * r / pivot.radiusPx * 0.5;
        const v1 = 0.5 + Math.sin(aRad - dAng) * r / pivot.radiusPx * 0.5;
        const u2 = 0.5 + Math.cos(aRad + dAng) * r / pivot.radiusPx * 0.5;
        const v2 = 0.5 + Math.sin(aRad + dAng) * r / pivot.radiusPx * 0.5;
        const e1 = samplePivotElevation(pivot, u1, v1);
        const e2 = samplePivotElevation(pivot, u2, v2);
        const arcLen = 2 * dAng * r || 1;
        return (e2 - e1) / arcLen * 100;
      };
      const innerSlope = innerR > 0 ? sampleArc(innerR) : 0;
      const outerSlope = sampleArc(outerR);
      // Opposition: one positive, one negative
      if (innerSlope * outerSlope < 0) {
        const delta = Math.abs(innerSlope) + Math.abs(outerSlope);
        if (delta > worstDelta) {
          worstDelta = delta;
          worstAngle = angle;
          innerAt = innerSlope;
          outerAt = outerSlope;
        }
      }
    }
    spans.push({
      spanIndex: s + 1,
      innerR,
      outerR,
      worstAngle,
      innerSlope: innerAt,
      outerSlope: outerAt,
      delta: worstDelta
    });
  }
  const worst = spans.reduce((m, s) => s.delta > m.delta ? s : m, spans[0]);
  const verdict = classifySlope(worst.delta, WARN_DELTA, EXCEED_DELTA);
  return <>
      <CollapsibleSection label="Worst Torsion Span" data-id="element-10590">
        <div className="px-3 py-2 grid grid-cols-3 gap-1.5" data-id="element-10591">
          <SlopeStat label="Span" value={`#${worst.spanIndex}`} tone="accent" data-id="element-10592" />
          <SlopeStat label="At angle" value={`${worst.worstAngle.toFixed(0)}°`} tone="accent" data-id="element-10593" />
          <SlopeStat label="Δ Opposing" value={`${worst.delta.toFixed(1)}%`} tone={worst.delta >= EXCEED_DELTA ? 'bad' : worst.delta >= WARN_DELTA ? 'warn' : 'good'} data-id="element-10594" />
          <SlopeStat label="Inner slope" value={`${worst.innerSlope >= 0 ? '+' : ''}${worst.innerSlope.toFixed(1)}%`} tone={worst.innerSlope >= 0 ? 'good' : 'bad'} data-id="element-10595" />
          <SlopeStat label="Outer slope" value={`${worst.outerSlope >= 0 ? '+' : ''}${worst.outerSlope.toFixed(1)}%`} tone={worst.outerSlope >= 0 ? 'good' : 'bad'} data-id="element-10596" />
          <SlopeStat label="Spans evaluated" value={`${spans.length}`} data-id="element-10597" />
        </div>
        <div className="px-3 pb-2 text-[9px] text-[#8A9BBA] leading-relaxed italic" data-id="element-10598">
          A high Δ indicates that the inner and outer wheel paths are on
          opposing slopes — the span experiences twisting stress.
        </div>
      </CollapsibleSection>
      <CollapsibleSection label="Per-Span Torsion" data-id="element-10599">
        <div className="px-3 py-2 space-y-1 max-h-[260px] overflow-y-auto" data-id="element-10600">
          {spans.map(s => {
          const lvl = classifySlope(s.delta, WARN_DELTA, EXCEED_DELTA);
          return <div key={s.spanIndex} className={`px-2 py-1.5 rounded bg-[#132038] border flex items-center justify-between ${lvl === 'exceeded' ? 'border-[#F87171]/40' : lvl === 'warning' ? 'border-[#FBBF24]/40' : 'border-[#2F4268]/60'}`} data-id="element-10601">
                <div data-id="element-10602">
                  <div className="text-[10px] text-[#F1F5FB] font-mono" data-id="element-10603">
                    Span {s.spanIndex}
                  </div>
                  <div className="text-[9px] text-[#8A9BBA]" data-id="element-10604">
                    {s.delta > 0 ? `Worst at ${s.worstAngle.toFixed(0)}°` : 'No opposing slopes detected'}
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-10605">
                  <span className="text-[10px] font-mono text-[#C5D1E4]" data-id="element-10606">
                    {s.delta.toFixed(1)}%
                  </span>
                  <VerdictBadge level={lvl} data-id="element-10607" />
                </div>
              </div>;
        })}
        </div>
      </CollapsibleSection>
      <SectionVerdict verdict={verdict} data-id="element-10608" />
    </>;
}
// =====================
// 4. Longitudinal Slope
// =====================
function LongitudinalSlopeView({
  pivot
}: {
  pivot: Pivot;
}) {
  const n = Math.max(1, pivot.towers);
  const WARN_PCT = 10;
  const EXCEED_PCT = 15;
  // For each consecutive pair of towers along radius 0° (the arm direction),
  // measure the slope between their elevations.
  const angle = 0;
  const radii = [0, ...Array.from({
    length: n
  }).map((_, i) => (i + 1) / n * pivot.radiusPx)];
  const segments: {
    from: number;
    to: number;
    slopePct: number;
    elevDelta: number;
    kind: 'ascent' | 'descent' | 'flat';
  }[] = [];
  for (let i = 0; i < radii.length - 1; i++) {
    const r1 = radii[i];
    const r2 = radii[i + 1];
    const aRad = angle * Math.PI / 180;
    const u1 = 0.5 + Math.cos(aRad) * r1 / pivot.radiusPx * 0.5;
    const v1 = 0.5 + Math.sin(aRad) * r1 / pivot.radiusPx * 0.5;
    const u2 = 0.5 + Math.cos(aRad) * r2 / pivot.radiusPx * 0.5;
    const v2 = 0.5 + Math.sin(aRad) * r2 / pivot.radiusPx * 0.5;
    const e1 = samplePivotElevation(pivot, u1, v1);
    const e2 = samplePivotElevation(pivot, u2, v2);
    const dx = r2 - r1 || 1;
    const slope = (e2 - e1) / dx * 100;
    segments.push({
      from: r1,
      to: r2,
      slopePct: slope,
      elevDelta: e2 - e1,
      kind: Math.abs(slope) < 0.5 ? 'flat' : slope > 0 ? 'ascent' : 'descent'
    });
  }
  let maxAscent = 0;
  let maxDescent = 0;
  let worstAbs = 0;
  for (const s of segments) {
    if (s.slopePct > maxAscent) maxAscent = s.slopePct;
    if (s.slopePct < maxDescent) maxDescent = s.slopePct;
    if (Math.abs(s.slopePct) > worstAbs) worstAbs = Math.abs(s.slopePct);
  }
  const verdict = classifySlope(worstAbs, WARN_PCT, EXCEED_PCT);
  return <>
      <CollapsibleSection label="Direction" data-id="element-10609">
        <div className="px-3 py-2 text-[10px] text-[#C5D1E4] leading-relaxed" data-id="element-10610">
          Measured along the arm at{' '}
          <span className="text-[#00D4FF] font-mono font-semibold" data-id="element-10611">0°</span>,
          between each consecutive tower support.
        </div>
      </CollapsibleSection>
      <CollapsibleSection label="Summary" data-id="element-10612">
        <div className="px-3 py-2 grid grid-cols-3 gap-1.5" data-id="element-10613">
          <SlopeStat label="Max ascent" value={`+${maxAscent.toFixed(1)}%`} tone={maxAscent >= EXCEED_PCT ? 'bad' : maxAscent >= WARN_PCT ? 'warn' : 'good'} data-id="element-10614" />
          <SlopeStat label="Max descent" value={`${maxDescent.toFixed(1)}%`} tone={Math.abs(maxDescent) >= EXCEED_PCT ? 'bad' : Math.abs(maxDescent) >= WARN_PCT ? 'warn' : 'good'} data-id="element-10615" />
          <SlopeStat label="Segments" value={`${segments.length}`} data-id="element-10616" />
        </div>
      </CollapsibleSection>
      <CollapsibleSection label="Per-Segment Slope" data-id="element-10617">
        <div className="px-3 py-2 space-y-1 max-h-[260px] overflow-y-auto" data-id="element-10618">
          {segments.map((s, i) => {
          const lvl = classifySlope(Math.abs(s.slopePct), WARN_PCT, EXCEED_PCT);
          return <div key={i} className={`px-2 py-1.5 rounded bg-[#132038] border flex items-center justify-between ${lvl === 'exceeded' ? 'border-[#F87171]/40' : lvl === 'warning' ? 'border-[#FBBF24]/40' : 'border-[#2F4268]/60'}`} data-id="element-10619">
                <div data-id="element-10620">
                  <div className="text-[10px] text-[#F1F5FB] font-mono" data-id="element-10621">
                    {s.from.toFixed(0)} – {s.to.toFixed(0)} m
                  </div>
                  <div className="text-[9px] text-[#8A9BBA] flex items-center gap-1" data-id="element-10622">
                    {s.kind === 'ascent' ? <ArrowUpIcon size={9} className="text-[#34D399]" data-id="element-10623" /> : s.kind === 'descent' ? <ArrowDownIcon size={9} className="text-[#F87171]" data-id="element-10624" /> : <MinusIcon size={9} data-id="element-10625" />}
                    Δ {s.elevDelta >= 0 ? '+' : ''}
                    {s.elevDelta.toFixed(1)} m
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-10626">
                  <span className={`text-[10px] font-mono font-semibold ${s.slopePct > 0 ? 'text-[#34D399]' : s.slopePct < 0 ? 'text-[#F87171]' : 'text-[#C5D1E4]'}`} data-id="element-10627">
                    {s.slopePct >= 0 ? '+' : ''}
                    {s.slopePct.toFixed(1)}%
                  </span>
                  <VerdictBadge level={lvl} data-id="element-10628" />
                </div>
              </div>;
        })}
        </div>
      </CollapsibleSection>
      <SectionVerdict verdict={verdict} data-id="element-10629" />
    </>;
}
// =====================
// 5. Structural Absorption
// =====================
function StructuralAbsorptionView({
  pivot
}: {
  pivot: Pivot;
}) {
  const n = Math.max(1, pivot.towers);
  const FLEX_LIMIT_DEG = 2.5; // per-joint allowable flex
  const CUMULATIVE_LIMIT_DEG = 5.0; // structure-wide allowable cumulative bend
  const WARN_RATIO = 0.7; // 70% of limit triggers warning
  // Compute per-joint angular bend = difference between successive segment slopes
  // (in degrees), measured along radius 0°.
  const angle = 0;
  const radii = [0, ...Array.from({
    length: n
  }).map((_, i) => (i + 1) / n * pivot.radiusPx)];
  const aRad = angle * Math.PI / 180;
  const elevAt = (r: number) => {
    const u = 0.5 + Math.cos(aRad) * r / pivot.radiusPx * 0.5;
    const v = 0.5 + Math.sin(aRad) * r / pivot.radiusPx * 0.5;
    return samplePivotElevation(pivot, u, v);
  };
  const segmentSlopesDeg: number[] = [];
  for (let i = 0; i < radii.length - 1; i++) {
    const dx = radii[i + 1] - radii[i] || 1;
    const dy = elevAt(radii[i + 1]) - elevAt(radii[i]);
    segmentSlopesDeg.push(Math.atan2(dy, dx) * 180 / Math.PI);
  }
  // Joint bends: difference between successive segment slopes
  const joints: {
    jointIndex: number;
    bendDeg: number;
    status: 'safe' | 'warning' | 'exceeded';
  }[] = [];
  for (let i = 0; i < segmentSlopesDeg.length - 1; i++) {
    const bend = segmentSlopesDeg[i + 1] - segmentSlopesDeg[i];
    joints.push({
      jointIndex: i + 1,
      bendDeg: bend,
      status: classifySlope(Math.abs(bend), FLEX_LIMIT_DEG * WARN_RATIO, FLEX_LIMIT_DEG)
    });
  }
  const cumulativeAbs = joints.reduce((s, j) => s + Math.abs(j.bendDeg), 0);
  const maxJoint = joints.reduce((m, j) => Math.abs(j.bendDeg) > Math.abs(m.bendDeg) ? j : m, joints[0] || {
    jointIndex: 0,
    bendDeg: 0,
    status: 'safe' as const
  });
  const cumulativeStatus = classifySlope(cumulativeAbs, CUMULATIVE_LIMIT_DEG * WARN_RATIO, CUMULATIVE_LIMIT_DEG);
  const anyExceeded = joints.some(j => j.status === 'exceeded') || cumulativeStatus === 'exceeded';
  const anyWarning = joints.some(j => j.status === 'warning') || cumulativeStatus === 'warning';
  const verdict: 'safe' | 'warning' | 'exceeded' = anyExceeded ? 'exceeded' : anyWarning ? 'warning' : 'safe';
  return <>
      <CollapsibleSection label="Limits" data-id="element-10630">
        <div className="px-3 py-2 grid grid-cols-2 gap-1.5" data-id="element-10631">
          <SlopeStat label="Joint flex limit" value={`±${FLEX_LIMIT_DEG.toFixed(1)}°`} data-id="element-10632" />
          <SlopeStat label="Cumulative limit" value={`±${CUMULATIVE_LIMIT_DEG.toFixed(1)}°`} data-id="element-10633" />
        </div>
      </CollapsibleSection>
      <CollapsibleSection label="Summary" data-id="element-10634">
        <div className="px-3 py-2 grid grid-cols-3 gap-1.5" data-id="element-10635">
          <SlopeStat label="Max joint bend" value={`${maxJoint.bendDeg >= 0 ? '+' : ''}${maxJoint.bendDeg.toFixed(2)}°`} tone={maxJoint.status === 'exceeded' ? 'bad' : maxJoint.status === 'warning' ? 'warn' : 'good'} data-id="element-10636" />
          <SlopeStat label="At joint" value={`J${maxJoint.jointIndex}`} tone="accent" data-id="element-10637" />
          <SlopeStat label="Cumulative |Δ|" value={`${cumulativeAbs.toFixed(2)}°`} tone={cumulativeStatus === 'exceeded' ? 'bad' : cumulativeStatus === 'warning' ? 'warn' : 'good'} data-id="element-10638" />
        </div>
      </CollapsibleSection>
      <CollapsibleSection label="Per-Joint Bend" data-id="element-10639">
        <div className="px-3 py-2 space-y-1 max-h-[260px] overflow-y-auto" data-id="element-10640">
          {joints.length === 0 && <p className="text-[10px] text-[#8A9BBA]" data-id="element-10641">
              Not enough segments to compute joint flex.
            </p>}
          {joints.map(j => <div key={j.jointIndex} className={`px-2 py-1.5 rounded bg-[#132038] border flex items-center justify-between ${j.status === 'exceeded' ? 'border-[#F87171]/40' : j.status === 'warning' ? 'border-[#FBBF24]/40' : 'border-[#2F4268]/60'}`} data-id="element-10642">
              <div data-id="element-10643">
                <div className="text-[10px] text-[#F1F5FB] font-mono" data-id="element-10644">
                  Joint {j.jointIndex}
                </div>
                <div className="text-[9px] text-[#8A9BBA]" data-id="element-10645">
                  Between T{j.jointIndex} and T{j.jointIndex + 1}
                </div>
              </div>
              <div className="flex items-center gap-2" data-id="element-10646">
                <span className={`text-[10px] font-mono font-semibold ${j.status === 'exceeded' ? 'text-[#F87171]' : j.status === 'warning' ? 'text-[#FBBF24]' : 'text-[#34D399]'}`} data-id="element-10647">
                  {j.bendDeg >= 0 ? '+' : ''}
                  {j.bendDeg.toFixed(2)}°
                </span>
                <VerdictBadge level={j.status} data-id="element-10648" />
              </div>
            </div>)}
        </div>
      </CollapsibleSection>
      <SectionVerdict verdict={verdict} data-id="element-10649" />
    </>;
}
// Final verdict strip at the bottom of every analysis detail view
function SectionVerdict({
  verdict
}: {
  verdict: 'safe' | 'warning' | 'exceeded';
}) {
  const cfg = {
    safe: {
      bg: 'bg-[#34D399]/10',
      border: 'border-[#34D399]/40',
      text: 'text-[#34D399]',
      title: 'Within safe range',
      msg: 'The current configuration absorbs the slopes detected in this area.',
      icon: <ShieldCheckIcon size={14} data-id="element-10650" />
    },
    warning: {
      bg: 'bg-[#FBBF24]/10',
      border: 'border-[#FBBF24]/40',
      text: 'text-[#FBBF24]',
      title: 'Attention needed',
      msg: 'Values approach the operational threshold. Review the highlighted zones.',
      icon: <AlertTriangleIcon size={14} data-id="element-10651" />
    },
    exceeded: {
      bg: 'bg-[#F87171]/10',
      border: 'border-[#F87171]/40',
      text: 'text-[#F87171]',
      title: 'Limit exceeded',
      msg: 'One or more values exceed the configuration limits. Re-evaluate machine config or terrain.',
      icon: <AlertTriangleIcon size={14} data-id="element-10652" />
    }
  }[verdict];
  return <div className={`mx-3 my-3 px-3 py-2 rounded border ${cfg.bg} ${cfg.border} flex items-start gap-2`} data-id="element-10653">
      <div className={`flex-shrink-0 ${cfg.text}`} data-id="element-10654">{cfg.icon}</div>
      <div className="flex-1 min-w-0" data-id="element-10655">
        <div className={`text-[11px] font-bold ${cfg.text} mb-0.5`} data-id="element-10656">
          {cfg.title}
        </div>
        <p className="text-[9px] text-[#C5D1E4] leading-relaxed" data-id="element-10657">{cfg.msg}</p>
      </div>
    </div>;
}
// ============================================================
// Slope Analysis chart panel — horizontal panel mirroring the
// elevation profile panel, with analysis-specific overlays on
// top of the terrain curve. Towers are placed on the curve.
// ============================================================
function SlopeAnalysisChartPanel({
  pivot,
  analysis,
  towerIndex,
  onTowerIndexChange,
  cursorT,
  onCursorChange,
  onClose,
  cropClearanceConfig,
  radialProfileConfig,
  onRadialProfileConfigChange,
  samplingDistance
}: {
  pivot: Pivot;
  analysis: SlopeAnalysisType;
  towerIndex: number;
  onTowerIndexChange: (i: number) => void;
  cursorT: number | null;
  onCursorChange: (t: number | null) => void;
  onClose: () => void;
  cropClearanceConfig: {
    structHeightM: number;
    warnAtM: number;
    cropHeightM: number;
    cropName: string;
  };
  radialProfileConfig: {
    angleStep: number;
    selectedIndex: number;
  };
  onRadialProfileConfigChange: (c: {
    angleStep: number;
    selectedIndex: number;
  }) => void;
  samplingDistance: number;
}) {
  const H = 280;
  const def = SLOPE_ANALYSES.find(d => d.id === analysis)!;
  const n = Math.max(1, pivot.towers);
  const sel = Math.max(1, Math.min(n, towerIndex));
  const isAngleBased = analysis === 'transverse' || analysis === 'torsional';
  // Selected radial angle (used by crop-clearance to evaluate along the chosen
  // direction — mirrors the Radial Profiles selection logic).
  const radialCount = Math.max(1, Math.floor(360 / radialProfileConfig.angleStep));
  const selRadialIdx = radialProfileConfig.selectedIndex % radialCount;
  const selectedRadialDeg = selRadialIdx * radialProfileConfig.angleStep;
  return <div className="bg-[#0a1322] border-t border-[#2F4268] flex-shrink-0 flex flex-col" style={{
    height: H
  }} data-id="element-10658">
      {/* Header strip */}
      <div className="flex items-center justify-between px-3 h-[30px] border-b border-[#2F4268] flex-shrink-0 bg-[#0b1628]" data-id="element-10659">
        <div className="flex items-center gap-2 min-w-0" data-id="element-10660">
          <div className="text-[#00D4FF] flex-shrink-0" data-id="element-10661">{def.icon}</div>
          <span className="text-[11px] font-semibold text-[#F1F5FB]" data-id="element-10662">
            {def.label}
          </span>
          {isAngleBased && <>
              <span className="text-[10px] text-[#8A9BBA]" data-id="element-10663">·</span>
              <span className="text-[10px] text-[#C5D1E4] font-mono" data-id="element-10664">
                Tower {sel} / {n}
              </span>
            </>}
          {analysis === 'crop-clearance' && <>
              <span className="text-[10px] text-[#8A9BBA]" data-id="element-10665">·</span>
              <span className="text-[10px] text-[#C5D1E4] font-mono" data-id="element-10666">
                Radial {selectedRadialDeg.toFixed(0)}°
              </span>
              <span className="text-[10px] text-[#8A9BBA]" data-id="element-10667">·</span>
              <span className="text-[10px] text-[#C5D1E4] font-mono" data-id="element-10668">
                Struct {cropClearanceConfig.structHeightM.toFixed(1)} m
              </span>
              <span className="text-[10px] text-[#8A9BBA]" data-id="element-10669">·</span>
              <span className="text-[10px] text-[#34D399] font-mono" data-id="element-10670">
                {cropClearanceConfig.cropName} (
                {cropClearanceConfig.cropHeightM.toFixed(2)} m)
              </span>
            </>}
        </div>
        <div className="flex items-center gap-1" data-id="element-10671">
          {isAngleBased && <>
              <button onClick={() => onTowerIndexChange((sel - 2 + n) % n + 1)} className="p-1 rounded text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5 transition-colors" title="Previous tower" data-id="element-10672">
                <ChevronRightIcon size={13} className="rotate-180" data-id="element-10673" />
              </button>
              <button onClick={() => onTowerIndexChange(sel % n + 1)} className="p-1 rounded text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5 transition-colors" title="Next tower" data-id="element-10674">
                <ChevronRightIcon size={13} data-id="element-10675" />
              </button>
              <div className="w-px h-4 bg-[#2F4268] mx-1" data-id="element-10676" />
            </>}
          {analysis === 'crop-clearance' && <>
              <button onClick={() => {
            onRadialProfileConfigChange({
              ...radialProfileConfig,
              selectedIndex: (selRadialIdx - 1 + radialCount) % radialCount
            });
            onCursorChange(null);
          }} className="p-1 rounded text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5 transition-colors" title="Previous radial" data-id="element-10677">
                <ChevronRightIcon size={13} className="rotate-180" data-id="element-10678" />
              </button>
              <button onClick={() => {
            onRadialProfileConfigChange({
              ...radialProfileConfig,
              selectedIndex: (selRadialIdx + 1) % radialCount
            });
            onCursorChange(null);
          }} className="p-1 rounded text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5 transition-colors" title="Next radial" data-id="element-10679">
                <ChevronRightIcon size={13} data-id="element-10680" />
              </button>
              <div className="w-px h-4 bg-[#2F4268] mx-1" data-id="element-10681" />
            </>}
          <button onClick={onClose} className="p-1 rounded text-[#8A9BBA] hover:text-[#F87171] hover:bg-white/5 transition-colors" title="Close chart panel" data-id="element-10682">
            <XIcon size={13} data-id="element-10683" />
          </button>
        </div>
      </div>

      {/* Chart body */}
      <div className="flex-1 min-h-0 px-3 py-2" data-id="element-10684">
        <SlopeAnalysisChart pivot={pivot} analysis={analysis} towerIndex={sel} cursorT={cursorT} onCursorChange={onCursorChange} cropClearanceConfig={cropClearanceConfig} selectedRadialDeg={selectedRadialDeg} samplingDistance={samplingDistance} data-id="element-10685" />
      </div>

      <div className="px-3 py-1 border-t border-[#2F4268]/40 text-[9px] text-[#8A9BBA] italic" data-id="element-10686">
        {isAngleBased ? 'Hover the chart to scrub the angular position around the tower wheel path. Towers ride the elevation curve at their respective radii.' : analysis === 'crop-clearance' ? `Clearance evaluated along the ${selectedRadialDeg.toFixed(0)}° radial · ${samplingDistance} m sampling · structure underside follows the span envelope between towers.` : 'Hover the chart to scrub along the pivot arm. Towers are drawn on top of the elevation curve.'}
      </div>
    </div>;
}
function SlopeAnalysisChart({
  pivot,
  analysis,
  towerIndex,
  cursorT,
  onCursorChange,
  cropClearanceConfig,
  selectedRadialDeg,
  samplingDistance
}: {
  pivot: Pivot;
  analysis: SlopeAnalysisType;
  towerIndex: number;
  cursorT: number | null;
  onCursorChange: (t: number | null) => void;
  cropClearanceConfig: {
    structHeightM: number;
    warnAtM: number;
    cropHeightM: number;
    cropName: string;
  };
  selectedRadialDeg: number;
  samplingDistance: number;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({
    w: 1000,
    h: 200
  });
  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver(entries => {
      for (const e of entries) {
        const cr = e.contentRect;
        setSize({
          w: Math.max(320, Math.floor(cr.width)),
          h: Math.max(120, Math.floor(cr.height))
        });
      }
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);
  const W = size.w;
  const H = size.h;
  const padL = 54;
  const padR = 20;
  const padT = 18;
  const padB = 26;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const n = Math.max(1, pivot.towers);
  const isAngleBased = analysis === 'transverse' || analysis === 'torsional';
  // Crop-clearance follows the same radial-selection logic as Radial Profiles.
  // Other arm-based analyses still default to the 0° arm direction.
  const radialAngleRad = analysis === 'crop-clearance' ? selectedRadialDeg * Math.PI / 180 : 0;
  const armSampleCount = analysis === 'crop-clearance' ? radialSampleCount(pivot, samplingDistance) : 240;
  // Build the elevation profile that drives the chart
  const profile = isAngleBased ? buildTowerPathProfile(pivot, towerIndex, 240) : buildRadialProfile(pivot, radialAngleRad, armSampleCount);
  const dMax = profile.points[profile.points.length - 1].d;
  // Padded Y range
  const dataRange = profile.maxElev - profile.minElev || 1;
  const yPadAbs = Math.max(2, dataRange * 0.22);
  const niceStep = (() => {
    const raw = (dataRange + yPadAbs * 2) / 4;
    const pow10 = Math.pow(10, Math.floor(Math.log10(raw)));
    const norm = raw / pow10;
    const nice = norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10;
    return nice * pow10;
  })();
  const yMin = Math.floor((profile.minElev - yPadAbs) / niceStep) * niceStep;
  const yMax = Math.ceil((profile.maxElev + yPadAbs) / niceStep) * niceStep;
  const eRange = yMax - yMin || 1;
  const xFor = (d: number) => padL + d / dMax * innerW;
  const yFor = (e: number) => padT + (1 - (e - yMin) / eRange) * innerH;
  // Catmull-Rom smoothed path
  const pts = profile.points.map(p => ({
    x: xFor(p.d),
    y: yFor(p.elev)
  }));
  const linePath = (() => {
    if (pts.length < 2) return '';
    const tension = 0.5;
    let d = `M${pts[0].x.toFixed(2)},${pts[0].y.toFixed(2)}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i - 1] || pts[i];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[i + 2] || p2;
      const cp1x = p1.x + (p2.x - p0.x) / 6 * tension;
      const cp1y = p1.y + (p2.y - p0.y) / 6 * tension;
      const cp2x = p2.x - (p3.x - p1.x) / 6 * tension;
      const cp2y = p2.y - (p3.y - p1.y) / 6 * tension;
      d += ` C${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p2.x.toFixed(2)},${p2.y.toFixed(2)}`;
    }
    return d;
  })();
  const areaPath = `${linePath} L${xFor(dMax).toFixed(1)},${(padT + innerH).toFixed(1)} L${xFor(0).toFixed(1)},${(padT + innerH).toFixed(1)} Z`;
  // Tower positions on the elevation curve
  // For arm-based analyses: each tower sits at d = (i/n)*dMax
  // For angle-based analyses (tower-path): mark cardinal angles 0/90/180/270 on the curve
  const towerMarks = isAngleBased ? [0, 90, 180, 270].map(deg => ({
    d: deg,
    label: `${deg}°`
  })) : Array.from({
    length: n
  }).map((_, i) => ({
    d: (i + 1) / n * dMax,
    label: `T${i + 1}`
  }));
  // Elevation lookup along the curve (linear interp between profile points)
  const elevAtT = (t: number) => {
    const idx = t * (profile.points.length - 1);
    const lo = Math.floor(idx);
    const hi = Math.min(profile.points.length - 1, lo + 1);
    const frac = idx - lo;
    return profile.points[lo].elev + (profile.points[hi].elev - profile.points[lo].elev) * frac;
  };
  // Y gridlines
  const gridLines = (() => {
    const lines: {
      y: number;
      label: string;
    }[] = [];
    for (let e = yMin; e <= yMax + 0.0001; e += niceStep) {
      lines.push({
        y: yFor(e),
        label: e.toFixed(niceStep < 1 ? 1 : 0)
      });
    }
    return lines;
  })();
  // X ticks
  const xTicks = isAngleBased ? [0, 90, 180, 270, 360].map(deg => ({
    x: xFor(deg),
    label: `${deg}°`
  })) : Array.from({
    length: n + 1
  }).map((_, i) => ({
    x: xFor(i / n * dMax),
    label: i === 0 ? 'Center' : `${(i / n * dMax).toFixed(0)} m`
  }));
  // Analysis-specific overlays (computed once)
  const overlay = useSlopeOverlay({
    pivot,
    analysis,
    towerIndex,
    profile,
    xFor,
    yFor,
    elevAtT,
    dMax,
    yMin,
    yMax,
    padT,
    innerH,
    isAngleBased,
    cropClearanceConfig,
    radialAngleRad
  });
  // Hover scrubbing
  const svgRef = useRef<SVGSVGElement>(null);
  const updateFromEvent = (clientX: number) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const localX = clientX - rect.left;
    let t = (localX - padL) / innerW;
    t = Math.max(0, Math.min(1, t));
    onCursorChange(t);
  };
  const cursorX = cursorT !== null ? padL + cursorT * innerW : null;
  const cursorElev = cursorT !== null ? elevAtT(cursorT) : null;
  const cursorY = cursorElev !== null ? yFor(cursorElev) : null;
  const cursorXValue = cursorT !== null ? isAngleBased ? `${(cursorT * 360).toFixed(0)}°` : `${(cursorT * dMax).toFixed(1)} m` : '';
  return <div ref={wrapRef} className="w-full h-full" data-id="element-10687">
      <svg ref={svgRef} width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="block select-none" style={{
      cursor: 'crosshair'
    }} onMouseMove={e => updateFromEvent(e.clientX)} onMouseEnter={e => updateFromEvent(e.clientX)} onMouseLeave={() => onCursorChange(null)} data-id="element-10688">
        <defs data-id="element-10689">
          <linearGradient id="slope-area-grad" x1="0" y1="0" x2="0" y2="1" data-id="element-10690">
            <stop offset="0%" stopColor="#00D4FF" stopOpacity="0.4" data-id="element-10691" />
            <stop offset="60%" stopColor="#00D4FF" stopOpacity="0.12" data-id="element-10692" />
            <stop offset="100%" stopColor="#00D4FF" stopOpacity="0.02" data-id="element-10693" />
          </linearGradient>
        </defs>
        {/* Plot background */}
        <rect x={padL} y={padT} width={innerW} height={innerH} fill="#0a1322" opacity="0.6" data-id="element-10694" />

        {/* Critical zone bands (rendered behind everything) */}
        {overlay.zoneBands}

        {/* Y gridlines */}
        {gridLines.map((g, i) => <g key={`yg-${i}`} data-id="element-10695">
            <line x1={padL} y1={g.y} x2={W - padR} y2={g.y} stroke="#2F4268" strokeWidth="1" strokeDasharray={i === 0 || i === gridLines.length - 1 ? undefined : '3 3'} opacity={i === 0 || i === gridLines.length - 1 ? 0.9 : 0.4} data-id="element-10696" />
            <text x={padL - 6} y={g.y + 3.5} fontSize="10" fontFamily="ui-monospace, monospace" fill="#8A9BBA" textAnchor="end" data-id="element-10697">
              {g.label} m
            </text>
          </g>)}
        {/* X ticks */}
        {xTicks.map((t, i) => <g key={`xt-${i}`} data-id="element-10698">
            <line x1={t.x} y1={padT + innerH} x2={t.x} y2={padT + innerH + 3} stroke="#8A9BBA" strokeWidth="1" data-id="element-10699" />
            <text x={t.x} y={padT + innerH + 14} fontSize="10" fontFamily="ui-monospace, monospace" fill="#8A9BBA" textAnchor="middle" data-id="element-10700">
              {t.label}
            </text>
          </g>)}

        {/* Area under terrain */}
        <path d={areaPath} fill="url(#slope-area-grad)" data-id="element-10701" />
        {/* Terrain line */}
        <path d={linePath} fill="none" stroke="#00D4FF" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" data-id="element-10702" />

        {/* Analysis-specific structural overlay (above terrain) */}
        {overlay.structureLayer}

        {/* Towers placed on the elevation curve */}
        {towerMarks.map((tw, i) => {
        // For arm-based: place tower at terrain elevation at that distance
        // For angle-based: place at terrain elevation at that angle
        const t = isAngleBased ? tw.d / 360 : tw.d / dMax;
        const elev = elevAtT(t);
        const x = xFor(tw.d);
        const y = yFor(elev);
        const towerHeight = 22; // px in SVG
        const baseW = 10;
        const isSel = !isAngleBased && i + 1 === towerIndex && overlay.highlightTower;
        const towerColor = isSel ? '#FBBF24' : '#F1F5FB';
        return <g key={`tower-${i}`} data-id="element-10703">
              {/* Vertical guide */}
              <line x1={x} x2={x} y1={y} y2={padT + innerH} stroke={towerColor} strokeWidth="0.5" strokeDasharray="2 3" opacity="0.4" data-id="element-10704" />
              {/* Tower body — small A-frame triangle */}
              <polygon points={`${x},${y - towerHeight} ${x - baseW / 2},${y} ${x + baseW / 2},${y}`} fill="none" stroke={towerColor} strokeWidth="1.4" strokeLinejoin="round" data-id="element-10705" />
              {/* Cross brace */}
              <line x1={x - baseW / 2 + 1.5} y1={y - 1} x2={x + baseW / 2 - 1.5} y2={y - 1} stroke={towerColor} strokeWidth="0.9" data-id="element-10706" />
              {/* Wheel */}
              <circle cx={x} cy={y + 2} r="1.6" fill={towerColor} stroke="#0b1628" strokeWidth="0.4" data-id="element-10707" />
              {/* Label */}
              <text x={x} y={y - towerHeight - 4} fontSize="10" fontFamily="ui-monospace, monospace" fontWeight="700" fill={towerColor} textAnchor="middle" style={{
            paintOrder: 'stroke',
            stroke: '#0b1628',
            strokeWidth: 2,
            strokeLinejoin: 'round'
          } as any} data-id="element-10708">
                {tw.label}
              </text>
            </g>;
      })}

        {/* Structural top line (the span structure connecting towers) — for arm analyses */}
        {!isAngleBased && overlay.spanLine}

        {/* Cursor */}
        {cursorX !== null && cursorY !== null && <g pointerEvents="none" data-id="element-10709">
            <line x1={cursorX} y1={padT} x2={cursorX} y2={padT + innerH} stroke="#FBBF24" strokeWidth="1" strokeDasharray="3 2" opacity="0.9" data-id="element-10710" />
            <circle cx={cursorX} cy={cursorY} r="5" fill="#FBBF24" stroke="#0b1628" strokeWidth="1.5" data-id="element-10711" />
            <circle cx={cursorX} cy={cursorY} r="2" fill="#0b1628" data-id="element-10712" />
          </g>}

        {/* HUD readout pinned top-right */}
        {cursorT !== null && cursorElev !== null && <g pointerEvents="none" data-id="element-10713">
            {(() => {
          const tipW = 150;
          const tipH = 52;
          const tipX = W - padR - tipW - 4;
          const tipY = padT + 4;
          return <g data-id="element-10714">
                  <rect x={tipX} y={tipY} width={tipW} height={tipH} rx="5" fill="#0b1628" stroke="#FBBF24" strokeWidth="1" opacity="0.95" data-id="element-10715" />
                  <text x={tipX + 10} y={tipY + 14} fontSize="10" fontFamily="ui-monospace, monospace" fill="#8A9BBA" data-id="element-10716">
                    {isAngleBased ? 'Angle' : 'Distance'}
                  </text>
                  <text x={tipX + tipW - 10} y={tipY + 14} fontSize="11" fontFamily="ui-monospace, monospace" fill="#FBBF24" fontWeight="700" textAnchor="end" data-id="element-10717">
                    {cursorXValue}
                  </text>
                  <text x={tipX + 10} y={tipY + 28} fontSize="10" fontFamily="ui-monospace, monospace" fill="#8A9BBA" data-id="element-10718">
                    Elevation
                  </text>
                  <text x={tipX + tipW - 10} y={tipY + 28} fontSize="11" fontFamily="ui-monospace, monospace" fill="#00D4FF" fontWeight="700" textAnchor="end" data-id="element-10719">
                    {cursorElev.toFixed(1)} m
                  </text>
                  <text x={tipX + 10} y={tipY + 42} fontSize="10" fontFamily="ui-monospace, monospace" fill="#8A9BBA" data-id="element-10720">
                    {overlay.cursorReadoutLabel}
                  </text>
                  <text x={tipX + tipW - 10} y={tipY + 42} fontSize="11" fontFamily="ui-monospace, monospace" fill={overlay.cursorReadoutColor(cursorT)} fontWeight="700" textAnchor="end" data-id="element-10721">
                    {overlay.cursorReadoutValue(cursorT)}
                  </text>
                </g>;
        })()}
          </g>}

        {/* Legend strip (bottom-left of plot) */}
        {overlay.legend && <g data-id="element-10722">{overlay.legend(padL + 4, padT + 4)}</g>}
      </svg>
    </div>;
}
// Per-analysis overlay layers (zone bands, structure lines, legend, cursor readouts)
function useSlopeOverlay({
  pivot,
  analysis,
  towerIndex,
  profile,
  xFor,
  yFor,
  elevAtT,
  dMax,
  yMin,
  yMax,
  padT,
  innerH,
  isAngleBased,
  cropClearanceConfig,
  radialAngleRad
}: {
  pivot: Pivot;
  analysis: SlopeAnalysisType;
  towerIndex: number;
  profile: {
    points: {
      d: number;
      elev: number;
      t: number;
    }[];
    minElev: number;
    maxElev: number;
  };
  xFor: (d: number) => number;
  yFor: (e: number) => number;
  elevAtT: (t: number) => number;
  dMax: number;
  yMin: number;
  yMax: number;
  padT: number;
  innerH: number;
  isAngleBased: boolean;
  cropClearanceConfig: {
    structHeightM: number;
    warnAtM: number;
    cropHeightM: number;
    cropName: string;
  };
  radialAngleRad: number;
}): {
  zoneBands: React.ReactNode;
  structureLayer: React.ReactNode;
  spanLine: React.ReactNode;
  highlightTower: boolean;
  legend: ((x: number, y: number) => React.ReactNode) | null;
  cursorReadoutLabel: string;
  cursorReadoutValue: (t: number) => string;
  cursorReadoutColor: (t: number) => string;
} {
  const n = Math.max(1, pivot.towers);
  // Helper to compute slope% between two distances along the curve (arm direction)
  const slopePctBetween = (d1: number, d2: number) => {
    const t1 = d1 / dMax;
    const t2 = d2 / dMax;
    const e1 = elevAtT(t1);
    const e2 = elevAtT(t2);
    return (e2 - e1) / Math.max(0.01, d2 - d1) * 100;
  };
  // Crop clearance overlay — uses the segmented structural envelope anchored at
  // each tower (terrain + structHeight) with a small mid-span sag between
  // adjacent towers, and respects the user-configured structure height, crop
  // height and warn threshold.
  if (analysis === 'crop-clearance') {
    const STRUCT_HEIGHT = cropClearanceConfig.structHeightM;
    const CROP_HEIGHT_M = cropClearanceConfig.cropHeightM;
    const WARN_M = cropClearanceConfig.warnAtM;
    const EXCEED_M = Math.max(0.1, WARN_M * 0.5);
    // Tower anchor points along the selected radial (terrain elev + structHeight).
    // We mark these so the chart visually exposes the span-by-span structure.
    const towerAnchors = Array.from({
      length: n
    }).map((_, i) => {
      const tA = (i + 1) / n;
      return {
        t: tA,
        d: tA * dMax,
        terrainE: elevAtT(tA),
        underE: elevAtT(tA) + STRUCT_HEIGHT
      };
    });
    // Per-sample structure underside, crop top, clearance.
    const samples = profile.points.map(p => {
      const structE = segmentedStructureUnderside(pivot, radialAngleRad, STRUCT_HEIGHT, p.t);
      const cropTopE = p.elev + CROP_HEIGHT_M;
      const clearance = structE - cropTopE;
      return {
        d: p.d,
        t: p.t,
        clearance,
        structE,
        cropTopE,
        terrainE: p.elev
      };
    });
    // SVG paths for structure underside and crop top.
    const structPath = {
      path: samples.map((s, i) => `${i === 0 ? 'M' : 'L'}${xFor(s.d).toFixed(1)},${yFor(s.structE).toFixed(1)}`).join(' '),
      cropPath: samples.map((s, i) => `${i === 0 ? 'M' : 'L'}${xFor(s.d).toFixed(1)},${yFor(s.cropTopE).toFixed(1)}`).join(' ')
    };
    const bands: {
      from: number;
      to: number;
      level: 'warning' | 'exceeded';
    }[] = [];
    let cur: {
      from: number;
      to: number;
      level: 'warning' | 'exceeded';
    } | null = null;
    for (const s of samples) {
      const lvl = s.clearance < EXCEED_M ? 'exceeded' : s.clearance < WARN_M ? 'warning' : null;
      if (lvl) {
        if (!cur || cur.level !== lvl) {
          if (cur) bands.push(cur);
          cur = {
            from: s.d,
            to: s.d,
            level: lvl
          };
        } else {
          cur.to = s.d;
        }
      } else if (cur) {
        bands.push(cur);
        cur = null;
      }
    }
    if (cur) bands.push(cur);
    return {
      zoneBands: bands.map((b, i) => <rect key={`cb-${i}`} x={xFor(b.from)} y={padT} width={Math.max(2, xFor(b.to) - xFor(b.from))} height={innerH} fill={b.level === 'exceeded' ? '#F87171' : '#FBBF24'} opacity="0.16" data-id="element-10723" />),
      structureLayer: <g data-id="element-10724">
          {/* Crop canopy line above terrain */}
          <path d={structPath.cropPath} fill="none" stroke="#34D399" strokeWidth="1.2" strokeDasharray="3 2" opacity="0.7" data-id="element-10725" />
          {/* Segmented structure underside — chain of catenary arcs between
                                                        towers, NOT a global flat line. Each tower anchor is visible as
                                                        a node on the line. */}
          <path d={structPath.path} fill="none" stroke="#F1F5FB" strokeWidth="1.8" opacity="0.95" strokeLinejoin="round" data-id="element-10726" />
          {/* Tower anchor nodes on the underside line + drop guide to terrain.
                                                        These make the span-by-span structure visually explicit. */}
          {towerAnchors.map((a, i) => {
          const ax = xFor(a.d);
          const ayUnder = yFor(a.underE);
          const ayTerrain = yFor(a.terrainE);
          return <g key={`anchor-${i}`} data-id="element-10727">
                {/* Vertical drop from underside anchor to terrain — illustrates
                                                                            the structural support at this tower. */}
                <line x1={ax} y1={ayUnder} x2={ax} y2={ayTerrain} stroke="#F1F5FB" strokeWidth="0.8" strokeDasharray="2 2" opacity="0.5" data-id="element-10728" />
                {/* Anchor dot on the underside line */}
                <circle cx={ax} cy={ayUnder} r="2.6" fill="#F1F5FB" stroke="#0b1628" strokeWidth="1" data-id="element-10729" />
              </g>;
        })}
        </g>,
      spanLine: null,
      highlightTower: false,
      legend: (x, y) => <g transform={`translate(${x},${y})`} data-id="element-10730">
          <rect width="170" height="44" rx="3" fill="#0b1628" opacity="0.75" data-id="element-10731" />
          <line x1="8" y1="12" x2="22" y2="12" stroke="#F1F5FB" strokeWidth="1.6" data-id="element-10732" />
          <text x="28" y="15" fontSize="9" fill="#C5D1E4" fontFamily="ui-monospace, monospace" data-id="element-10733">
            Structure underside
          </text>
          <line x1="8" y1="24" x2="22" y2="24" stroke="#34D399" strokeWidth="1.2" strokeDasharray="3 2" data-id="element-10734" />
          <text x="28" y="27" fontSize="9" fill="#C5D1E4" fontFamily="ui-monospace, monospace" data-id="element-10735">
            Crop top
          </text>
          <line x1="8" y1="36" x2="22" y2="36" stroke="#00D4FF" strokeWidth="1.6" data-id="element-10736" />
          <text x="28" y="39" fontSize="9" fill="#C5D1E4" fontFamily="ui-monospace, monospace" data-id="element-10737">
            Terrain
          </text>
        </g>,
      cursorReadoutLabel: 'Crop clr.',
      cursorReadoutValue: t => {
        const idx = Math.round(t * (samples.length - 1));
        return `${samples[idx].clearance.toFixed(2)} m`;
      },
      cursorReadoutColor: t => {
        const idx = Math.round(t * (samples.length - 1));
        const c = samples[idx].clearance;
        return c < EXCEED_M ? '#F87171' : c < WARN_M ? '#FBBF24' : '#34D399';
      }
    };
  }
  // Longitudinal — color each segment between towers by severity
  if (analysis === 'longitudinal') {
    const WARN_PCT = 10;
    const EXCEED_PCT = 15;
    const radii = Array.from({
      length: n + 1
    }).map((_, i) => i / n * dMax);
    const segs = radii.slice(0, -1).map((r1, i) => {
      const r2 = radii[i + 1];
      const slopePct = slopePctBetween(r1, r2);
      const abs = Math.abs(slopePct);
      const level: 'safe' | 'warning' | 'exceeded' = abs >= EXCEED_PCT ? 'exceeded' : abs >= WARN_PCT ? 'warning' : 'safe';
      return {
        from: r1,
        to: r2,
        slopePct,
        level
      };
    });
    // Slope per sample for cursor readout
    const sampleSlope = (t: number) => {
      const idx = Math.min(segs.length - 1, Math.floor(t * n));
      return segs[idx].slopePct;
    };
    return {
      zoneBands: segs.map((s, i) => {
        if (s.level === 'safe') return null;
        const color = s.level === 'exceeded' ? '#F87171' : '#FBBF24';
        return <rect key={`lb-${i}`} x={xFor(s.from)} y={padT} width={Math.max(2, xFor(s.to) - xFor(s.from))} height={innerH} fill={color} opacity="0.16" data-id="element-10738" />;
      }),
      structureLayer: null,
      spanLine: (() => {
        // Draw segments between tower-tops connecting their elevation points
        const towerD = Array.from({
          length: n
        }).map((_, i) => (i + 1) / n * dMax);
        const points = towerD.map(d => ({
          x: xFor(d),
          y: yFor(elevAtT(d / dMax))
        }));
        // Include pivot center as origin
        const center = {
          x: xFor(0),
          y: yFor(elevAtT(0))
        };
        const allPts = [center, ...points];
        return <g data-id="element-10739">
            {allPts.slice(0, -1).map((p, i) => {
            const next = allPts[i + 1];
            const seg = segs[i];
            const color = seg.level === 'exceeded' ? '#F87171' : seg.level === 'warning' ? '#FBBF24' : '#34D399';
            return <line key={`spanseg-${i}`} x1={p.x} y1={p.y - 22} x2={next.x} y2={next.y - 22} stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.95" data-id="element-10740" />;
          })}
            {/* Slope labels on each segment */}
            {allPts.slice(0, -1).map((p, i) => {
            const next = allPts[i + 1];
            const seg = segs[i];
            const midX = (p.x + next.x) / 2;
            const midY = (p.y + next.y) / 2 - 30;
            return <text key={`spanlbl-${i}`} x={midX} y={midY} fontSize="9" fontFamily="ui-monospace, monospace" fontWeight="700" fill={seg.level === 'exceeded' ? '#F87171' : seg.level === 'warning' ? '#FBBF24' : '#34D399'} textAnchor="middle" style={{
              paintOrder: 'stroke',
              stroke: '#0b1628',
              strokeWidth: 2,
              strokeLinejoin: 'round'
            } as any} data-id="element-10741">
                  {seg.slopePct >= 0 ? '+' : ''}
                  {seg.slopePct.toFixed(1)}%
                </text>;
          })}
          </g>;
      })(),
      highlightTower: false,
      legend: (x, y) => <g transform={`translate(${x},${y})`} data-id="element-10742">
          <rect width="190" height="32" rx="3" fill="#0b1628" opacity="0.75" data-id="element-10743" />
          <line x1="8" y1="12" x2="22" y2="12" stroke="#34D399" strokeWidth="2.5" data-id="element-10744" />
          <text x="28" y="15" fontSize="9" fill="#C5D1E4" fontFamily="ui-monospace, monospace" data-id="element-10745">
            Safe
          </text>
          <line x1="62" y1="12" x2="76" y2="12" stroke="#FBBF24" strokeWidth="2.5" data-id="element-10746" />
          <text x="82" y="15" fontSize="9" fill="#C5D1E4" fontFamily="ui-monospace, monospace" data-id="element-10747">
            Warning
          </text>
          <line x1="120" y1="12" x2="134" y2="12" stroke="#F87171" strokeWidth="2.5" data-id="element-10748" />
          <text x="140" y="15" fontSize="9" fill="#C5D1E4" fontFamily="ui-monospace, monospace" data-id="element-10749">
            Exceeded
          </text>
          <text x="8" y="27" fontSize="8" fill="#8A9BBA" fontFamily="ui-monospace, monospace" data-id="element-10750">
            Span color = slope severity
          </text>
        </g>,
      cursorReadoutLabel: 'Segment slope',
      cursorReadoutValue: t => {
        const v = sampleSlope(t);
        return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
      },
      cursorReadoutColor: t => {
        const a = Math.abs(sampleSlope(t));
        return a >= EXCEED_PCT ? '#F87171' : a >= WARN_PCT ? '#FBBF24' : '#34D399';
      }
    };
  }
  // Structural absorption — joint markers + bend labels
  if (analysis === 'structural-absorption') {
    const FLEX_LIMIT_DEG = 2.5;
    const WARN_RATIO = 0.7;
    const radii = Array.from({
      length: n + 1
    }).map((_, i) => i / n * dMax);
    const segSlopesDeg = radii.slice(0, -1).map((r1, i) => {
      const r2 = radii[i + 1];
      const dy = elevAtT(r2 / dMax) - elevAtT(r1 / dMax);
      const dx = r2 - r1 || 1;
      return Math.atan2(dy, dx) * 180 / Math.PI;
    });
    const joints = segSlopesDeg.slice(0, -1).map((s, i) => ({
      jointIndex: i + 1,
      d: radii[i + 1],
      bendDeg: segSlopesDeg[i + 1] - s
    }));
    return {
      zoneBands: null,
      structureLayer: null,
      spanLine: (() => {
        const towerD = Array.from({
          length: n
        }).map((_, i) => (i + 1) / n * dMax);
        const points = towerD.map(d => ({
          x: xFor(d),
          y: yFor(elevAtT(d / dMax))
        }));
        const center = {
          x: xFor(0),
          y: yFor(elevAtT(0))
        };
        const allPts = [center, ...points];
        return <g data-id="element-10751">
            {allPts.slice(0, -1).map((p, i) => <line key={`abs-${i}`} x1={p.x} y1={p.y - 22} x2={allPts[i + 1].x} y2={allPts[i + 1].y - 22} stroke="#F1F5FB" strokeWidth="1.8" strokeLinecap="round" data-id="element-10752" />)}
            {joints.map(j => {
            const x = xFor(j.d);
            const y = yFor(elevAtT(j.d / dMax)) - 22;
            const abs = Math.abs(j.bendDeg);
            const color = abs >= FLEX_LIMIT_DEG ? '#F87171' : abs >= FLEX_LIMIT_DEG * WARN_RATIO ? '#FBBF24' : '#34D399';
            return <g key={`joint-${j.jointIndex}`} data-id="element-10753">
                  <circle cx={x} cy={y} r="4" fill={color} stroke="#0b1628" strokeWidth="1" data-id="element-10754" />
                  <text x={x} y={y - 8} fontSize="9" fontFamily="ui-monospace, monospace" fontWeight="700" fill={color} textAnchor="middle" style={{
                paintOrder: 'stroke',
                stroke: '#0b1628',
                strokeWidth: 2,
                strokeLinejoin: 'round'
              } as any} data-id="element-10755">
                    {j.bendDeg >= 0 ? '+' : ''}
                    {j.bendDeg.toFixed(1)}°
                  </text>
                </g>;
          })}
          </g>;
      })(),
      highlightTower: false,
      legend: (x, y) => <g transform={`translate(${x},${y})`} data-id="element-10756">
          <rect width="200" height="32" rx="3" fill="#0b1628" opacity="0.75" data-id="element-10757" />
          <circle cx="14" cy="12" r="4" fill="#34D399" data-id="element-10758" />
          <text x="24" y="15" fontSize="9" fill="#C5D1E4" fontFamily="ui-monospace, monospace" data-id="element-10759">
            Within flex
          </text>
          <circle cx="80" cy="12" r="4" fill="#FBBF24" data-id="element-10760" />
          <text x="90" y="15" fontSize="9" fill="#C5D1E4" fontFamily="ui-monospace, monospace" data-id="element-10761">
            Near limit
          </text>
          <circle cx="148" cy="12" r="4" fill="#F87171" data-id="element-10762" />
          <text x="158" y="15" fontSize="9" fill="#C5D1E4" fontFamily="ui-monospace, monospace" data-id="element-10763">
            Exceeded
          </text>
          <text x="8" y="27" fontSize="8" fill="#8A9BBA" fontFamily="ui-monospace, monospace" data-id="element-10764">
            Joint bend = Δ slope between spans
          </text>
        </g>,
      cursorReadoutLabel: 'Joint',
      cursorReadoutValue: t => {
        // Nearest joint
        if (joints.length === 0) return '—';
        const target = t * dMax;
        const j = joints.reduce((m, c) => Math.abs(c.d - target) < Math.abs(m.d - target) ? c : m);
        return `J${j.jointIndex} ${j.bendDeg >= 0 ? '+' : ''}${j.bendDeg.toFixed(2)}°`;
      },
      cursorReadoutColor: () => '#00D4FF'
    };
  }
  // Transverse (angle-based) — bands where |side slope| > warn
  if (analysis === 'transverse') {
    const WARN_PCT = 8;
    const EXCEED_PCT = 15;
    const radius = towerRadius(pivot, towerIndex);
    const SAMPLES = 180;
    const points: {
      angle: number;
      slopePct: number;
    }[] = [];
    for (let i = 0; i <= SAMPLES; i++) {
      const t = i / SAMPLES;
      const angle = t * 360;
      const aRad = angle * Math.PI / 180;
      const innerR = Math.max(0, radius - 1);
      const outerR = Math.min(pivot.radiusPx, radius + 1);
      const u1 = 0.5 + Math.cos(aRad) * innerR / pivot.radiusPx * 0.5;
      const v1 = 0.5 + Math.sin(aRad) * innerR / pivot.radiusPx * 0.5;
      const u2 = 0.5 + Math.cos(aRad) * outerR / pivot.radiusPx * 0.5;
      const v2 = 0.5 + Math.sin(aRad) * outerR / pivot.radiusPx * 0.5;
      const e1 = samplePivotElevation(pivot, u1, v1);
      const e2 = samplePivotElevation(pivot, u2, v2);
      const dx = outerR - innerR;
      const slopePct = dx > 0 ? (e2 - e1) / dx * 100 : 0;
      points.push({
        angle,
        slopePct
      });
    }
    const bands: {
      from: number;
      to: number;
      level: 'warning' | 'exceeded';
    }[] = [];
    let cur: {
      from: number;
      to: number;
      level: 'warning' | 'exceeded';
    } | null = null;
    for (const p of points) {
      const a = Math.abs(p.slopePct);
      const lvl: 'warning' | 'exceeded' | null = a >= EXCEED_PCT ? 'exceeded' : a >= WARN_PCT ? 'warning' : null;
      if (lvl) {
        if (!cur || cur.level !== lvl) {
          if (cur) bands.push(cur);
          cur = {
            from: p.angle,
            to: p.angle,
            level: lvl
          };
        } else {
          cur.to = p.angle;
        }
      } else if (cur) {
        bands.push(cur);
        cur = null;
      }
    }
    if (cur) bands.push(cur);
    const sampleSlope = (t: number) => {
      const idx = Math.min(points.length - 1, Math.round(t * (points.length - 1)));
      return points[idx].slopePct;
    };
    return {
      zoneBands: bands.map((b, i) => <rect key={`tb-${i}`} x={xFor(b.from)} y={padT} width={Math.max(2, xFor(b.to) - xFor(b.from))} height={innerH} fill={b.level === 'exceeded' ? '#F87171' : '#FBBF24'} opacity="0.16" data-id="element-10765" />),
      structureLayer: null,
      spanLine: null,
      highlightTower: false,
      legend: (x, y) => <g transform={`translate(${x},${y})`} data-id="element-10766">
          <rect width="220" height="32" rx="3" fill="#0b1628" opacity="0.75" data-id="element-10767" />
          <text x="8" y="13" fontSize="9" fill="#C5D1E4" fontFamily="ui-monospace, monospace" data-id="element-10768">
            Side-slope along wheel path
          </text>
          <rect x="8" y="20" width="14" height="6" fill="#FBBF24" opacity="0.5" data-id="element-10769" />
          <text x="26" y="26" fontSize="9" fill="#C5D1E4" fontFamily="ui-monospace, monospace" data-id="element-10770">
            ≥8%
          </text>
          <rect x="58" y="20" width="14" height="6" fill="#F87171" opacity="0.5" data-id="element-10771" />
          <text x="76" y="26" fontSize="9" fill="#C5D1E4" fontFamily="ui-monospace, monospace" data-id="element-10772">
            ≥15%
          </text>
        </g>,
      cursorReadoutLabel: 'Side slope',
      cursorReadoutValue: t => {
        const v = sampleSlope(t);
        return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
      },
      cursorReadoutColor: t => {
        const a = Math.abs(sampleSlope(t));
        return a >= EXCEED_PCT ? '#F87171' : a >= WARN_PCT ? '#FBBF24' : '#34D399';
      }
    };
  }
  // Torsional (angle-based) — bands where inner & outer wheel paths have opposing slopes
  if (analysis === 'torsional') {
    const WARN_DELTA = 4;
    const EXCEED_DELTA = 8;
    const radius = towerRadius(pivot, towerIndex);
    const innerR = Math.max(0, radius - pivot.radiusPx / Math.max(1, n) / 2);
    const outerR = Math.min(pivot.radiusPx, radius + pivot.radiusPx / Math.max(1, n) / 2);
    const SAMPLES = 180;
    const points: {
      angle: number;
      innerSlope: number;
      outerSlope: number;
      delta: number;
    }[] = [];
    for (let i = 0; i <= SAMPLES; i++) {
      const t = i / SAMPLES;
      const angle = t * 360;
      const aRad = angle * Math.PI / 180;
      const dAng = 1 * Math.PI / 180;
      const sampleArc = (r: number) => {
        const u1 = 0.5 + Math.cos(aRad - dAng) * r / pivot.radiusPx * 0.5;
        const v1 = 0.5 + Math.sin(aRad - dAng) * r / pivot.radiusPx * 0.5;
        const u2 = 0.5 + Math.cos(aRad + dAng) * r / pivot.radiusPx * 0.5;
        const v2 = 0.5 + Math.sin(aRad + dAng) * r / pivot.radiusPx * 0.5;
        const e1 = samplePivotElevation(pivot, u1, v1);
        const e2 = samplePivotElevation(pivot, u2, v2);
        const arcLen = 2 * dAng * r || 1;
        return (e2 - e1) / arcLen * 100;
      };
      const inS = innerR > 0 ? sampleArc(innerR) : 0;
      const ouS = sampleArc(outerR);
      const delta = inS * ouS < 0 ? Math.abs(inS) + Math.abs(ouS) : 0;
      points.push({
        angle,
        innerSlope: inS,
        outerSlope: ouS,
        delta
      });
    }
    const bands: {
      from: number;
      to: number;
      level: 'warning' | 'exceeded';
    }[] = [];
    let cur: {
      from: number;
      to: number;
      level: 'warning' | 'exceeded';
    } | null = null;
    for (const p of points) {
      const lvl: 'warning' | 'exceeded' | null = p.delta >= EXCEED_DELTA ? 'exceeded' : p.delta >= WARN_DELTA ? 'warning' : null;
      if (lvl) {
        if (!cur || cur.level !== lvl) {
          if (cur) bands.push(cur);
          cur = {
            from: p.angle,
            to: p.angle,
            level: lvl
          };
        } else {
          cur.to = p.angle;
        }
      } else if (cur) {
        bands.push(cur);
        cur = null;
      }
    }
    if (cur) bands.push(cur);
    const samplePoint = (t: number) => points[Math.min(points.length - 1, Math.round(t * (points.length - 1)))];
    return {
      zoneBands: bands.map((b, i) => <rect key={`torb-${i}`} x={xFor(b.from)} y={padT} width={Math.max(2, xFor(b.to) - xFor(b.from))} height={innerH} fill={b.level === 'exceeded' ? '#F87171' : '#FBBF24'} opacity="0.18" data-id="element-10773" />),
      structureLayer: null,
      spanLine: null,
      highlightTower: false,
      legend: (x, y) => <g transform={`translate(${x},${y})`} data-id="element-10774">
          <rect width="240" height="32" rx="3" fill="#0b1628" opacity="0.75" data-id="element-10775" />
          <text x="8" y="13" fontSize="9" fill="#C5D1E4" fontFamily="ui-monospace, monospace" data-id="element-10776">
            Twist zones = opposing wheel-path slopes
          </text>
          <rect x="8" y="20" width="14" height="6" fill="#FBBF24" opacity="0.5" data-id="element-10777" />
          <text x="26" y="26" fontSize="9" fill="#C5D1E4" fontFamily="ui-monospace, monospace" data-id="element-10778">
            ≥4% Δ
          </text>
          <rect x="70" y="20" width="14" height="6" fill="#F87171" opacity="0.5" data-id="element-10779" />
          <text x="88" y="26" fontSize="9" fill="#C5D1E4" fontFamily="ui-monospace, monospace" data-id="element-10780">
            ≥8% Δ
          </text>
        </g>,
      cursorReadoutLabel: 'Twist Δ',
      cursorReadoutValue: t => {
        const p = samplePoint(t);
        return p.delta > 0 ? `${p.delta.toFixed(1)}%` : '—';
      },
      cursorReadoutColor: t => {
        const p = samplePoint(t);
        return p.delta >= EXCEED_DELTA ? '#F87171' : p.delta >= WARN_DELTA ? '#FBBF24' : '#34D399';
      }
    };
  }
  return {
    zoneBands: null,
    structureLayer: null,
    spanLine: null,
    highlightTower: false,
    legend: null,
    cursorReadoutLabel: '',
    cursorReadoutValue: () => '',
    cursorReadoutColor: () => '#00D4FF'
  };
}
type ProfilePayload = {
  points: {
    d: number;
    elev: number;
    t: number;
  }[];
  minElev: number;
  maxElev: number;
  totalElev: number;
  maxAscent: number;
  maxDescent: number;
};
function HorizontalElevationPanel({
  profile,
  kind,
  titleRight,
  footerRight,
  onPrev,
  onNext,
  onClose,
  source,
  cursorT,
  onCursorChange
}: {
  profile: ProfilePayload;
  kind: 'radial' | 'tower-path';
  titleRight?: React.ReactNode;
  footerRight?: string;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  source: string;
  cursorT: number | null;
  onCursorChange: (t: number | null) => void;
}) {
  const H = 260;
  const title = kind === 'tower-path' ? 'Tower Path Profile' : 'Elevation Profile';
  const navTitlePrev = kind === 'tower-path' ? 'Previous tower' : 'Previous radial';
  const navTitleNext = kind === 'tower-path' ? 'Next tower' : 'Next radial';
  return <div className="bg-[#0a1322] border-t border-[#2F4268] flex-shrink-0 flex flex-col" style={{
    height: H
  }} data-id="element-10781">
      {/* Header strip */}
      <div className="flex items-center justify-between px-3 h-[30px] border-b border-[#2F4268] flex-shrink-0 bg-[#0b1628]" data-id="element-10782">
        <div className="flex items-center gap-2 min-w-0" data-id="element-10783">
          <LineChartIcon size={13} className="text-[#00D4FF] flex-shrink-0" data-id="element-10784" />
          <span className="text-[11px] font-semibold text-[#F1F5FB]" data-id="element-10785">
            {title}
          </span>
          {titleRight}
        </div>
        <div className="flex items-center gap-1" data-id="element-10786">
          <button onClick={onPrev} className="p-1 rounded text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5 transition-colors" title={navTitlePrev} data-id="element-10787">
            <ChevronRightIcon size={13} className="rotate-180" data-id="element-10788" />
          </button>
          <button onClick={onNext} className="p-1 rounded text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5 transition-colors" title={navTitleNext} data-id="element-10789">
            <ChevronRightIcon size={13} data-id="element-10790" />
          </button>
          <div className="w-px h-4 bg-[#2F4268] mx-1" data-id="element-10791" />
          {/* Inline stats */}
          <div className="flex items-center gap-3 px-2 text-[10px]" data-id="element-10792">
            <span className="flex items-center gap-1" data-id="element-10793">
              <ArrowUpIcon size={10} className="text-[#34D399]" data-id="element-10794" />
              <span className="text-[#8A9BBA]" data-id="element-10795">High</span>
              <span className="text-[#F1F5FB] font-mono font-semibold" data-id="element-10796">
                {profile.maxElev.toFixed(0)} m
              </span>
            </span>
            <span className="flex items-center gap-1" data-id="element-10797">
              <ArrowDownIcon size={10} className="text-[#F87171]" data-id="element-10798" />
              <span className="text-[#8A9BBA]" data-id="element-10799">Low</span>
              <span className="text-[#F1F5FB] font-mono font-semibold" data-id="element-10800">
                {profile.minElev.toFixed(0)} m
              </span>
            </span>
            <span className="flex items-center gap-1" data-id="element-10801">
              <TrendingUpIcon size={10} className="text-[#34D399]" data-id="element-10802" />
              <span className="text-[#8A9BBA]" data-id="element-10803">Ascent</span>
              <span className="text-[#34D399] font-mono font-semibold" data-id="element-10804">
                +{profile.maxAscent.toFixed(1)}%
              </span>
            </span>
            <span className="flex items-center gap-1" data-id="element-10805">
              <TrendingUpIcon size={10} className="text-[#F87171] rotate-180" data-id="element-10806" />
              <span className="text-[#8A9BBA]" data-id="element-10807">Descent</span>
              <span className="text-[#F87171] font-mono font-semibold" data-id="element-10808">
                {profile.maxDescent.toFixed(1)}%
              </span>
            </span>
            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#1C2B47] border border-[#00D4FF]/30" data-id="element-10809">
              <span className="text-[#8A9BBA] uppercase tracking-wide font-semibold text-[9px]" data-id="element-10810">
                Δ
              </span>
              <span className="text-[#00D4FF] font-mono font-bold" data-id="element-10811">
                {profile.totalElev.toFixed(1)} m
              </span>
            </span>
          </div>
          <div className="w-px h-4 bg-[#2F4268] mx-1" data-id="element-10812" />
          <button onClick={onClose} className="p-1 rounded text-[#8A9BBA] hover:text-[#F87171] hover:bg-white/5 transition-colors" title="Close profile panel" data-id="element-10813">
            <XIcon size={13} data-id="element-10814" />
          </button>
        </div>
      </div>
      {/* Chart body */}
      <div className="flex-1 min-h-0 px-3 py-2" data-id="element-10815">
        <HorizontalElevationChart profile={profile} cursorT={cursorT} onCursorChange={onCursorChange} kind={kind} data-id="element-10816" />
      </div>
      <div className="px-3 py-1 border-t border-[#2F4268]/40 flex items-center justify-between text-[9px] text-[#8A9BBA]" data-id="element-10817">
        <span className="italic" data-id="element-10818">
          {kind === 'tower-path' ? `Full 0° → 360° trajectory of the selected tower, sampled from the active topography (${source.replace('-', ' ')}).` : `Based on the active topography (${source.replace('-', ' ')}) sampled inside the pivot area.`}
        </span>
        {footerRight && <span className="font-mono" data-id="element-10819">{footerRight}</span>}
      </div>
    </div>;
}
function HorizontalElevationChart({
  profile,
  cursorT,
  onCursorChange,
  kind
}: {
  profile: ProfilePayload;
  cursorT: number | null;
  onCursorChange: (t: number | null) => void;
  kind?: 'radial' | 'tower-path';
}) {
  const isTowerPath = kind === 'tower-path';
  const xUnit = isTowerPath ? '°' : 'm';
  const xCursorLabel = isTowerPath ? 'Angle' : 'Distance';
  // Measure the container so the SVG renders at true pixel size — keeps strokes
  // and gradients crisp without preserveAspectRatio distortion.
  const wrapRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({
    w: 1000,
    h: 110
  });
  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver(entries => {
      for (const e of entries) {
        const cr = e.contentRect;
        setSize({
          w: Math.max(320, Math.floor(cr.width)),
          h: Math.max(80, Math.floor(cr.height))
        });
      }
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);
  const W = size.w;
  const H = size.h;
  const padL = 48;
  const padR = 18;
  const padT = 12;
  const padB = 22;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const dMax = profile.points[profile.points.length - 1].d;
  // Padded Y range — Google-Earth style: the line never hugs the top/bottom edges.
  // We extend the visible range ~18% beyond the data extremes and snap to "nice"
  // round numbers so axis labels read cleanly.
  const dataRange = profile.maxElev - profile.minElev || 1;
  const yPadAbs = Math.max(2, dataRange * 0.18);
  const niceStep = (() => {
    const raw = (dataRange + yPadAbs * 2) / 4;
    const pow10 = Math.pow(10, Math.floor(Math.log10(raw)));
    const norm = raw / pow10;
    const nice = norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10;
    return nice * pow10;
  })();
  const yMin = Math.floor((profile.minElev - yPadAbs) / niceStep) * niceStep;
  const yMax = Math.ceil((profile.maxElev + yPadAbs) / niceStep) * niceStep;
  const eRange = yMax - yMin || 1;
  const xFor = (d: number) => padL + d / dMax * innerW;
  const yFor = (e: number) => padT + (1 - (e - yMin) / eRange) * innerH;
  // Smooth path using Catmull-Rom → cubic Bezier conversion for a true "pro" curve.
  const pts = profile.points.map(p => ({
    x: xFor(p.d),
    y: yFor(p.elev)
  }));
  const linePath = (() => {
    if (pts.length < 2) return '';
    const tension = 0.5; // 0 = sharp polyline, 1 = very loose
    let d = `M${pts[0].x.toFixed(2)},${pts[0].y.toFixed(2)}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i - 1] || pts[i];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[i + 2] || p2;
      const cp1x = p1.x + (p2.x - p0.x) / 6 * tension;
      const cp1y = p1.y + (p2.y - p0.y) / 6 * tension;
      const cp2x = p2.x - (p3.x - p1.x) / 6 * tension;
      const cp2y = p2.y - (p3.y - p1.y) / 6 * tension;
      d += ` C${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p2.x.toFixed(2)},${p2.y.toFixed(2)}`;
    }
    return d;
  })();
  const areaPath = `${linePath} L${xFor(dMax).toFixed(1)},${(padT + innerH).toFixed(1)} L${xFor(0).toFixed(1)},${(padT + innerH).toFixed(1)} Z`;
  const peak = profile.points.find(p => p.elev === profile.maxElev) || profile.points[0];
  const valley = profile.points.find(p => p.elev === profile.minElev) || profile.points[0];
  // Y gridlines on nice round elevation values (Google-Earth style)
  const gridLines = (() => {
    const lines: {
      y: number;
      label: string;
    }[] = [];
    for (let e = yMin; e <= yMax + 0.0001; e += niceStep) {
      lines.push({
        y: yFor(e),
        label: e.toFixed(niceStep < 1 ? 1 : 0)
      });
    }
    return lines;
  })();
  // For tower-path use cardinal ticks 0°, 90°, 180°, 270°, 360°.
  // For radial use adaptive number of meter ticks.
  const xTicks = (() => {
    if (isTowerPath) {
      return [0, 90, 180, 270, 360].map(deg => ({
        x: padL + deg / dMax * innerW,
        label: `${deg}°`
      }));
    }
    const xTickCount = W < 600 ? 4 : W < 900 ? 6 : 8;
    return Array.from({
      length: xTickCount + 1
    }, (_, i) => {
      const t = i / xTickCount;
      return {
        x: padL + t * innerW,
        label: `${(t * dMax).toFixed(0)} m`
      };
    });
  })();
  // Cursor sampling — find elevation at cursorT
  const cursorElev = cursorT !== null ? (() => {
    const idx = cursorT * (profile.points.length - 1);
    const lo = Math.floor(idx);
    const hi = Math.min(profile.points.length - 1, lo + 1);
    const frac = idx - lo;
    return profile.points[lo].elev + (profile.points[hi].elev - profile.points[lo].elev) * frac;
  })() : null;
  const cursorX = cursorT !== null ? padL + cursorT * innerW : null;
  const cursorY = cursorElev !== null ? yFor(cursorElev) : null;
  // Hover-based scrubbing: any mouse movement inside the chart updates the cursor,
  // no click required. Leaving the chart clears the cursor.
  const svgRef = useRef<SVGSVGElement>(null);
  const updateFromEvent = (clientX: number) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const localX = clientX - rect.left;
    let t = (localX - padL) / innerW;
    t = Math.max(0, Math.min(1, t));
    onCursorChange(t);
  };
  const handleMove = (e: React.MouseEvent<SVGSVGElement>) => {
    updateFromEvent(e.clientX);
  };
  const handleLeave = () => {
    onCursorChange(null);
  };
  // Touch support — still drag-based since "hover" doesn't exist on touch
  const handleTouchStart = (e: React.TouchEvent<SVGSVGElement>) => {
    if (e.touches[0]) updateFromEvent(e.touches[0].clientX);
  };
  const handleTouchMove = (e: React.TouchEvent<SVGSVGElement>) => {
    if (e.touches[0]) updateFromEvent(e.touches[0].clientX);
  };
  return <div ref={wrapRef} className="w-full h-full" data-id="element-10820">
      <svg ref={svgRef} width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="block select-none" style={{
      cursor: 'crosshair'
    }} onMouseMove={handleMove} onMouseEnter={handleMove} onMouseLeave={handleLeave} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} data-id="element-10821">
        <defs data-id="element-10822">
          <linearGradient id="hprofile-area-grad" x1="0" y1="0" x2="0" y2="1" data-id="element-10823">
            <stop offset="0%" stopColor="#00D4FF" stopOpacity="0.5" data-id="element-10824" />
            <stop offset="60%" stopColor="#00D4FF" stopOpacity="0.18" data-id="element-10825" />
            <stop offset="100%" stopColor="#00D4FF" stopOpacity="0.02" data-id="element-10826" />
          </linearGradient>
        </defs>
        {/* Plot background */}
        <rect x={padL} y={padT} width={innerW} height={innerH} fill="#0a1322" opacity="0.6" data-id="element-10827" />
        {/* Y gridlines */}
        {gridLines.map((g, i) => <g key={`yg-${i}`} data-id="element-10828">
            <line x1={padL} y1={g.y} x2={W - padR} y2={g.y} stroke="#2F4268" strokeWidth="1" strokeDasharray={i === 0 || i === gridLines.length - 1 ? undefined : '3 3'} opacity={i === 0 || i === gridLines.length - 1 ? 0.9 : 0.45} data-id="element-10829" />
            <text x={padL - 6} y={g.y + 3.5} fontSize="10" fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace" fill="#8A9BBA" textAnchor="end" data-id="element-10830">
              {g.label} m
            </text>
          </g>)}
        {/* X ticks */}
        {xTicks.map((t, i) => <g key={`xt-${i}`} data-id="element-10831">
            <line x1={t.x} y1={padT + innerH} x2={t.x} y2={padT + innerH + 3} stroke="#8A9BBA" strokeWidth="1" data-id="element-10832" />
            <text x={t.x} y={padT + innerH + 14} fontSize="10" fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace" fill="#8A9BBA" textAnchor="middle" data-id="element-10833">
              {t.label}
            </text>
          </g>)}
        {/* Area under curve */}
        <path d={areaPath} fill="url(#hprofile-area-grad)" data-id="element-10834" />
        {/* Line */}
        <path d={linePath} fill="none" stroke="#00D4FF" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" data-id="element-10835" />
        {/* Peak marker */}
        <g data-id="element-10836">
          <line x1={xFor(peak.d)} x2={xFor(peak.d)} y1={yFor(peak.elev)} y2={padT + innerH} stroke="#34D399" strokeWidth="1" strokeDasharray="2 3" opacity="0.55" data-id="element-10837" />
          <circle cx={xFor(peak.d)} cy={yFor(peak.elev)} r="4" fill="#34D399" stroke="#0b1628" strokeWidth="1.2" data-id="element-10838" />
          <text x={xFor(peak.d)} y={yFor(peak.elev) - 8} fontSize="10" fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace" fill="#34D399" fontWeight="700" textAnchor="middle" style={{
          paintOrder: 'stroke',
          stroke: '#0b1628',
          strokeWidth: 2.5,
          strokeLinejoin: 'round'
        } as any} data-id="element-10839">
            ▲ {peak.elev.toFixed(0)} m
          </text>
        </g>
        {/* Valley marker */}
        <g data-id="element-10840">
          <line x1={xFor(valley.d)} x2={xFor(valley.d)} y1={yFor(valley.elev)} y2={padT + innerH} stroke="#F87171" strokeWidth="1" strokeDasharray="2 3" opacity="0.55" data-id="element-10841" />
          <circle cx={xFor(valley.d)} cy={yFor(valley.elev)} r="4" fill="#F87171" stroke="#0b1628" strokeWidth="1.2" data-id="element-10842" />
          <text x={xFor(valley.d)} y={yFor(valley.elev) + 14} fontSize="10" fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace" fill="#F87171" fontWeight="700" textAnchor="middle" style={{
          paintOrder: 'stroke',
          stroke: '#0b1628',
          strokeWidth: 2.5,
          strokeLinejoin: 'round'
        } as any} data-id="element-10843">
            ▼ {valley.elev.toFixed(0)} m
          </text>
        </g>
        {/* Drag cursor */}
        {cursorX !== null && cursorY !== null && cursorElev !== null && cursorT !== null && <g pointerEvents="none" data-id="element-10844">
              <line x1={cursorX} y1={padT} x2={cursorX} y2={padT + innerH} stroke="#FBBF24" strokeWidth="1" strokeDasharray="3 2" opacity="0.9" data-id="element-10845" />
              <circle cx={cursorX} cy={cursorY} r="5" fill="#FBBF24" stroke="#0b1628" strokeWidth="1.5" data-id="element-10846" />
              <circle cx={cursorX} cy={cursorY} r="2" fill="#0b1628" data-id="element-10847" />
              {/* Fixed HUD readout pinned to the top-right of the plot — never
               covers the moving cursor dot. */}
              {(() => {
          const dist = cursorT * dMax;
          const tipW = 130;
          const tipH = 38;
          const tipX = W - padR - tipW - 4;
          const tipY = padT + 4;
          const xValueText = isTowerPath ? `${dist.toFixed(1)}°` : `${dist.toFixed(1)} m`;
          return <g data-id="element-10848">
                    <rect x={tipX} y={tipY} width={tipW} height={tipH} rx="5" fill="#0b1628" stroke="#FBBF24" strokeWidth="1" opacity="0.95" data-id="element-10849" />
                    <text x={tipX + 10} y={tipY + 15} fontSize="10" fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace" fill="#8A9BBA" data-id="element-10850">
                      {xCursorLabel}
                    </text>
                    <text x={tipX + tipW - 10} y={tipY + 15} fontSize="11" fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace" fill="#FBBF24" fontWeight="700" textAnchor="end" data-id="element-10851">
                      {xValueText}
                    </text>
                    <text x={tipX + 10} y={tipY + 30} fontSize="10" fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace" fill="#8A9BBA" data-id="element-10852">
                      Elevation
                    </text>
                    <text x={tipX + tipW - 10} y={tipY + 30} fontSize="11" fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace" fill="#00D4FF" fontWeight="700" textAnchor="end" data-id="element-10853">
                      {cursorElev.toFixed(1)} m
                    </text>
                  </g>;
        })()}
            </g>}
      </svg>
    </div>;
}
function RadialProfileChart({
  profile
}: {
  profile: ReturnType<typeof buildRadialProfile>;
}) {
  // Build an SVG line chart with elevation samples
  const W = 320;
  const H = 110;
  const padL = 28;
  const padR = 8;
  const padT = 8;
  const padB = 18;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const eRange = profile.maxElev - profile.minElev || 1;
  const dMax = profile.points[profile.points.length - 1].d;
  const xFor = (d: number) => padL + d / dMax * innerW;
  const yFor = (e: number) => padT + (1 - (e - profile.minElev) / eRange) * innerH;
  const linePath = profile.points.map((p, i) => `${i === 0 ? 'M' : 'L'}${xFor(p.d).toFixed(1)},${yFor(p.elev).toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${xFor(dMax).toFixed(1)},${(padT + innerH).toFixed(1)} L${xFor(0).toFixed(1)},${(padT + innerH).toFixed(1)} Z`;
  // Find peak/valley points
  const peak = profile.points.find(p => p.elev === profile.maxElev) || profile.points[0];
  const valley = profile.points.find(p => p.elev === profile.minElev) || profile.points[0];
  // 4 horizontal gridlines
  const gridLines = [0, 0.25, 0.5, 0.75, 1].map(t => ({
    y: padT + t * innerH,
    label: (profile.maxElev - t * eRange).toFixed(0)
  }));
  // Distance axis ticks
  const xTicks = [0, 0.25, 0.5, 0.75, 1].map(t => ({
    x: padL + t * innerW,
    label: (t * dMax).toFixed(0)
  }));
  return <div className="px-3 py-2" data-id="element-10854">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{
      maxHeight: 130
    }} preserveAspectRatio="none" data-id="element-10855">
        <defs data-id="element-10856">
          <linearGradient id="profile-area-grad" x1="0" y1="0" x2="0" y2="1" data-id="element-10857">
            <stop offset="0%" stopColor="#00D4FF" stopOpacity="0.45" data-id="element-10858" />
            <stop offset="100%" stopColor="#00D4FF" stopOpacity="0.02" data-id="element-10859" />
          </linearGradient>
        </defs>
        {/* Gridlines */}
        {gridLines.map((g, i) => <g key={i} data-id="element-10860">
            <line x1={padL} y1={g.y} x2={W - padR} y2={g.y} stroke="#2F4268" strokeWidth="0.5" strokeDasharray={i === 0 || i === 4 ? undefined : '2 2'} opacity={i === 0 || i === 4 ? 0.9 : 0.4} data-id="element-10861" />
            <text x={padL - 3} y={g.y + 3} fontSize="7" fontFamily="monospace" fill="#8A9BBA" textAnchor="end" data-id="element-10862">
              {g.label}
            </text>
          </g>)}
        {/* X-axis ticks */}
        {xTicks.map((t, i) => <g key={i} data-id="element-10863">
            <line x1={t.x} y1={padT + innerH} x2={t.x} y2={padT + innerH + 2} stroke="#8A9BBA" strokeWidth="0.5" data-id="element-10864" />
            <text x={t.x} y={padT + innerH + 9} fontSize="7" fontFamily="monospace" fill="#8A9BBA" textAnchor="middle" data-id="element-10865">
              {t.label}m
            </text>
          </g>)}
        {/* Area under curve */}
        <path d={areaPath} fill="url(#profile-area-grad)" data-id="element-10866" />
        {/* Line */}
        <path d={linePath} fill="none" stroke="#00D4FF" strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" data-id="element-10867" />
        {/* Peak marker */}
        <g data-id="element-10868">
          <circle cx={xFor(peak.d)} cy={yFor(peak.elev)} r="2.4" fill="#34D399" stroke="#0b1628" strokeWidth="0.6" data-id="element-10869" />
          <text x={xFor(peak.d)} y={yFor(peak.elev) - 4} fontSize="7" fontFamily="monospace" fill="#34D399" fontWeight="700" textAnchor="middle" data-id="element-10870">
            ▲ {peak.elev.toFixed(0)}
          </text>
        </g>
        {/* Valley marker */}
        <g data-id="element-10871">
          <circle cx={xFor(valley.d)} cy={yFor(valley.elev)} r="2.4" fill="#F87171" stroke="#0b1628" strokeWidth="0.6" data-id="element-10872" />
          <text x={xFor(valley.d)} y={yFor(valley.elev) + 9} fontSize="7" fontFamily="monospace" fill="#F87171" fontWeight="700" textAnchor="middle" data-id="element-10873">
            ▼ {valley.elev.toFixed(0)}
          </text>
        </g>
        {/* Axis labels */}
        <text x={W / 2} y={H - 2} fontSize="7" fontFamily="monospace" fill="#8A9BBA" textAnchor="middle" data-id="element-10874">
          Distance from center (m)
        </text>
      </svg>
    </div>;
}
function PropRow({
  label,
  children,
  readonly = false
}: {
  label: string;
  children: React.ReactNode;
  readonly?: boolean;
}) {
  return <div className="grid grid-cols-[110px_1fr] items-center gap-2 px-3 py-1" data-id="element-10875">
      <span className={`text-[10px] ${readonly ? 'text-[#8A9BBA]' : 'text-[#C5D1E4]'} truncate`} data-id="element-10876">
        {label}
      </span>
      <div className="min-w-0" data-id="element-10877">{children}</div>
    </div>;
}
function InputWithUnit({
  value,
  unit,
  onChange,
  type = 'text'
}: {
  value: string | number;
  unit: string;
  onChange: (v: any) => void;
  type?: string;
}) {
  return <div className="flex items-center gap-1" data-id="element-10878">
      <input type={type} value={value} onChange={e => onChange(type === 'number' ? parseFloat(e.target.value) : e.target.value)} className="flex-1 min-w-0 bg-[#132038] border border-[#2F4268] rounded px-2 py-0.5 text-[11px] text-[#F1F5FB] font-mono focus:outline-none focus:border-[#00D4FF] transition-colors" data-id="element-10879" />
      {unit && <span className="w-4 text-[10px] text-[#8A9BBA] font-mono flex-shrink-0" data-id="element-10880">
          {unit}
        </span>}
    </div>;
}
function InlineToggle({
  value,
  onChange
}: {
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return <button onClick={() => onChange(!value)} className={`relative w-8 h-4 rounded-full transition-colors ${value ? 'bg-[#00D4FF]' : 'bg-[#2F4268]'}`} role="switch" aria-checked={value} data-id="element-10881">
      <span className={`absolute top-[3px] w-2.5 h-2.5 rounded-full bg-white shadow-sm transition-transform ${value ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} data-id="element-10882" />
    </button>;
}
function InlineColor({
  value,
  onChange
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return <div className="flex items-center gap-1.5" data-id="element-10883">
      <input type="color" value={value} onChange={e => onChange(e.target.value)} className="w-5 h-5 rounded cursor-pointer border border-[#2F4268] bg-transparent flex-shrink-0" style={{
      padding: 0
    }} data-id="element-10884" />
      <input type="text" value={value} onChange={e => onChange(e.target.value)} className="flex-1 min-w-0 bg-[#132038] border border-[#2F4268] rounded px-2 py-0.5 text-[11px] text-[#F1F5FB] font-mono uppercase focus:outline-none focus:border-[#00D4FF] transition-colors" data-id="element-10885" />
    </div>;
}
function InlineSlider({
  value,
  min,
  max,
  step = 1,
  unit,
  onChange
}: {
  value: number;
  min: number;
  max: number;
  step?: number;
  unit: string;
  onChange: (v: number) => void;
}) {
  return <div className="flex items-center gap-2" data-id="element-10886">
      <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(parseFloat(e.target.value))} className="flex-1 min-w-0 h-1 accent-[#00D4FF] cursor-pointer" data-id="element-10887" />
      <span className="w-10 text-right text-[10px] text-[#C5D1E4] font-mono flex-shrink-0" data-id="element-10888">
        {value}
        {unit}
      </span>
    </div>;
}
function LayerControlPanel({
  pivots,
  globalLayers,
  onTogglePivotVisibility,
  onTogglePivotLayer,
  onToggleGlobalLayer,
  onSelectPivot,
  hydraulicObjects,
  onSelectHydraulic
}: {
  pivots: Pivot[];
  globalLayers: GlobalLayers;
  onTogglePivotVisibility: (id: number) => void;
  onTogglePivotLayer: (id: number, key: PivotSubLayerKey) => void;
  onToggleGlobalLayer: (key: keyof GlobalLayers) => void;
  onSelectPivot: (id: number) => void;
  hydraulicObjects: HydraulicObject[];
  onSelectHydraulic: (sel: HydraulicSelection) => void;
}) {
  const [expandedPivots, setExpandedPivots] = useState<Set<number>>(new Set());
  const togglePivotExpanded = (id: number) => {
    setExpandedPivots(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  return <aside className="w-[280px] bg-[#0b1628] border-l border-[#2F4268] flex flex-col flex-shrink-0" data-id="element-10889">
      <div className="flex items-center gap-2 px-3 h-[42px] border-b border-[#2F4268] flex-shrink-0" data-id="element-10890">
        <LayersIcon size={14} className="text-[#00D4FF] flex-shrink-0" data-id="element-10891" />
        <span className="text-[#F1F5FB] text-xs font-semibold" data-id="element-10892">
          Layer Control
        </span>
      </div>

      <div className="flex-1 overflow-y-auto" data-id="element-10893">
        {/* Global layers */}
        <div className="border-b border-[#2F4268]/60" data-id="element-10894">
          <div className="px-3 py-1.5 bg-[#0a1322]" data-id="element-10895">
            <span className="text-[10px] text-[#8A9BBA] uppercase tracking-wide font-semibold" data-id="element-10896">
              Global
            </span>
          </div>
          <div className="py-1" data-id="element-10897">
            <LayerRow label="Crosshair" icon={<CrosshairIcon size={12} data-id="element-10899" />} visible={globalLayers.crosshair} onToggle={() => onToggleGlobalLayer('crosshair')} data-id="element-10898" />
            <LayerRow label="Base Map" icon={<MapIcon size={12} data-id="element-10901" />} visible={globalLayers.baseMap} onToggle={() => onToggleGlobalLayer('baseMap')} data-id="element-10900" />
            <LayerRow label="Hydraulic Network" icon={<DropletIcon size={12} data-id="element-10903" />} visible={globalLayers.hydraulicNetwork} onToggle={() => onToggleGlobalLayer('hydraulicNetwork')} data-id="element-10902" />
          </div>
        </div>

        {/* Hydraulic Network Summary */}
        <HydraulicNetworkSummarySection hydraulicObjects={hydraulicObjects} pivots={pivots} onSelectHydraulic={onSelectHydraulic} onSelectPivot={onSelectPivot} data-id="element-10904" />

        {/* Pivots */}
        <div data-id="element-10905">
          <div className="px-3 py-1.5 bg-[#0a1322] flex items-center justify-between" data-id="element-10906">
            <span className="text-[10px] text-[#8A9BBA] uppercase tracking-wide font-semibold" data-id="element-10907">
              Pivots
            </span>
            <span className="text-[10px] text-[#8A9BBA] font-mono" data-id="element-10908">
              {pivots.length}
            </span>
          </div>
          {pivots.length === 0 ? <div className="px-3 py-4 text-center" data-id="element-10909">
              <p className="text-[11px] text-[#8A9BBA] italic" data-id="element-10910">
                No pivots placed yet.
              </p>
            </div> : <ul data-id="element-10911">
              {pivots.map(pivot => {
            const isExpanded = expandedPivots.has(pivot.id);
            return <li key={pivot.id} className="border-b border-[#2F4268]/40" data-id="element-10912">
                    <div className="flex items-center gap-1 px-2 py-1.5 hover:bg-white/[0.02] transition-colors" data-id="element-10913">
                      <button onClick={() => togglePivotExpanded(pivot.id)} className="text-[#8A9BBA] hover:text-[#F1F5FB] p-0.5 rounded transition-colors flex-shrink-0" data-id="element-10914">
                        {isExpanded ? <ChevronDownIcon size={11} data-id="element-10915" /> : <ChevronRightIcon size={11} data-id="element-10916" />}
                      </button>
                      <button onClick={() => onTogglePivotVisibility(pivot.id)} className="text-[#8A9BBA] hover:text-[#F1F5FB] p-0.5 rounded transition-colors flex-shrink-0" title={pivot.visible ? 'Hide pivot' : 'Show pivot'} data-id="element-10917">
                        {pivot.visible ? <EyeIcon size={12} data-id="element-10918" /> : <EyeOffIcon size={12} data-id="element-10919" />}
                      </button>
                      <button onClick={() => onSelectPivot(pivot.id)} className="flex-1 min-w-0 text-left text-[11px] text-[#F1F5FB] hover:text-[#00D4FF] transition-colors truncate" data-id="element-10920">
                        {pivot.name}
                      </button>
                    </div>
                    {isExpanded && <div className="bg-[#0a1322]/60 py-0.5 pl-7" data-id="element-10921">
                        <SubLayerRow label="Outline" icon={<CircleIcon size={10} data-id="element-10923" />} visible={pivot.layers.outline} onToggle={() => onTogglePivotLayer(pivot.id, 'outline')} data-id="element-10922" />
                        <SubLayerRow label="Center" icon={<DotIcon size={10} data-id="element-10925" />} visible={pivot.layers.center} onToggle={() => onTogglePivotLayer(pivot.id, 'center')} data-id="element-10924" />
                        <SubLayerRow label="Arm" icon={<MinusIcon size={10} data-id="element-10927" />} visible={pivot.layers.arm} onToggle={() => onTogglePivotLayer(pivot.id, 'arm')} data-id="element-10926" />
                        <SubLayerRow label="Label" icon={<TagIcon size={10} data-id="element-10929" />} visible={pivot.layers.label} onToggle={() => onTogglePivotLayer(pivot.id, 'label')} data-id="element-10928" />
                        <SubLayerRow label="Towers" icon={<ZapIcon size={10} data-id="element-10931" />} visible={pivot.layers.towers} onToggle={() => onTogglePivotLayer(pivot.id, 'towers')} data-id="element-10930" />
                        <SubLayerRow label="Contours" icon={<HexagonIcon size={10} data-id="element-10933" />} visible={pivot.layers.contours} onToggle={() => onTogglePivotLayer(pivot.id, 'contours')} data-id="element-10932" />
                      </div>}
                  </li>;
          })}
            </ul>}
        </div>
      </div>
    </aside>;
}
function LayerRow({
  label,
  icon,
  visible,
  onToggle
}: {
  label: string;
  icon: React.ReactNode;
  visible: boolean;
  onToggle: () => void;
}) {
  return <div className="flex items-center gap-2 px-3 py-1 hover:bg-white/[0.02] transition-colors" data-id="element-10934">
      <button onClick={onToggle} className="text-[#8A9BBA] hover:text-[#F1F5FB] p-0.5 rounded transition-colors flex-shrink-0" title={visible ? `Hide ${label}` : `Show ${label}`} data-id="element-10935">
        {visible ? <EyeIcon size={12} data-id="element-10936" /> : <EyeOffIcon size={12} data-id="element-10937" />}
      </button>
      <span className="text-[#8A9BBA] flex-shrink-0" data-id="element-10938">{icon}</span>
      <span className="text-[11px] text-[#C5D1E4] truncate" data-id="element-10939">{label}</span>
    </div>;
}
function SubLayerRow({
  label,
  icon,
  visible,
  onToggle
}: {
  label: string;
  icon: React.ReactNode;
  visible: boolean;
  onToggle: () => void;
}) {
  return <div className="flex items-center gap-1.5 px-2 py-0.5 hover:bg-white/[0.02] transition-colors" data-id="element-10940">
      <button onClick={onToggle} className="text-[#8A9BBA] hover:text-[#F1F5FB] p-0.5 rounded transition-colors flex-shrink-0" data-id="element-10941">
        {visible ? <EyeIcon size={10} data-id="element-10942" /> : <EyeOffIcon size={10} data-id="element-10943" />}
      </button>
      <span className="text-[#8A9BBA] flex-shrink-0" data-id="element-10944">{icon}</span>
      <span className="text-[10px] text-[#C5D1E4] truncate" data-id="element-10945">{label}</span>
    </div>;
}
// ─── Hydraulic Network — Phase 1: Properties Panel ──────────────────────────
function hydraulicKindLabel(kind: HydraulicKind): string {
  switch (kind) {
    case 'water-source':
      return 'Water Source';
    case 'pipe':
      return 'Pipe';
    case 'node':
      return 'Node / Junction';
    case 'valve':
      return 'Valve';
    case 'pivot-connection':
      return 'Pivot Connection';
  }
}
function HydraulicKindIcon({
  kind,
  size = 14
}: {
  kind: HydraulicKind;
  size?: number;
}) {
  const cls = 'text-[#00D4FF] flex-shrink-0';
  switch (kind) {
    case 'water-source':
      return <DropletIcon size={size} className={cls} data-id="element-10946" />;
    case 'pipe':
      return <MinusIcon size={size} className={cls} data-id="element-10947" />;
    case 'node':
      return <CircleIcon size={size} className={cls} data-id="element-10948" />;
    case 'valve':
      return <HexagonIcon size={size} className={cls} data-id="element-10949" />;
    case 'pivot-connection':
      return <ZapIcon size={size} className={cls} data-id="element-10950" />;
  }
}
type HydraulicPanelMode = 'info' | 'summary';
// Nominal real-world scale for Phase 1: 1% of canvas dimension ≈ 10 m
// (so a full 100% canvas is ~1 km across). Used for pipe length display
// until a real geo-scale is introduced.
const HYDRAULIC_CANVAS_SCALE_M_PER_PCT = 10;
function HydraulicPropertiesPanel({
  selection,
  objects,
  pivots,
  onUpdate,
  onDelete,
  onClose,
  onSelect
}: {
  selection: {
    kind: HydraulicKind;
    id: string;
  };
  objects: HydraulicObject[];
  pivots: Pivot[];
  onUpdate: (id: string, updates: Partial<HydraulicObject>) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
  onSelect: (sel: {
    kind: HydraulicKind;
    id: string;
  } | null) => void;
}) {
  const [mode, setMode] = useState<HydraulicPanelMode>('info');
  const obj = objects.find(o => o.id === selection.id);
  if (!obj) {
    return <aside className="w-[360px] bg-[#0b1628] border-l border-[#2F4268] flex flex-col flex-shrink-0" data-id="element-10951">
        <div className="flex items-center justify-between px-3 h-[42px] border-b border-[#2F4268] flex-shrink-0" data-id="element-10952">
          <span className="text-[#F1F5FB] text-xs font-semibold" data-id="element-10953">
            Not Found
          </span>
          <button onClick={onClose} className="text-[#8A9BBA] hover:text-[#F1F5FB] p-1 rounded transition-colors" data-id="element-10954">
            <XIcon size={14} data-id="element-10955" />
          </button>
        </div>
        <div className="p-3 text-[11px] text-[#8A9BBA] italic" data-id="element-10956">
          The selected hydraulic object no longer exists.
        </div>
      </aside>;
  }
  // Pipes can connect to non-pipe hydraulic objects or directly to a pivot center.
  // Pipe-to-pipe is supported only via canvas drag (auto-creates a junction node).
  type EndpointOption = {
    value: string;
    label: string;
  };
  const pipeEndpointCandidates: EndpointOption[] = [...objects.filter(o => o.kind !== 'pipe').map(c => ({
    value: c.id,
    label: `${hydraulicKindLabel(c.kind)}: ${c.name}`
  })), ...pivots.map(p => ({
    value: `pivot:${p.id}`,
    label: `Pivot: ${p.name}`
  }))];
  const endpointLabel = (linkId: string | null): string => {
    if (!linkId) return '';
    if (linkId.startsWith('pivot:')) {
      const pid = Number(linkId.slice('pivot:'.length));
      const p = pivots.find(pp => pp.id === pid);
      return p ? `Pivot: ${p.name}` : '';
    }
    const target = objects.find(o => o.id === linkId);
    return target ? `${hydraulicKindLabel(target.kind)}: ${target.name}` : '';
  };
  return <aside className="w-[360px] bg-[#0b1628] border-l border-[#2F4268] flex flex-row flex-shrink-0" data-id="element-10957">
      {/* Vertical mode rail — mirrors PivotPropertiesPanel */}
      <div className="w-9 bg-[#0a1322] border-r border-[#2F4268] flex flex-col items-center py-2 flex-shrink-0" data-id="element-10958">
        <ModeRailButton active={mode === 'info'} onClick={() => setMode('info')} icon={<InfoIcon size={14} data-id="element-10960" />} label="Information" data-id="element-10959" />
        <ModeRailButton active={mode === 'summary'} onClick={() => setMode('summary')} icon={<ActivityIcon size={14} data-id="element-10962" />} label="Hydraulic Summary" data-id="element-10961" />
      </div>

      <div className="flex-1 flex flex-col min-w-0" data-id="element-10963">
        {/* Header */}
        <div className="flex items-center gap-2 px-3 h-[42px] border-b border-[#2F4268] flex-shrink-0" data-id="element-10964">
          <HydraulicKindIcon kind={obj.kind} data-id="element-10965" />
          <div className="flex-1 min-w-0" data-id="element-10966">
            <div className="text-[10px] text-[#8A9BBA] uppercase tracking-wide leading-none" data-id="element-10967">
              {hydraulicKindLabel(obj.kind)}
            </div>
            <div className="text-[#F1F5FB] text-xs font-semibold truncate leading-tight" data-id="element-10968">
              {obj.name}
            </div>
          </div>
          <button onClick={onClose} className="text-[#8A9BBA] hover:text-[#F1F5FB] p-1 rounded transition-colors flex-shrink-0" title="Close" data-id="element-10969">
            <XIcon size={14} data-id="element-10970" />
          </button>
        </div>

        {mode === 'summary' ? <HydraulicSummaryView obj={obj} objects={objects} pivots={pivots} data-id="element-10971" /> : <div className="flex-1 overflow-y-auto" data-id="element-10972">
            {/* General */}
            <HydraulicSection title="General" data-id="element-10973">
              <HydraulicTextField label="Name" value={obj.name} onChange={v => onUpdate(obj.id, {
            name: v
          } as Partial<HydraulicObject>)} data-id="element-10974" />
              <HydraulicToggleField label="Visible" value={obj.visible} onChange={v => onUpdate(obj.id, {
            visible: v
          } as Partial<HydraulicObject>)} data-id="element-10975" />
            </HydraulicSection>

            {/* Type-specific fields */}
            {obj.kind === 'water-source' && <>
                <HydraulicSection title="Source" data-id="element-10976">
                  <HydraulicSelectField label="Type" value={obj.sourceType} options={[{
              value: 'well',
              label: 'Well'
            }, {
              value: 'reservoir',
              label: 'Reservoir'
            }, {
              value: 'canal',
              label: 'Canal'
            }, {
              value: 'river',
              label: 'River'
            }, {
              value: 'existing-pipeline',
              label: 'Existing Pipeline'
            }]} onChange={v => onUpdate(obj.id, {
              sourceType: v as HydraulicWaterSource['sourceType']
            } as Partial<HydraulicObject>)} data-id="element-10977" />
                  <HydraulicNumberField label="Elevation" unit="m" value={obj.elevation} onChange={v => onUpdate(obj.id, {
              elevation: v
            } as Partial<HydraulicObject>)} data-id="element-10978" />
                </HydraulicSection>
                <HydraulicSection title="Supply" data-id="element-10979">
                  <HydraulicNumberField label="Avail. Flow" unit="GPM" value={obj.availableFlow} onChange={v => onUpdate(obj.id, {
              availableFlow: v
            } as Partial<HydraulicObject>)} data-id="element-10980" />
                  <HydraulicNumberField label="Avail. Pressure" unit="PSI" value={obj.availablePressure} onChange={v => onUpdate(obj.id, {
              availablePressure: v
            } as Partial<HydraulicObject>)} data-id="element-10981" />
                </HydraulicSection>
              </>}

            {obj.kind === 'node' && <HydraulicSection title="Node" data-id="element-10982">
                <HydraulicSelectField label="Type" value={obj.nodeType} options={[{
            value: 'junction',
            label: 'Junction'
          }, {
            value: 'tee',
            label: 'Tee'
          }, {
            value: 'elbow',
            label: 'Elbow'
          }, {
            value: 'reducer',
            label: 'Reducer'
          }, {
            value: 'end-node',
            label: 'End Node'
          }]} onChange={v => onUpdate(obj.id, {
            nodeType: v as HydraulicNode['nodeType']
          } as Partial<HydraulicObject>)} data-id="element-10983" />
                <HydraulicNumberField label="Elevation" unit="m" value={obj.elevation} onChange={v => onUpdate(obj.id, {
            elevation: v
          } as Partial<HydraulicObject>)} data-id="element-10984" />
              </HydraulicSection>}

            {obj.kind === 'valve' && <HydraulicSection title="Valve" data-id="element-10985">
                <HydraulicSelectField label="Type" value={obj.valveType} options={[{
            value: 'manual',
            label: 'Manual'
          }, {
            value: 'electric',
            label: 'Electric'
          }, {
            value: 'check',
            label: 'Check'
          }, {
            value: 'air',
            label: 'Air'
          }, {
            value: 'pressure-reducing',
            label: 'Pressure Reducing'
          }]} onChange={v => onUpdate(obj.id, {
            valveType: v as HydraulicValve['valveType']
          } as Partial<HydraulicObject>)} data-id="element-10986" />
                <HydraulicNumberField label="Diameter" unit="mm" value={obj.diameter} onChange={v => onUpdate(obj.id, {
            diameter: v
          } as Partial<HydraulicObject>)} data-id="element-10987" />
                <HydraulicSelectField label="Status" value={obj.status} options={[{
            value: 'open',
            label: 'Open'
          }, {
            value: 'closed',
            label: 'Closed'
          }, {
            value: 'unknown',
            label: 'Unknown'
          }]} onChange={v => onUpdate(obj.id, {
            status: v as HydraulicValve['status']
          } as Partial<HydraulicObject>)} data-id="element-10988" />
              </HydraulicSection>}

            {obj.kind === 'pivot-connection' && <>
                <HydraulicSection title="Pivot Link" data-id="element-10989">
                  <HydraulicSelectField label="Pivot" value={obj.pivotId === null ? '' : String(obj.pivotId)} options={[{
              value: '',
              label: '— Not connected —'
            }, ...pivots.map(p => ({
              value: String(p.id),
              label: p.name
            }))]} onChange={v => onUpdate(obj.id, {
              pivotId: v === '' ? null : Number(v)
            } as Partial<HydraulicObject>)} data-id="element-10990" />
                  {obj.pivotId === null && <div className="flex items-start gap-1.5 px-3 pt-1" data-id="element-10991">
                      <AlertTriangleIcon size={11} className="text-amber-400 flex-shrink-0 mt-px" data-id="element-10992" />
                      <span className="text-[10px] text-amber-400 leading-tight" data-id="element-10993">
                        Not linked to a pivot.
                      </span>
                    </div>}
                  <HydraulicNumberField label="Elevation" unit="m" value={obj.elevation} onChange={v => onUpdate(obj.id, {
              elevation: v
            } as Partial<HydraulicObject>)} data-id="element-10994" />
                </HydraulicSection>
                <HydraulicSection title="Demand" data-id="element-10995">
                  <HydraulicNumberField label="Req. Flow" unit="GPM" value={obj.requiredFlow} onChange={v => onUpdate(obj.id, {
              requiredFlow: v
            } as Partial<HydraulicObject>)} data-id="element-10996" />
                  <HydraulicNumberField label="Req. Pressure" unit="PSI" value={obj.requiredPressure} onChange={v => onUpdate(obj.id, {
              requiredPressure: v
            } as Partial<HydraulicObject>)} data-id="element-10997" />
                </HydraulicSection>
              </>}

            {obj.kind === 'pipe' && <>
                <HydraulicSection title="Pipe" data-id="element-10998">
                  <HydraulicSelectField label="Type" value={obj.pipeType} options={[{
              value: 'main',
              label: 'Main'
            }, {
              value: 'secondary',
              label: 'Secondary'
            }, {
              value: 'pivot-connection',
              label: 'Pivot Connection'
            }]} onChange={v => onUpdate(obj.id, {
              pipeType: v as HydraulicPipe['pipeType']
            } as Partial<HydraulicObject>)} data-id="element-10999" />
                  <HydraulicSelectField label="Material" value={obj.material} options={[{
              value: 'PVC',
              label: 'PVC'
            }, {
              value: 'HDPE',
              label: 'HDPE'
            }, {
              value: 'Steel',
              label: 'Steel'
            }, {
              value: 'Ductile Iron',
              label: 'Ductile Iron'
            }, {
              value: 'Concrete',
              label: 'Concrete'
            }]} onChange={v => onUpdate(obj.id, {
              material: v
            } as Partial<HydraulicObject>)} data-id="element-11000" />
                  <HydraulicNumberField label="Diameter" unit="mm" value={obj.diameter} onChange={v => onUpdate(obj.id, {
              diameter: v
            } as Partial<HydraulicObject>)} data-id="element-11001" />
                  <HydraulicNumberField label="Pressure Rating" unit="bar" value={obj.pressureRating} onChange={v => onUpdate(obj.id, {
              pressureRating: v
            } as Partial<HydraulicObject>)} data-id="element-11002" />
                </HydraulicSection>
                <HydraulicSection title="Connections" data-id="element-11003">
                  <HydraulicSelectField label="Start" value={obj.startObjectId ?? ''} options={[{
              value: '',
              label: '— Not connected —'
            }, ...pipeEndpointCandidates]} onChange={v => onUpdate(obj.id, {
              startObjectId: v === '' ? null : v
            } as Partial<HydraulicObject>)} data-id="element-11004" />
                  <HydraulicSelectField label="End" value={obj.endObjectId ?? ''} options={[{
              value: '',
              label: '— Not connected —'
            }, ...pipeEndpointCandidates]} onChange={v => onUpdate(obj.id, {
              endObjectId: v === '' ? null : v
            } as Partial<HydraulicObject>)} data-id="element-11005" />
                  <div className="px-3 pt-1" data-id="element-11006">
                    <p className="text-[10px] text-[#8A9BBA]/80 leading-tight" data-id="element-11007">
                      Tip: drag the pipe's endpoint handles on the canvas to
                      connect. Dropping onto another pipe creates a junction
                      automatically.
                    </p>
                  </div>
                  {(obj.startObjectId === null || obj.endObjectId === null) && <div className="flex items-start gap-1.5 px-3 pt-1" data-id="element-11008">
                      <AlertTriangleIcon size={11} className="text-amber-400 flex-shrink-0 mt-px" data-id="element-11009" />
                      <span className="text-[10px] text-amber-400 leading-tight" data-id="element-11010">
                        Pipe has at least one disconnected end.
                      </span>
                    </div>}
                  {(obj.startObjectId || obj.endObjectId) && <div className="flex flex-col gap-1 pt-1 px-3" data-id="element-11011">
                      {obj.startObjectId && !obj.startObjectId.startsWith('pivot:') && <button onClick={() => {
                const target = objects.find(o => o.id === obj.startObjectId);
                if (target) onSelect({
                  kind: target.kind,
                  id: target.id
                });
              }} className="text-[10px] text-[#00D4FF] hover:underline text-left truncate" data-id="element-11012">
                            → Start: {endpointLabel(obj.startObjectId)}
                          </button>}
                      {obj.startObjectId && obj.startObjectId.startsWith('pivot:') && <span className="text-[10px] text-[#C5D1E4] truncate" data-id="element-11013">
                            Start: {endpointLabel(obj.startObjectId)}
                          </span>}
                      {obj.endObjectId && !obj.endObjectId.startsWith('pivot:') && <button onClick={() => {
                const target = objects.find(o => o.id === obj.endObjectId);
                if (target) onSelect({
                  kind: target.kind,
                  id: target.id
                });
              }} className="text-[10px] text-[#00D4FF] hover:underline text-left truncate" data-id="element-11014">
                            → End: {endpointLabel(obj.endObjectId)}
                          </button>}
                      {obj.endObjectId && obj.endObjectId.startsWith('pivot:') && <span className="text-[10px] text-[#C5D1E4] truncate" data-id="element-11015">
                            End: {endpointLabel(obj.endObjectId)}
                          </span>}
                    </div>}
                </HydraulicSection>
              </>}

            {/* Position (not shown for pipes — geometry is derived from endpoints) */}
            {obj.kind !== 'pipe' && <HydraulicSection title="Position" data-id="element-11016">
                <HydraulicNumberField label="X" unit="%" value={Number((obj as {
            xPct: number;
          }).xPct.toFixed(1))} onChange={v => onUpdate(obj.id, {
            xPct: v
          } as Partial<HydraulicObject>)} data-id="element-11017" />
                <HydraulicNumberField label="Y" unit="%" value={Number((obj as {
            yPct: number;
          }).yPct.toFixed(1))} onChange={v => onUpdate(obj.id, {
            yPct: v
          } as Partial<HydraulicObject>)} data-id="element-11018" />
              </HydraulicSection>}

            {/* Notes */}
            <HydraulicSection title="Notes" defaultOpen={false} data-id="element-11019">
              <div className="px-3 py-1" data-id="element-11020">
                <textarea value={obj.notes} onChange={e => onUpdate(obj.id, {
              notes: e.target.value
            } as Partial<HydraulicObject>)} rows={3} placeholder="Add notes…" className="w-full bg-[#132038] border border-[#2F4268] rounded px-2 py-1.5 text-[11px] text-[#F1F5FB] placeholder:text-[#8A9BBA]/60 focus:outline-none focus:border-[#00D4FF] resize-none" data-id="element-11021" />
              </div>
            </HydraulicSection>

            {/* Danger zone */}
            <div className="px-3 py-3 border-t border-[#2F4268]/60" data-id="element-11022">
              <button onClick={() => onDelete(obj.id)} className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 hover:border-red-500/60 rounded text-[11px] text-red-400 hover:text-red-300 transition-colors" data-id="element-11023">
                <TrashIcon size={11} data-id="element-11024" />
                <span data-id="element-11025">Delete {hydraulicKindLabel(obj.kind)}</span>
              </button>
            </div>
          </div>}
      </div>
    </aside>;
}
// Wraps CollapsibleSection so HydraulicPropertiesPanel sections look & feel
// identical to the PivotPropertiesPanel sections (collapsible header + body).
function HydraulicSection({
  title,
  children,
  defaultOpen = true
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  return <CollapsibleSection label={title} defaultOpen={defaultOpen} data-id="element-11026">
      {children}
    </CollapsibleSection>;
}
function HydraulicTextField({
  label,
  value,
  onChange
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return <PropRow label={label} data-id="element-11027">
      <input type="text" value={value} onChange={e => onChange(e.target.value)} className="w-full bg-[#132038] border border-[#2F4268] rounded px-2 py-0.5 text-[11px] text-[#F1F5FB] focus:outline-none focus:border-[#00D4FF] transition-colors" data-id="element-11028" />
    </PropRow>;
}
function HydraulicNumberField({
  label,
  value,
  unit,
  onChange
}: {
  label: string;
  value: number;
  unit: string;
  onChange: (v: number) => void;
}) {
  return <PropRow label={label} data-id="element-11029">
      <InputWithUnit type="number" value={value} unit={unit} onChange={v => {
      const n = typeof v === 'number' ? v : parseFloat(v);
      if (!Number.isNaN(n)) onChange(n);
    }} data-id="element-11030" />
    </PropRow>;
}
function HydraulicSelectField({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: string;
  options: {
    value: string;
    label: string;
  }[];
  onChange: (v: string) => void;
}) {
  return <PropRow label={label} data-id="element-11031">
      <select value={value} onChange={e => onChange(e.target.value)} className="w-full bg-[#132038] border border-[#2F4268] rounded px-2 py-0.5 text-[11px] text-[#F1F5FB] focus:outline-none focus:border-[#00D4FF] transition-colors" data-id="element-11032">
        {options.map(opt => <option key={opt.value} value={opt.value} data-id="element-11033">
            {opt.label}
          </option>)}
      </select>
    </PropRow>;
}
function HydraulicToggleField({
  label,
  value,
  onChange
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return <PropRow label={label} data-id="element-11034">
      <InlineToggle value={value} onChange={onChange} data-id="element-11035" />
    </PropRow>;
}
// ─── Hydraulic Summary view ─────────────────────────────────────────────────
// Phase 1: shows a structured technical summary per element kind. Pipe gets the
// full spec (Geometry, Flow, Velocity, Pressure, Capacity, Status). Other
// objects get a slimmer, kind-specific summary so the UI/UX stays consistent.
function HydStatusBadge({
  status
}: {
  status: 'ok' | 'warn' | 'critical' | 'unknown' | 'info';
}) {
  const styles: Record<typeof status, string> = {
    ok: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    warn: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    critical: 'bg-red-500/15 text-red-300 border-red-500/30',
    unknown: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
    info: 'bg-sky-500/15 text-sky-300 border-sky-500/30'
  } as Record<'ok' | 'warn' | 'critical' | 'unknown' | 'info', string>;
  const label: Record<'ok' | 'warn' | 'critical' | 'unknown' | 'info', string> = {
    ok: 'Acceptable',
    warn: 'Warning',
    critical: 'Critical',
    unknown: 'Not calculated',
    info: 'Info'
  };
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-medium ${styles[status]}`} data-id="element-11036">
      {label[status]}
    </span>;
}
function HydReadoutValue({
  value,
  unit,
  muted = false
}: {
  value: React.ReactNode;
  unit?: string;
  muted?: boolean;
}) {
  return <span className={`text-[11px] font-mono ${muted ? 'text-[#8A9BBA] italic' : 'text-[#C5D1E4]'}`} data-id="element-11037">
      {value}
      {unit && <span className="ml-1 text-[10px] text-[#8A9BBA]" data-id="element-11038">{unit}</span>}
    </span>;
}
function HydraulicSummaryView({
  obj,
  objects,
  pivots
}: {
  obj: HydraulicObject;
  objects: HydraulicObject[];
  pivots: Pivot[];
}) {
  // Helper to resolve a pipe endpoint's elevation + canvas position.
  const resolveEndpoint = (pipe: HydraulicPipe, which: 'start' | 'end'): {
    elevation: number | null;
    x: number;
    y: number;
    connected: boolean;
    label: string;
  } => {
    const linkId = which === 'start' ? pipe.startObjectId : pipe.endObjectId;
    const fallbackPos = which === 'start' ? pipe.startPos ?? {
      x: 45,
      y: 50
    } : pipe.endPos ?? {
      x: 55,
      y: 50
    };
    if (!linkId) return {
      elevation: null,
      x: fallbackPos.x,
      y: fallbackPos.y,
      connected: false,
      label: 'Free endpoint'
    };
    if (linkId.startsWith('pivot:')) {
      const pid = Number(linkId.slice('pivot:'.length));
      const p = pivots.find(pp => pp.id === pid);
      return {
        elevation: null,
        x: p?.cxPct ?? fallbackPos.x,
        y: p?.cyPct ?? fallbackPos.y,
        connected: !!p,
        label: p ? `Pivot: ${p.name}` : 'Pivot (missing)'
      };
    }
    const target = objects.find(o => o.id === linkId);
    if (target && 'xPct' in target) {
      const elev = target.kind === 'water-source' || target.kind === 'node' || target.kind === 'pivot-connection' ? (target as {
        elevation: number;
      }).elevation : null;
      return {
        elevation: elev,
        x: target.xPct,
        y: target.yPct,
        connected: true,
        label: `${hydraulicKindLabel(target.kind)}: ${target.name}`
      };
    }
    return {
      elevation: null,
      x: fallbackPos.x,
      y: fallbackPos.y,
      connected: false,
      label: 'Missing target'
    };
  };
  // ─── Pipe-specific full summary ──────────────────────────────────────────
  if (obj.kind === 'pipe') {
    const pipe = obj;
    const start = resolveEndpoint(pipe, 'start');
    const end = resolveEndpoint(pipe, 'end');
    const dxPct = end.x - start.x;
    const dyPct = end.y - start.y;
    const distPct = Math.hypot(dxPct, dyPct);
    const lengthM = distPct * HYDRAULIC_CANVAS_SCALE_M_PER_PCT;
    const elevDiff = start.elevation !== null && end.elevation !== null ? end.elevation - start.elevation : null;
    const internalDiameter = pipe.diameter * 0.95; // nominal estimate
    const startConnected = pipe.startObjectId !== null;
    const endConnected = pipe.endObjectId !== null;
    const fullyConnected = startConnected && endConnected;
    const hasDiameter = pipe.diameter > 0;
    const hasMaterial = !!pipe.material;
    // Resolve overall hydraulic status & main warning
    let status: 'ok' | 'warn' | 'critical' | 'unknown' = 'unknown';
    let mainWarning: string | null = null;
    if (!fullyConnected) {
      status = 'critical';
      mainWarning = 'Pipe disconnected';
    } else if (!hasDiameter) {
      status = 'critical';
      mainWarning = 'Missing diameter';
    } else if (!hasMaterial) {
      status = 'warn';
      mainWarning = 'Missing material';
    } else {
      status = 'unknown';
      mainWarning = null;
    }
    return <div className="flex-1 overflow-y-auto" data-id="element-11039">
        <HydraulicSection title="Geometry / Elevation" data-id="element-11040">
          <PropRow label="Length" readonly data-id="element-11041">
            <HydReadoutValue value={lengthM.toFixed(1)} unit="m" muted={distPct < 0.01} data-id="element-11042" />
          </PropRow>
          <PropRow label="Start elevation" readonly data-id="element-11043">
            {start.elevation !== null ? <HydReadoutValue value={start.elevation.toFixed(1)} unit="m" data-id="element-11044" /> : <HydReadoutValue value="—" muted data-id="element-11045" />}
          </PropRow>
          <PropRow label="End elevation" readonly data-id="element-11046">
            {end.elevation !== null ? <HydReadoutValue value={end.elevation.toFixed(1)} unit="m" data-id="element-11047" /> : <HydReadoutValue value="—" muted data-id="element-11048" />}
          </PropRow>
          <PropRow label="Elevation diff" readonly data-id="element-11049">
            {elevDiff !== null ? <HydReadoutValue value={`${elevDiff >= 0 ? '+' : ''}${elevDiff.toFixed(1)}`} unit="m" data-id="element-11050" /> : <HydReadoutValue value="—" muted data-id="element-11051" />}
          </PropRow>
        </HydraulicSection>

        <HydraulicSection title="Flow" data-id="element-11052">
          <PropRow label="Assigned flow" readonly data-id="element-11053">
            <HydReadoutValue value="Not calculated" muted data-id="element-11054" />
          </PropRow>
          <PropRow label="Flow source" readonly data-id="element-11055">
            <HydReadoutValue value="—" muted data-id="element-11056" />
          </PropRow>
        </HydraulicSection>

        <HydraulicSection title="Velocity" data-id="element-11057">
          <PropRow label="Water velocity" readonly data-id="element-11058">
            <HydReadoutValue value="Not calculated" muted data-id="element-11059" />
          </PropRow>
          <PropRow label="Velocity status" readonly data-id="element-11060">
            <HydStatusBadge status="unknown" data-id="element-11061" />
          </PropRow>
        </HydraulicSection>

        <HydraulicSection title="Pressure / Losses" data-id="element-11062">
          <PropRow label="Head loss" readonly data-id="element-11063">
            <HydReadoutValue value="Not calculated" muted data-id="element-11064" />
          </PropRow>
          <PropRow label="Head loss / 100 m" readonly data-id="element-11065">
            <HydReadoutValue value="Not calculated" muted data-id="element-11066" />
          </PropRow>
          <PropRow label="Pressure drop" readonly data-id="element-11067">
            <HydReadoutValue value="Not calculated" muted data-id="element-11068" />
          </PropRow>
        </HydraulicSection>

        <HydraulicSection title="Pipe Capacity" data-id="element-11069">
          <PropRow label="Diameter" readonly data-id="element-11070">
            {hasDiameter ? <HydReadoutValue value={pipe.diameter} unit="mm" data-id="element-11071" /> : <HydReadoutValue value="—" muted data-id="element-11072" />}
          </PropRow>
          <PropRow label="Internal diameter" readonly data-id="element-11073">
            {hasDiameter ? <HydReadoutValue value={internalDiameter.toFixed(1)} unit="mm" data-id="element-11074" /> : <HydReadoutValue value="—" muted data-id="element-11075" />}
          </PropRow>
          <PropRow label="Pressure rating" readonly data-id="element-11076">
            <HydReadoutValue value={pipe.pressureRating} unit="bar" data-id="element-11077" />
          </PropRow>
          <PropRow label="Pressure rating status" readonly data-id="element-11078">
            <HydStatusBadge status="unknown" data-id="element-11079" />
          </PropRow>
        </HydraulicSection>

        <HydraulicSection title="Hydraulic Status" data-id="element-11080">
          <PropRow label="Hydraulic status" readonly data-id="element-11081">
            <HydStatusBadge status={status} data-id="element-11082" />
          </PropRow>
          <PropRow label="Main warning" readonly data-id="element-11083">
            {mainWarning ? <span className="flex items-center gap-1.5 text-[10px] text-amber-300" data-id="element-11084">
                <AlertTriangleIcon size={10} className="flex-shrink-0" data-id="element-11085" />
                <span className="truncate" data-id="element-11086">{mainWarning}</span>
              </span> : <HydReadoutValue value="None" muted data-id="element-11087" />}
          </PropRow>
        </HydraulicSection>
      </div>;
  }
  // ─── Non-pipe summary views (consistent UX) ─────────────────────────────
  // Count of pipes referencing this object (for connectivity stats).
  const pipesConnected = objects.filter(o => o.kind === 'pipe' && (o.startObjectId === obj.id || o.endObjectId === obj.id)).length;
  if (obj.kind === 'water-source') {
    return <div className="flex-1 overflow-y-auto" data-id="element-11088">
        <HydraulicSection title="Supply" data-id="element-11089">
          <PropRow label="Source type" readonly data-id="element-11090">
            <HydReadoutValue value={obj.sourceType} data-id="element-11091" />
          </PropRow>
          <PropRow label="Available flow" readonly data-id="element-11092">
            <HydReadoutValue value={obj.availableFlow} unit="GPM" data-id="element-11093" />
          </PropRow>
          <PropRow label="Available pressure" readonly data-id="element-11094">
            <HydReadoutValue value={obj.availablePressure} unit="PSI" data-id="element-11095" />
          </PropRow>
        </HydraulicSection>
        <HydraulicSection title="Location" data-id="element-11096">
          <PropRow label="Elevation" readonly data-id="element-11097">
            <HydReadoutValue value={obj.elevation.toFixed(1)} unit="m" data-id="element-11098" />
          </PropRow>
        </HydraulicSection>
        <HydraulicSection title="Network" data-id="element-11099">
          <PropRow label="Pipes connected" readonly data-id="element-11100">
            <HydReadoutValue value={pipesConnected} data-id="element-11101" />
          </PropRow>
          <PropRow label="Status" readonly data-id="element-11102">
            <HydStatusBadge status={pipesConnected > 0 ? 'ok' : 'warn'} data-id="element-11103" />
          </PropRow>
        </HydraulicSection>
      </div>;
  }
  if (obj.kind === 'node') {
    return <div className="flex-1 overflow-y-auto" data-id="element-11104">
        <HydraulicSection title="Node" data-id="element-11105">
          <PropRow label="Type" readonly data-id="element-11106">
            <HydReadoutValue value={obj.nodeType} data-id="element-11107" />
          </PropRow>
          <PropRow label="Elevation" readonly data-id="element-11108">
            <HydReadoutValue value={obj.elevation.toFixed(1)} unit="m" data-id="element-11109" />
          </PropRow>
        </HydraulicSection>
        <HydraulicSection title="Network" data-id="element-11110">
          <PropRow label="Pipes connected" readonly data-id="element-11111">
            <HydReadoutValue value={pipesConnected} data-id="element-11112" />
          </PropRow>
          <PropRow label="Status" readonly data-id="element-11113">
            <HydStatusBadge status={pipesConnected >= 2 ? 'ok' : 'warn'} data-id="element-11114" />
          </PropRow>
        </HydraulicSection>
      </div>;
  }
  if (obj.kind === 'valve') {
    return <div className="flex-1 overflow-y-auto" data-id="element-11115">
        <HydraulicSection title="Valve" data-id="element-11116">
          <PropRow label="Type" readonly data-id="element-11117">
            <HydReadoutValue value={obj.valveType} data-id="element-11118" />
          </PropRow>
          <PropRow label="Diameter" readonly data-id="element-11119">
            <HydReadoutValue value={obj.diameter} unit="mm" data-id="element-11120" />
          </PropRow>
          <PropRow label="Status" readonly data-id="element-11121">
            <HydReadoutValue value={obj.status} data-id="element-11122" />
          </PropRow>
        </HydraulicSection>
        <HydraulicSection title="Network" data-id="element-11123">
          <PropRow label="Pipes connected" readonly data-id="element-11124">
            <HydReadoutValue value={pipesConnected} data-id="element-11125" />
          </PropRow>
          <PropRow label="Overall" readonly data-id="element-11126">
            <HydStatusBadge status={pipesConnected >= 2 ? 'ok' : 'warn'} data-id="element-11127" />
          </PropRow>
        </HydraulicSection>
      </div>;
  }
  if (obj.kind === 'pivot-connection') {
    const linkedPivot = obj.pivotId !== null ? pivots.find(p => p.id === obj.pivotId) : undefined;
    return <div className="flex-1 overflow-y-auto" data-id="element-11128">
        <HydraulicSection title="Pivot link" data-id="element-11129">
          <PropRow label="Linked pivot" readonly data-id="element-11130">
            {linkedPivot ? <HydReadoutValue value={linkedPivot.name} data-id="element-11131" /> : <span className="flex items-center gap-1.5 text-[10px] text-amber-300" data-id="element-11132">
                <AlertTriangleIcon size={10} className="flex-shrink-0" data-id="element-11133" />
                <span data-id="element-11134">Not linked</span>
              </span>}
          </PropRow>
          <PropRow label="Elevation" readonly data-id="element-11135">
            <HydReadoutValue value={obj.elevation.toFixed(1)} unit="m" data-id="element-11136" />
          </PropRow>
        </HydraulicSection>
        <HydraulicSection title="Demand" data-id="element-11137">
          <PropRow label="Required flow" readonly data-id="element-11138">
            <HydReadoutValue value={obj.requiredFlow} unit="GPM" data-id="element-11139" />
          </PropRow>
          <PropRow label="Required pressure" readonly data-id="element-11140">
            <HydReadoutValue value={obj.requiredPressure} unit="PSI" data-id="element-11141" />
          </PropRow>
        </HydraulicSection>
        <HydraulicSection title="Network" data-id="element-11142">
          <PropRow label="Pipes connected" readonly data-id="element-11143">
            <HydReadoutValue value={pipesConnected} data-id="element-11144" />
          </PropRow>
          <PropRow label="Status" readonly data-id="element-11145">
            <HydStatusBadge status={linkedPivot && pipesConnected > 0 ? 'ok' : 'warn'} data-id="element-11146" />
          </PropRow>
        </HydraulicSection>
      </div>;
  }
  return null;
}
// ─── Hydraulic Network — Phase 1: Summary Section (inside LayerControlPanel) ──
function HydraulicNetworkSummarySection({
  hydraulicObjects,
  pivots,
  onSelectHydraulic,
  onSelectPivot
}: {
  hydraulicObjects: HydraulicObject[];
  pivots: Pivot[];
  onSelectHydraulic: (sel: {
    kind: HydraulicKind;
    id: string;
  } | null) => void;
  onSelectPivot: (id: number) => void;
}) {
  const [expanded, setExpanded] = useState<boolean>(true);
  const counts = {
    'water-source': 0,
    pipe: 0,
    node: 0,
    valve: 0,
    'pivot-connection': 0
  } as Record<HydraulicKind, number>;
  for (const o of hydraulicObjects) counts[o.kind]++;
  // Validation warnings
  type Warning = {
    id: string;
    label: string;
    target: {
      type: 'hydraulic';
      sel: {
        kind: HydraulicKind;
        id: string;
      };
    } | {
      type: 'pivot';
      id: number;
    };
  };
  const warnings: Warning[] = [];
  for (const o of hydraulicObjects) {
    if (o.kind === 'pipe') {
      if (o.startObjectId === null || o.endObjectId === null) {
        warnings.push({
          id: `w-${o.id}`,
          label: `${o.name} has a disconnected end`,
          target: {
            type: 'hydraulic',
            sel: {
              kind: 'pipe',
              id: o.id
            }
          }
        });
      }
    }
    if (o.kind === 'pivot-connection' && o.pivotId === null) {
      warnings.push({
        id: `w-${o.id}`,
        label: `${o.name} is not linked to a pivot`,
        target: {
          type: 'hydraulic',
          sel: {
            kind: 'pivot-connection',
            id: o.id
          }
        }
      });
    }
  }
  const total = hydraulicObjects.length;
  return <div className="border-b border-[#2F4268]/60" data-id="element-11147">
      <button onClick={() => setExpanded(v => !v)} className="w-full px-3 py-1.5 bg-[#0a1322] flex items-center justify-between hover:bg-white/[0.02] transition-colors" data-id="element-11148">
        <span className="flex items-center gap-1.5" data-id="element-11149">
          {expanded ? <ChevronDownIcon size={11} className="text-[#8A9BBA]" data-id="element-11150" /> : <ChevronRightIcon size={11} className="text-[#8A9BBA]" data-id="element-11151" />}
          <span className="text-[10px] text-[#8A9BBA] uppercase tracking-wide font-semibold" data-id="element-11152">
            Hydraulic Network
          </span>
        </span>
        <span className="text-[10px] text-[#8A9BBA] font-mono" data-id="element-11153">{total}</span>
      </button>
      {expanded && (total === 0 ? <div className="px-3 py-3 text-center" data-id="element-11154">
            <p className="text-[11px] text-[#8A9BBA] italic" data-id="element-11155">
              No hydraulic objects yet.
            </p>
            <p className="text-[10px] text-[#8A9BBA]/70 mt-0.5" data-id="element-11156">
              Use the Insert tab → Hydraulic Network.
            </p>
          </div> : <div data-id="element-11157">
            {/* Counts grid */}
            <div className="px-3 py-2 grid grid-cols-2 gap-1.5" data-id="element-11158">
              <HydraulicCountChip kind="water-source" count={counts['water-source']} data-id="element-11159" />
              <HydraulicCountChip kind="pipe" count={counts.pipe} data-id="element-11160" />
              <HydraulicCountChip kind="node" count={counts.node} data-id="element-11161" />
              <HydraulicCountChip kind="valve" count={counts.valve} data-id="element-11162" />
              <HydraulicCountChip kind="pivot-connection" count={counts['pivot-connection']} data-id="element-11163" />
            </div>

            {/* Warnings */}
            {warnings.length > 0 && <div className="px-3 pb-2" data-id="element-11164">
                <div className="flex items-center gap-1.5 mb-1" data-id="element-11165">
                  <AlertTriangleIcon size={11} className="text-amber-400 flex-shrink-0" data-id="element-11166" />
                  <span className="text-[10px] text-amber-400 uppercase tracking-wide font-semibold" data-id="element-11167">
                    {warnings.length} issue{warnings.length === 1 ? '' : 's'}
                  </span>
                </div>
                <ul className="space-y-0.5" data-id="element-11168">
                  {warnings.map(w => <li key={w.id} data-id="element-11169">
                      <button onClick={() => {
              if (w.target.type === 'hydraulic') onSelectHydraulic(w.target.sel);else onSelectPivot(w.target.id);
            }} className="w-full text-left text-[10px] text-amber-300/90 hover:text-amber-200 hover:underline truncate" data-id="element-11170">
                        • {w.label}
                      </button>
                    </li>)}
                </ul>
              </div>}

            {/* Object list */}
            <ul className="border-t border-[#2F4268]/40" data-id="element-11171">
              {hydraulicObjects.map(o => {
          const subLabel = (() => {
            if (o.kind === 'pipe') return `${o.diameter}mm · ${o.material}`;
            if (o.kind === 'valve') return `${o.diameter}mm · ${o.valveType}`;
            if (o.kind === 'water-source') return o.sourceType;
            if (o.kind === 'node') return o.nodeType;
            if (o.kind === 'pivot-connection') {
              const p = pivots.find(pp => pp.id === o.pivotId);
              return p ? `→ ${p.name}` : 'unlinked';
            }
            return '';
          })();
          return <li key={o.id} data-id="element-11172">
                    <button onClick={() => onSelectHydraulic({
              kind: o.kind,
              id: o.id
            })} className="w-full flex items-center gap-2 px-3 py-1 hover:bg-white/[0.02] transition-colors text-left" data-id="element-11173">
                      <HydraulicKindIcon kind={o.kind} size={11} data-id="element-11174" />
                      <span className="flex-1 min-w-0 text-[11px] text-[#F1F5FB] truncate" data-id="element-11175">
                        {o.name}
                      </span>
                      <span className="text-[10px] text-[#8A9BBA] truncate max-w-[80px]" data-id="element-11176">
                        {subLabel}
                      </span>
                    </button>
                  </li>;
        })}
            </ul>
          </div>)}
    </div>;
}
function HydraulicCountChip({
  kind,
  count
}: {
  kind: HydraulicKind;
  count: number;
}) {
  return <div className={`flex items-center gap-1.5 px-2 py-1 rounded border ${count > 0 ? 'border-[#2F4268] bg-[#0a1322]' : 'border-[#2F4268]/40 bg-[#0a1322]/40'}`} data-id="element-11177">
      <HydraulicKindIcon kind={kind} size={11} data-id="element-11178" />
      <span className="text-[10px] text-[#C5D1E4] truncate flex-1 min-w-0" data-id="element-11179">
        {hydraulicKindLabel(kind)}
      </span>
      <span className={`text-[10px] font-mono ${count > 0 ? 'text-[#F1F5FB]' : 'text-[#8A9BBA]'}`} data-id="element-11180">
        {count}
      </span>
    </div>;
}